// RefineFlow — the chat, re-presented one step at a time.
//
// This owns the live session and is a REPRESENTATION of it: it sends the seed +
// answers through `useWsSession.send` and reads the readiness card the chat
// proposes. The CHAT decides what to ask (needs_input slots) and what it could
// derive (green slots carrying their value). Focus surfaces those as a QUESTION
// screen or a REVIEW screen ("Loom set X — keep / change?"), until the card is
// all-green and a token-gated Accept appears.
//
// THE QUEUE (why this isn't a pure function of the latest card):
// The agent re-emits the readiness card on EVERY propose_card call, and the
// Stop-hook grind loop fires it up to 6× PER TURN while it resolves red slots
// from code. A naive "first non-green field on the latest card" derivation
// therefore yanks the on-screen question out from under the user every time a
// card lands — the reported "the question just switches, not on my input" bug.
//
// So the visible step is PINNED: `current` only changes when the USER acts on
// it (answers / keeps / changes). Background cards feed a derived `queue` of
// what comes next; they never preempt what's on screen. See focus-diag.ts +
// the on-screen debug panel (`?focusdebug`) which flags every card where the
// old derivation WOULD have switched — the pin absorbing it is the fix.
//
// BATCHING (why answers don't go out one-by-one):
// The bridge SIGTERM-kills the running agent the instant a new user_prompt
// arrives, so sending answers one-at-a-time restarts the agent mid-grounding
// with partial context — exactly while the user is still answering. Instead we
// accumulate answers locally and flush them as ONE message when the user has
// worked through everything currently asked; the agent then runs once with the
// full set. (Free-text from the message bar flushes any held answers with it.)

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import {
  PLAN_CLIENT_BACKSTOP_MS,
  BREAKDOWN_RESIZE_CLIENT_BACKSTOP_MS,
  isBareAssent,
  milestoneTitleOf,
  splitAcceptanceCriteria,
} from "@loom/shared/readiness";
import { forgeOfProjectId } from "@loom/shared/forge";
import { uiProse } from "@loom/shared/brand";

import { HOLD_BUSY_BACKSTOP_MS } from "../chat/render/useAnswerHold";
import { isSessionDead } from "../chat/session-status";
import {
  useWsSession,
  type FigmaLinkSnapshot,
  type TimelineEntry,
} from "../chat/useWsSession";
import { askEntrySeq, entrySeq } from "../chat/timeline";
import { useLatchedEntry } from "../chat/useLatchedEntry";
import {
  buildMilestoneAnswer,
  defaultWidgetForField,
  PRIORITY_OPTIONS,
  priorityOptionValue,
} from "../chat/render/answerMessage";
import {
  isChoiceField,
  isListField,
  isValueField,
  loomAnswerOf,
  splitListRows,
  useArmedClick,
  unreadUserTurns,
  useRedOfferLatch,
  type LoomAnswer,
} from "../chat/render/loomAnswer";
import { BreakdownCard } from "../breakdown/BreakdownCard";
import {
  buildKeepSingleMessage,
  buildPieceElicitationMessage,
} from "../breakdown/breakdownMessage";
import { useBreakdownCard } from "../breakdown/useBreakdownCard";
import { IdleReconnectDialog } from "../chat/IdleReconnectDialog";
import { MarkdownView } from "../dashboard/components/MarkdownView";
import {
  clearPersistedSession,
  useProjectSession,
} from "../dashboard/useProjectSession";
import { reloadApp } from "../lib/host";
import { reconnectBody } from "../lib/reconnect-body";
import {
  useSessionPagehideBeacon,
  useSessionBridgeEnsure,
} from "../lib/sessionBeacon";
import { useLocale, useT, type MessageKey } from "../i18n/index.js";
import { optionLabelKey } from "../chat/render/answerMessage";
import { stripMarkdownInline } from "../lib/strip-markdown";
import {
  reportClientEvent,
  type ClientTraceFields,
} from "../lib/client-trace";
import {
  createRemountSafeStore,
  RemountSafeStoreProvider,
  useRemountSafeState,
  type RemountSafeStore,
} from "../state/remount-safe-state";

import {
  cardProgress,
  comprehensionObjection,
  FIELD_ORDER,
  isOptionalField,
  policyFor,
  skipLabelKey,
  storyClauseRoleOf,
  storyClauseValue,
  fieldsWaitingOnUser,
  useFieldCopy,
  useSourceLabel,
  type ReadinessEntry,
  type ValueSource,
} from "./copy";
import { focusDiag, isFocusDebugEnabled } from "./focus-diag";
import {
  applyErrorCode,
  applyErrorCopyKey,
  applyRetryHelps,
  errorCopyKey,
  errorText,
} from "./errorCopy";
import {
  activityOf,
  FIRST_CONNECT_GRACE_MS,
  IDLE_SETTLE_MS,
  NO_WORKSPACE_SETTLE_MS,
  nextWaitFlipMs,
  OFFLINE_GRACE_MS,
  stopCandidateOf,
  waitStatusOf,
  type WaitInput,
} from "./wait-status";
import { ConnectionLine, useHeldFor, WaitLine } from "./WaitLine";
import { FocusDebugPanel, type FocusDebugSnapshot } from "./debug";
import {
  FocusFrame,
  GhostButton,
  Headline,
  Help,
  HelpDetail,
  IconAlert,
  IconArrowRight,
  IconCheck,
  Kicker,
  MessageBar,
  NextButton,
  NoteLinkComposer,
  ThinkingDots,
  type FocusStatus,
} from "./ui";
import { composeBatch, questionAnswerParts } from "./answer-frame";
import {
  AnswerWidget,
  LoomBlock,
  UseButton,
  type AnswerMeta,
  type ChoiceCurrent,
} from "./widgets";
import { useMilestoneWrite } from "./useMilestoneWrite";
import { FigmaLinkPanel } from "./figma/FigmaLinkPanel";
import { FigmaPreview } from "./figma/FigmaPreview";
import { PREVIEW_FLOW_MAX_H } from "./figma/useDesignScene";

/**
 * The bus seq of the user turn a Figma chip belongs to.
 *
 * `sourceSeq` names that turn when the server stamps it. A row without one falls
 * back to its own `seq`, which is always AFTER the carrying turn's echo — the
 * scan emits once the echo is on the bus — so the flow fence below reads the
 * same either way. Narrowed with `in` rather than declared: the stamp is
 * optional on the wire and older rows never carry it.
 */
const figmaSourceSeq = (link: FigmaLinkSnapshot): number =>
  "sourceSeq" in link && typeof link.sourceSeq === "number"
    ? link.sourceSeq
    : link.seq;

/**
 * Another issue this shared project session is mid-flow on.
 *
 * One sandbox serves a whole PROJECT, not one issue (see the header of
 * `dashboard/useProjectSession.ts`), so opening a second issue reuses the first
 * one's session by design and the server re-scopes the flow to the new issue.
 * The first issue's card, its verified greens and its questions are NOT
 * destroyed — they are persisted per (session, issue) and come back on a return
 * — but until this notice existed nothing on screen said so.
 */
/** Which screen's Accept / Apply a click came from (the `accept_click` trace). */
type AcceptSurface = "ready" | "budget" | "sandbox_dead" | "tools";

/**
 * The refine step on screen, for the `focus.stage` trace — one name per branch
 * of the render ladder, so a trace names the branch that actually rendered.
 */
type FocusStage =
  | "fatal"
  | "session_error"
  | "apply_failed"
  | "apply_timeout"
  | "applied"
  | "sandbox_dead"
  | "tools"
  | "budget"
  | "preview"
  | "ask"
  | "concern"
  | "agent_stopped"
  | "no_workspace"
  | "card_stopped"
  | "boot"
  | "question"
  | "review"
  | "held"
  | "thinking"
  | "plan_simulation"
  | "plan_confirm"
  | "sizing_wait"
  | "breakdown"
  | "correct"
  | "ready"
  | "waiting";

/** The steps that draw the wait line (`boot`, `thinking`, the grind's
 *  `waiting`) or a stop screen (`agent_stopped`, `no_workspace`,
 *  `card_stopped`) — the only places a `focus.wait` line is about something the
 *  user can see. The page's own socket (`offline` / `unreachable`) is drawn on
 *  every step. */
const WAIT_DRAWN_STAGES: ReadonlySet<FocusStage> = new Set<FocusStage>([
  "boot",
  "thinking",
  "waiting",
  "agent_stopped",
  "no_workspace",
  "card_stopped",
]);

interface ParkedFlow {
  readonly iid: number;
  /**
   * Whether that flow still has a card to land on. Gates the button AND which
   * sentence is used: a flow whose only trace is a retired pre-card ask has no
   * card, no pinned question and (by its own `flowEvidence`) no seed either, so
   * a button would open an empty screen with nothing but the composer — and
   * there may be no saved answers to promise, since the switch may have landed
   * on that issue's very first turn.
   *
   * Deliberately NOT accompanied by a question COUNT. The count that would be
   * true is the one the step queue computes on landing, and that queue reads a
   * card's verdicts through the field policy, the durable declines, the open
   * typed asks and the user-sourced greens. Predicting it from here is a second
   * copy of that logic, in a second file, that would drift — and a bar that
   * promises "1 question is waiting" and then shows three is worse in two
   * languages than a bar that never counts.
   */
  readonly resumable: boolean;
}

interface RefineFlowProps {
  readonly projectId: number;
  readonly iid: number;
  readonly issueTitle: string;
  /** The issue's GitLab web URL (when known), so the end-of-flow "Open #N"
   *  buttons go straight to GitLab instead of Loom's own issue editor. */
  readonly issueWebUrl: string | null;
  readonly seed: string;
  /**
   * The byte-exact span of `seed` the USER authored (the confirmed map location
   * + the task they typed). The rest of a wizard seed is machine-composed — the
   * AI-derived issue title, the project map's advisory paths, the standing
   * instructions — and the grounding gate must not be able to quote it back as
   * user evidence.
   *
   * `""` is a real, meaningful value: the refine seed contains nothing the user
   * wrote. `undefined` means "declare no span", which keeps the gate scanning
   * the whole seed — correct for a legacy persisted row (see FocusPage's
   * `readSeed`) and for any caller that predates this.
   *
   * `seed` itself is untouched by this, so the seed fence (`e.text === seed`)
   * still matches the echo byte-for-byte.
   */
  readonly seedEvidence?: string;
  /**
   * How this mount ENTERED the flow — explicit, never inferred here.
   *
   * `false` = a FRESH entry: the wizard just created or picked the issue and
   * navigated in, so nothing exists server-side yet and the seed must go out
   * immediately (it is what opens the flow).
   *
   * `true` = a RESUME: the refine URL was mounted without that in-flow context
   * (a reload, a deep link, browser Back). Seeding is then WITHHELD until we
   * know the server no longer holds this issue's flow — see the seed effect.
   * The seed carries `flow.boundary: true`, so re-sending it on a reload is not
   * a harmless duplicate: the server clears the prior flow's CLI conversation
   * and moves the evidence-eviction horizon, i.e. the agent loses everything
   * the user already told it and re-asks questions whose answers are already
   * verified green on the (server-persisted, still-displayed) card.
   */
  readonly resumed: boolean;
  /** Back to the project picker — every escape screen's way out. */
  readonly onProjects: () => void;
  readonly onRestart: () => void;
  readonly onOpenIssue: (iid: number) => void;
  /**
   * Go back to another issue's refine flow in THIS project session.
   *
   * Must be the ROUTER-level navigation, not the wizard's `enterRefine`: that
   * one marks a fresh entry, and a fresh entry seeds at once — which on a flow
   * the server still holds is the `boundary: true` send that wipes it. Without
   * the marker `RefineResume` mounts `resumed` and the flow waits for the replay
   * to prove the server still has it.
   *
   * Optional: a caller with nowhere to navigate gets the notice without the
   * button, which is the sentence the user actually needs.
   *
   * It is a resume, not a free ride: the landing costs no sandbox turn only
   * while that issue's card is still in the replayed range. If the evidence has
   * aged out of the 4000-event ring, the resumed mount's grace elapses and it
   * seeds — one billed round, the same as opening the issue from the picker.
   */
  readonly onSwitchIssue?: (iid: number) => void;
  /**
   * Start a FRESH session and stay on THIS issue.
   *
   * `onRestart` leaves the flow: it navigates back to the project and drops the
   * user on the mode picker, which from the budget wall meant the issue they had
   * just created was abandoned mid-refine — they had to find it again through
   * the Refine picker. The caller remounts this component with a new key
   * instead, so the flow re-enters as a fresh entry on the same iid and seeds
   * the new session immediately.
   *
   * Optional: a caller that has no way to remount in place falls back to
   * `onRestart`, which is the prior behaviour.
   */
  readonly onFreshSession?: () => void;
  /**
   * What the ISSUE carries on GitLab right now, for the closed-choice steps.
   *
   * The card cannot supply this on the step that needs it: an agent question is
   * a `needs_input` slot, and a `needs_input` slot carries a question and NO
   * value (`propose-card.ts`'s `resolveSlot`). So on the priority step — where
   * the agent's own words were "it's currently flagged as Priority 2 in GitLab —
   * keep that, or change it?" — the card-value fallback is empty and the three
   * options stood unmarked, with no way to express "keep".
   *
   * `priority: null` means the issue carries NO `priority::` label. That
   * distinction is load-bearing: `DashboardIssue.priority` defaults an unlabelled
   * issue to `"normal"`, and marking "Normal" as the issue's current value would
   * be inventing one.
   */
  readonly issueCurrent?: {
    readonly priority: "high" | "normal" | "low" | null;
    readonly milestoneTitle: string | null;
  };
}

type Step =
  | {
      readonly kind: "question";
      readonly field: string;
      readonly question: string;
      // The O3 surface: this question exists because the gate RED'd a field the
      // user demonstrably answered, on a card that provably read the answer —
      // not because the agent asked for something missing. Only the copy differs
      // (kicker + headline); the answer path is identical.
      readonly unverified?: boolean;
    }
  | {
      readonly kind: "review";
      readonly field: string;
      readonly value: string;
      readonly source: ValueSource;
    }
  // A TYPED ASK (`question_pending`) — the agent explicitly asking, with no card
  // involved. This is the only step kind that exists before a card does, and the
  // only one whose `field` can be null (a scoping / disambiguation question that
  // maps to no readiness field).
  | {
      readonly kind: "ask";
      readonly askId: string;
      readonly field: string | null;
      readonly question: string;
      readonly help?: string;
      readonly optional: boolean;
      // A CONCERN: the agent flagging a problem WITH something already settled
      // (two acceptance criteria that cannot both hold, a priority that
      // contradicts them) rather than asking for something missing. It may be
      // bound to a field the user already answered, it outranks the ready screen,
      // and it is always skippable — skipping it IS the decision.
      readonly concern: boolean;
      // Loom's draft answer rows for this ask, if it carried any.
      readonly draft?: ReadonlyArray<string>;
    }
  // THE FIGMA SKETCH. A typed ask (`question_pending`) whose `subject` names a
  // drawing instead of a readiness field: "is this the screen you meant?", yes
  // or no. It rides the ask channel for everything that channel already
  // solved — pinning, clearing, reconnect survival, instance-recycle survival —
  // and is its own step kind for everything it does NOT share with an ask:
  // there is no widget, no text, no attribution, and the answer leaves on its
  // own frame.
  //
  // `field` is `null` and can never be anything else, which is what keeps it
  // out of `setProvided`, out of `answers`, out of the field-ordered loop and
  // out of `composeAnswerPrompt` structurally rather than by convention.
  // Approving a sketch is evidence for NOTHING on the readiness card.
  | {
      readonly kind: "preview";
      readonly askId: string;
      readonly field: null;
      /** The opaque handle the ask carried. The client never builds one. */
      readonly previewId: string;
      readonly optional: boolean;
    };

// The end-of-refine tail round, after the card goes all-green and BEFORE the
// existing Accept screen:
//   idle         — not started yet (the trigger effect kicks it on first all-green)
//   plan_loading — the repo-aware sandbox agent is producing an implementation plan
//   validate     — show the plain-language business summary, ask "does this match?"
//   correct      — the user said "not quite": collect a freeform clarification → re-run
//   sizing_wait  — the user confirmed the plan BEFORE the size verdict landed
//                  (plan and sizing are separate events from the same tail turn,
//                  and sizing can lag by minutes): wait visibly instead of
//                  silently dropping the split path; the sizing effect advances
//                  the moment the verdict (or a degrade) arrives.
//   breakdown_review — the sizing step recommended a split (or the user forced
//                  one): review the epic/children proposal, adjust, confirm or
//                  keep-as-one. Confirm flips `accepted` (the server sequences
//                  parent apply + children creation off one frame); decline and
//                  every degrade path fall through to `accept`.
//   accept       — validated (or the round was unavailable) → the existing Accept preview
// PINNED like `current`: background card churn never moves it; only the trigger,
// the plan result, and user clicks advance it.
type TailPhase =
  | "idle"
  | "plan_loading"
  | "validate"
  | "correct"
  | "sizing_wait"
  | "breakdown_review"
  | "accept";

/** Catalog key per story-clause role, so the review screen can show a value in
 *  the sentence position it fills. Written out rather than interpolated from the
 *  role: the catalog's key type widens to `string`, so a template would compile
 *  either way — but a literal is greppable, which is how a renamed message is
 *  actually caught here. `translate` falls back to the key itself on a miss, and
 *  the tests below assert the rendered sentence, not the key. */
const IN_STORY_KEY = {
  role: "focus.refine.review.inStory.role",
  means: "focus.refine.review.inStory.means",
  ends: "focus.refine.review.inStory.ends",
} as const;

// Sandbox phases that have a localized pill label (`focus.sandbox.<phase>`).
// The display text itself lives in the message catalog; this is only the set of
// known keys, so an unmapped phase falls through to the call site's own copy.
const SANDBOX_PHASES = new Set<string>([
  "idle",
  "triggering",
  "waiting-for-runner",
  "booting",
  "ready",
  "degraded",
  "suspended",
  "terminated",
]);

/** Strip ONLY a known self-label so a derived value reads cleanly on review.
 *  Free-text fields are rendered verbatim — a colon there is real content, not
 *  a label, and must not be stripped. */
const prettyValue = (field: string, value: string): string => {
  if (field === "acceptance_criteria" || field === "dod_criteria") {
    const lines = value.split("\n");
    const head = lines[0]?.trim() ?? "";
    return head.endsWith(":") ? lines.slice(1).join("\n").trim() : value;
  }
  if (field === "priority") return value.replace(/^Priority:\s*/iu, "");
  if (field === "milestone") return value.replace(/^Milestone:\s*/iu, "");
  return value;
};

/**
 * The ISSUE's own value for a closed-choice field, as the option-matching string
 * a widget marks — or null when the issue has none.
 *
 * The last of the three provenances a choice step can mark, and the only one an
 * OPEN QUESTION has: an agent ask arrives as a `needs_input` slot, which carries
 * a question and no `value`, so the user's held answer and the card's value are
 * both empty exactly when the agent says "it's currently flagged as Priority 2
 * in GitLab — keep that, or change it?".
 *
 * Never invents one. `priority: null` (no `priority::` label on the issue) yields
 * null rather than the `DashboardIssue` projection's `"normal"` default — marking
 * "Normal" as "what the issue has" would be this surface asserting a value the
 * forge does not hold. Same for an issue with no milestone.
 *
 * The origin names the issue's forge (GitLab or GitHub), so the caption under
 * the marked option says where the value lives.
 *
 * The strings are the canonical self-labelled send-texts, not display labels:
 * option labels are localized chrome, so matching on them would break the moment
 * the UI locale changed.
 */
export const issueChoiceFor = (
  field: string,
  issueCurrent: RefineFlowProps["issueCurrent"],
  projectId: number,
): ChoiceCurrent | null => {
  if (issueCurrent === undefined) return null;
  if (field === "priority" && issueCurrent.priority !== null)
    return {
      value: priorityOptionValue(issueCurrent.priority),
      origin: forgeOfProjectId(projectId),
    };
  if (field === "milestone" && issueCurrent.milestoneTitle !== null)
    return {
      value: buildMilestoneAnswer(issueCurrent.milestoneTitle),
      origin: forgeOfProjectId(projectId),
    };
  return null;
};

/**
 * Whether the card's value for a CHOICE step is one the field STANDS on, and so
 * may be marked as its current value (`origin: "card"`).
 *
 * Not a red or unavailable one. That value is Loom's own wording, which the gate
 * refused (or could not check) against the user's words: `loomAnswerOf` offers
 * it as Loom's suggestion once settled and when no draft rows carry the answer.
 * So a refused value is never marked as current; at most it is Loom's
 * suggestion, and the issue's own value shows. Marked as current, it hid the issue's
 * own one: live on dev (both forges) the agent quoted the label "priority::p2",
 * the gate refused it, it matched no option, and "Normal", which the issue
 * carried, stood unmarked.
 */
const cardValueStands = (
  verdict: "green" | "red" | "needs_input" | "unavailable" | undefined,
): boolean => verdict !== "red" && verdict !== "unavailable";

/** The `AnswerWidget` seed props for pre-filling a control from a field's
 *  current raw value. A list field's value is `buildListAnswer`-serialized, so
 *  it seeds `initialRows` (split back out); every other field seeds the text box
 *  with the self-label stripped (`prettyValue`) — exactly what the box shows. */
const seedFrom = (
  field: string,
  rawValue: string,
): { initialRows: string[] } | { initialValue: string } =>
  isListField(field)
    ? { initialRows: splitListRows(rawValue) }
    : { initialValue: prettyValue(field, rawValue) };

/**
 * A scalar value as the user reads it: `prettyValue`, and a closed-set choice
 * by its LABEL. The priority buttons send the English contract value
 * ("Priority: High") and show a localized label, and the review and ready
 * screens printed the contract value back — "Priorität: High" under a German
 * session (dev #174). The value itself is untouched.
 */
const shownValue = (
  field: string,
  value: string,
  t: ReturnType<typeof useT>,
): string => {
  const shown = prettyValue(field, value);
  const choiceKey =
    field === "priority" ? optionLabelKey(`Priority: ${shown}`) : null;
  return choiceKey === null ? shown : t(choiceKey);
};

/**
 * A field's value as the flow RENDERS it — the display half of the same inverse
 * `seedFrom` uses to fill the editor.
 *
 * A list field renders as rows, whatever bullet or heading shape the value came
 * back wearing, because that is what it IS: the widget serialized rows to send
 * them and the ready card has to show rows back. Everything else renders as the
 * text it is, minus a known self-label.
 */
const FieldValue = ({
  field,
  value,
}: {
  readonly field: string;
  readonly value: string;
}) => {
  const t = useT();
  const rows = isListField(field) ? splitListRows(value) : [];
  if (rows.length === 0) {
    return <div className="focus-prose">{shownValue(field, value, t)}</div>;
  }
  return (
    <ul className="focus-prose focus-value-rows">
      {rows.map((r, i) => (
        <li key={`${i}-${r}`}>{r}</li>
      ))}
    </ul>
  );
};

/**
 * A milestone on the ready screen, as Accept will treat it. When the project's
 * list holds no milestone of that name, apply leaves the issue's milestone as
 * it is — so the row says THAT instead of showing a value that will not land
 * (`useMilestoneWrite`, the same list and comparison apply uses). Edit stays.
 */
const ReadyMilestoneValue = ({
  projectId,
  value,
}: {
  readonly projectId: number;
  readonly value: string;
}) => {
  const t = useT();
  const write = useMilestoneWrite(projectId, value);
  if (write === "not_written") {
    return (
      <div className="focus-prose" data-milestone-write="not_written">
        {t("focus.refine.ready.milestoneUnchanged", {
          title: milestoneTitleOf(value) ?? value,
        })}
      </div>
    );
  }
  return <FieldValue field="milestone" value={value} />;
};

/**
 * A CONCERN's answer: what the field says now, then Keep / Change of equal
 * weight, and no box until the user asks for one.
 *
 * Neither button is the default and neither carries the check mark: keeping a
 * value Loom has just questioned is a decision, not a confirmation, and on #239
 * the only yes-shaped button ("Skip — leave it as it is") kept "so that yes" and
 * wrote it to GitLab. "Keep it as it is" now sits under the value it keeps.
 *
 * "Change it" opens the field's own control EMPTY — never Loom's text, never the
 * current value — and its reply leaves as a concern reply (`kind: "concern"`),
 * which the server routes as a decision about the field, not as its new value.
 * No bare-yes guard here: "ja" can be a real reply to a concern.
 *
 * When the concern carries Loom's own suggested reply (`loomReply` — the
 * concern's one-row draft, most often the rewording it is asking about), it
 * stands between the two, closed, with "Use this text": one click and it is the
 * user's reply, on the same concern lane. Without it the user saw only the OLD
 * text under "does this shorter wording fit?", answered "ja", and was told the
 * new wording had been confirmed (#248, 2026-09-25).
 */
function ConcernAnswer({
  field,
  currentValue,
  question,
  projectId,
  draftKey,
  loomReply,
  answerLabel,
  onKeep,
  onReply,
}: {
  readonly field: string | null;
  readonly currentValue: string | null;
  readonly question: string;
  readonly projectId: number;
  readonly draftKey: string;
  readonly loomReply?: string;
  readonly answerLabel?: string;
  readonly onKeep: () => void;
  readonly onReply: (text: string, meta?: AnswerMeta) => void;
}) {
  const t = useT();
  const [changing, setChanging] = useRemountSafeState(`${draftKey}:changing`, false);
  const armed = useArmedClick();
  const keep = (
    <button
      type="button"
      className="focus-btn is-ghost"
      onClick={(e) => {
        if (!armed(e)) return;
        onKeep();
      }}
    >
      {t("focus.refine.concern.skip")}
    </button>
  );
  return (
    <>
      {field !== null && currentValue !== null && (
        <div className="focus-concern-current">
          <p className="focus-suggest-label">{t("focus.refine.concern.current")}</p>
          <FieldValue field={field} value={currentValue} />
        </div>
      )}
      {!changing && loomReply !== undefined && (
        <LoomBlock text={loomReply}>
          <div className="focus-suggest-actions">
            <UseButton
              label={t("focus.refine.suggest.adopt")}
              {...(answerLabel !== undefined
                ? { ariaLabel: t("focus.refine.suggest.adoptAria", { label: answerLabel }) }
                : {})}
              onUse={() => {
                focusDiag("suggestion_adopted", { field: field ?? "concern" });
                reportClientEvent("ui.click", {
                  control: "loom_answer_use",
                  surface: "focus_concern",
                });
                onReply(loomReply, { via: "loom" });
              }}
            />
          </div>
        </LoomBlock>
      )}
      {changing ? (
        <>
          <AnswerWidget
            field={field ?? "agent_ask"}
            projectId={projectId}
            ariaLabel={question}
            onAnswer={(text) => onReply(text)}
            draftKey={draftKey}
          />
          <div className="focus-skip-optional">{keep}</div>
        </>
      ) : (
        <div className="focus-footer focus-concern-pair">
          {keep}
          <button
            type="button"
            className="focus-btn is-ghost"
            onClick={() => setChanging(true)}
          >
            {field !== null
              ? t("focus.refine.concern.change")
              : t("focus.refine.concern.reply")}
          </button>
        </div>
      )}
    </>
  );
}

const FIELD_ORDER_SET = FIELD_ORDER as ReadonlyArray<string>;

/**
 * The identity of a step — what "the same question" means across the queue, the
 * held-answers batch, the pin, and the widget subtree key.
 *
 * A card step is its field. A typed ask is ALSO its field when the ask is
 * spec-bound, so an ask for `role` and a later card verdict for `role` are ONE
 * step: the user is never asked the same thing twice just because the question
 * arrived through two channels. A field-less ask keys on its askId instead —
 * without that, two scoping questions would collide on the same `null` key and
 * the second would silently overwrite the first's answer.
 *
 * A CONCERN never shares a step with its field's card question — it is ABOUT the
 * answer, not a substitute for it. Under the field key it would collide with
 * that field's question and be filtered out by `liveTypedAsks`' `!answers.has()`
 * / `owned` exactly when the user has already answered the field, which is
 * precisely when a concern can exist. Conditioned strictly on `concern`: keying
 * a plain spec-bound ask by askId would resurrect the double-ask this collapsing
 * scheme exists to prevent.
 */
const stepKeyOf = (step: Step): string =>
  // A sketch is keyed by its own ask id and by nothing else: it is bound to no
  // field, so it can never collapse into a field's step and can never be
  // suppressed (or resurrected) by one.
  step.kind === "preview"
    ? `preview:${step.askId}`
    : step.kind !== "ask"
      ? step.field
      : step.field !== null && !step.concern && FIELD_ORDER_SET.includes(step.field)
        ? step.field
        : `ask:${step.askId}`;

/* ─── Held-answer-batch persistence (sessionStorage, per issue) ────────────
 *
 * The batch (answers/declines the user has given but the flush gate hasn't
 * sent yet) used to live in plain `useState` only. The flush gate requires
 * the WHOLE currently-open queue to drain before it sends
 * (`current === null && openAsks.length === 0`), and the on-screen copy
 * promises exactly that wait — "Loom waits for all your answers before it
 * picks back up" — so the exposure window is minutes, not seconds, BY
 * DESIGN. A reload is a new document: it silently dropped the whole batch
 * (answers, held declines, attribution) and the flow re-asked a question the
 * user had just answered. Mirrors the create draft's persistence
 * (`loom.create.draft:<projectId>` in FocusPage.tsx) — same per-tab,
 * per-issue lifetime, same fail-open try/catch. Wiped implicitly by the same
 * setState calls that already clear the live maps on flush and on
 * `ws.resetEpoch` (the write-back effect below persists whatever those maps
 * hold, so an empty map persists as "nothing held").
 */

interface HeldBatchRow {
  /** `null` only for a field-less typed ask (a scoping/disambiguation ask). */
  readonly field: string | null;
  /** `null` for a plain card-field answer with no typed-ask attribution. */
  readonly askId: string | null;
  readonly concern: boolean;
  readonly text: string;
}
interface HeldBatchDecline {
  readonly askId: string;
  readonly field: string | null;
  readonly concern: boolean;
}
interface HeldBatch {
  readonly answered: readonly HeldBatchRow[];
  readonly declined: readonly HeldBatchDecline[];
  /**
   * Fields whose skip the user UNDID — see `unDeclined` / `declinedAll`.
   *
   * Not a held row: there is nothing here to send, and the skip it takes back
   * already left the browser. It rides in this bag because it needs exactly
   * this bag's lifetime. A skip is durable — the server stamps `declined` onto
   * the slot and every later card carries it — so once the card flag started
   * reaching the client, the mask survived a reload while the override that
   * cancels it did not: the user skipped, undid, reloaded, and the question was
   * masked again with no surface left to un-skip it. Both halves have to
   * survive the same remount or the undo is a lie.
   */
  readonly unDeclined: readonly string[];
}

/** Same identity rule as `stepKeyOf`, restated over the persisted row shape
 *  (which has no `Step` to hand it) — a spec-bound, non-concern ask shares its
 *  field's key; everything else keys on its own askId. */
const heldBatchKey = (row: {
  readonly field: string | null;
  readonly askId: string | null;
  readonly concern: boolean;
}): string =>
  row.askId === null
    ? (row.field as string)
    : row.field !== null && !row.concern && FIELD_ORDER_SET.includes(row.field)
      ? row.field
      : `ask:${row.askId}`;

const batchStorageKey = (projectId: number, iid: number): string =>
  `loom.refine.batch:${projectId}:${iid}`;

const readHeldBatch = (projectId: number, iid: number): HeldBatch | null => {
  try {
    const raw = window.sessionStorage.getItem(batchStorageKey(projectId, iid));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<HeldBatch>;
    if (!Array.isArray(parsed.answered) || !Array.isArray(parsed.declined)) {
      return null;
    }
    // Shape-check every row rather than trust the blob — a half-written or
    // cross-version row must not hand a garbage key into the batch maps.
    const answered = parsed.answered.filter(
      (r): r is HeldBatchRow =>
        typeof r === "object" &&
        r !== null &&
        (r.field === null || typeof r.field === "string") &&
        (r.askId === null || typeof r.askId === "string") &&
        typeof r.concern === "boolean" &&
        typeof r.text === "string",
    );
    const declined = parsed.declined.filter(
      (r): r is HeldBatchDecline =>
        typeof r === "object" &&
        r !== null &&
        typeof r.askId === "string" &&
        (r.field === null || typeof r.field === "string") &&
        typeof r.concern === "boolean",
    );
    // Tolerated absent, not required: blobs written before the un-skip started
    // persisting have only the two arrays, and rejecting those would drop a
    // whole held batch on the deploy that added this key.
    const unDeclined = Array.isArray(parsed.unDeclined)
      ? parsed.unDeclined.filter((f): f is string => typeof f === "string")
      : [];
    if (answered.length === 0 && declined.length === 0 && unDeclined.length === 0) {
      return null;
    }
    return { answered, declined, unDeclined };
  } catch {
    return null;
  }
};

const writeHeldBatch = (
  projectId: number,
  iid: number,
  batch: HeldBatch,
): void => {
  try {
    if (
      batch.answered.length === 0 &&
      batch.declined.length === 0 &&
      batch.unDeclined.length === 0
    ) {
      window.sessionStorage.removeItem(batchStorageKey(projectId, iid));
      return;
    }
    window.sessionStorage.setItem(
      batchStorageKey(projectId, iid),
      JSON.stringify(batch),
    );
  } catch {
    /* best-effort — a lost persist just re-exposes the original reload gap */
  }
};

/* ─── Pending plan-correction marker (sessionStorage, per issue) ────────────
 *
 * "The user pressed 'Not quite' on plan revision N and the re-run is still in
 * flight." Same per-tab, per-issue lifetime and the same fail-open try/catch as
 * the held batch above, and persisted for the same reason: the window is
 * MINUTES long, and a remount inside it (projects and back, a reload) is an
 * ordinary move. Without this, the resume branch re-consumes the plan the user
 * just rejected and shows it as the answer.
 *
 * Cleared when the re-run's plan is consumed, when the client plan deadline
 * gives up, and on `/reset`.
 *
 * It carries `requestedAtMs` because EVERY ONE of those clearers needs this
 * component mounted at that instant — the deadline effect's `clearTimeout`
 * cleanup runs on unmount — so a tab that leaves during a re-run that never
 * lands strands the marker in sessionStorage with nothing left to remove it.
 * The stamp is what lets a later mount ask "could that re-run still be
 * running?" instead of assuming yes: see `PlanCorrection` at the state
 * declaration for what the answer changes.
 */
const planCorrectionKey = (projectId: number, iid: number): string =>
  `loom.refine.plancorrection:${projectId}:${iid}`;

interface PlanCorrection {
  readonly fromRevision: number | null;
  /** `Date.now()` at the moment the correction went out. */
  readonly requestedAtMs: number;
}

const readPlanCorrection = (
  projectId: number,
  iid: number,
): PlanCorrection | null => {
  try {
    const raw = window.sessionStorage.getItem(planCorrectionKey(projectId, iid));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as {
      fromRevision?: unknown;
      requestedAtMs?: unknown;
    };
    // Shape-check rather than trust the blob: a garbage `fromRevision` would
    // become a comparison that silently never fires (or never stops firing).
    // A marker with no usable stamp is read as LONG AGO (epoch), not as fresh
    // and not as absent. Its only writer is a bundle that predates the stamp,
    // so "there was a correction, we cannot prove it is still in flight" is the
    // honest reading — and it routes to the expired branch, which refuses to
    // resume onto the rejected plan, rather than to the no-marker branch, which
    // would. Treating it as fresh instead would hand it a full backstop
    // measured from the RELOAD: precisely the unbounded wait this bounds.
    const requestedAtMs =
      typeof parsed.requestedAtMs === "number" &&
      Number.isFinite(parsed.requestedAtMs)
        ? parsed.requestedAtMs
        : 0;
    if (parsed.fromRevision === null) return { fromRevision: null, requestedAtMs };
    return typeof parsed.fromRevision === "number"
      ? { fromRevision: parsed.fromRevision, requestedAtMs }
      : null;
  } catch {
    return null;
  }
};

const writePlanCorrection = (
  projectId: number,
  iid: number,
  value: PlanCorrection | null,
): void => {
  try {
    if (value === null) {
      window.sessionStorage.removeItem(planCorrectionKey(projectId, iid));
      return;
    }
    window.sessionStorage.setItem(
      planCorrectionKey(projectId, iid),
      JSON.stringify(value),
    );
  } catch {
    /* best-effort — a lost persist just re-exposes the remount gap */
  }
};

// Error `code`s the ready-screen field-edit path emits (see the server's
// handleReadinessFieldEdit). Rendered as one flow-level note under the field
// list; `edit_agent_unavailable` gets its own reassuring copy, the rest the
// catalog line for their code (`errorCopy.ts`).
const EDIT_ERROR_CODES: ReadonlySet<string> = new Set([
  "edit_unavailable",
  "edit_after_apply",
  "edit_agent_unavailable",
  "edit_failed",
]);

/**
 * What a restarted workspace is told when the server RESUMED its conversation
 * but no card exists yet (see `reKick`). A same-flow turn, never a boundary, so
 * the restored conversation is kept; machine words, sent with `evidenceText:
 * ""`, so nothing in it can ground a card field. It names the issue because
 * the pm-mode directive the router wraps it in does not, and a resume that
 * restored nothing still has to know what to work on.
 */
export const makeRefineNudge = (iid: number): string =>
  `Your workspace was restarted. Carry on refining issue #${iid} from where you ` +
  `left off: propose the readiness card, or ask me what you still need. If you ` +
  `have no earlier conversation about it, start from the issue and the code.`;

/**
 * What "Carry on" sends when a card exists, the workspace is up, the server
 * says nothing is running, and nothing is waiting on the user (`card_stopped`).
 * The same shape as `makeRefineNudge`: a same-flow turn, never a boundary, in
 * machine words sent with `evidenceText: ""`, so nothing in it can ground a
 * field. Live (dev #284, #285): the first sandbox turn proposed a card holding
 * only the priority the boot-window agent had asked about, the Stop hook let it
 * end (`reason=only_needs_input`), and nothing more came until the user wrote
 * "please fill in the rest of the card" — which is what this says for them.
 */
export const makeCarryOnNudge = (iid: number): string =>
  `Carry on refining issue #${iid}. The readiness card is not finished yet: ` +
  `work out the fields that are still missing from the issue and the code, ` +
  `and ask me what only I can answer.`;

// How long a RESUMED mount listens for proof that the server still holds this
// issue's flow before giving up and opening a new one.
//
// Deliberately generous, because the two failure directions are not
// symmetrical: seeding too early DESTROYS a live conversation (the seed carries
// `boundary: true`), while waiting too long only leaves the boot screen up a few
// seconds longer on a flow that is genuinely gone. The evidence rides the attach
// replay, which lands within the same second as `session_attached`, so this
// timer only ever gets to fire when there is nothing to find.
const RESUME_SEED_GRACE_MS = 12_000;

// The `!card` branch is the ladder's fall-through, and it used to end in a
// browser-only silence clock: three minutes with no visible change put up
// "Nothing has come back for a while … we don't know why" and a restart button.
// It was a false alarm every time it was measured (dev, 2026-09-24 → 26: 5 of
// 42 refine sessions, a card in all 5), and its button killed the running turn
// on a healthy bridge. It is gone. What the page says while it waits now comes
// from the server — see `./wait-status` — and the two "nothing is coming"
// screens (`agent_stopped`, `no_workspace`) fire only on server facts that have
// held still: the workspace is up (or absent), the server says idle, and
// nothing is open.

// How long a drained batch is held for a BUSY agent before it goes anyway. It
// used to be 12 s — and a send into a busy agent SIGTERMs its running turn, the
// same harm the old restart button did. The issue page moved to this constant
// for exactly that reason (#247, #258): the answers go out on the server's idle
// edge, and this only bounds a busy flag that never clears. The longest sandbox
// turn measured on dev is 268 s.
const FLUSH_BUSY_BACKSTOP_MS = HOLD_BUSY_BACKSTOP_MS;

// How long the parked-issue bar must stay on screen, unbroken, before
// `refine.parked_notice_shown` reports it as seen rather than as a replay flash.
export const PARKED_NOTICE_HELD_MS = 2_000;

// Client backstops for the tail turns. These sit ABOVE the server's wall-clock
// ceilings (F6), sourced from the ONE shared module so they can never drift back
// below the server and pre-empt a productive turn. The `*_unavailable` degrade
// is transient (not reconnect-re-emitted) and the client wipes its timeline on
// reconnect, so these only rescue a degrade frame LOST across a reconnect — a
// delivered server decision always wins first.
const PLAN_LOADING_DEADLINE_MS = PLAN_CLIENT_BACKSTOP_MS;

/**
 * How long after a `readiness_plan_confirm` a plan frame still counts as the
 * SERVER'S ANSWER to it rather than an unrelated re-emit.
 *
 * The server handles the confirm on the same socket and answers a refusal in
 * the same round trip, so this only has to cover network + a slow read. It must
 * NOT be open-ended: `reemitPersistedReadinessPlan` publishes a plan frame on
 * every bridge attach, and against a server that predates the frozen plan
 * revision that re-emit carries a revision the client's held value no longer
 * equals — read as a refusal, that would bounce the user back to validate once
 * per attach for the life of the flow.
 */
const PLAN_CONFIRM_ANSWER_WINDOW_MS = 20_000;

/**
 * The `readiness_plan_unavailable` reasons that are an answer to a CONFIRM.
 *
 * Disjoint from the plan-request path's (`no_sandbox`, `plan_timeout`) on
 * purpose: a degrade frame carries no revision, so the reason is the only thing
 * that tells the server's answer to this confirm apart from a historical
 * degrade replayed on a reconnect. Kept in sync with
 * `handleReadinessPlanConfirm`'s `degrade(...)` calls — a reason added there
 * and not here is a silent refusal again.
 */
const PLAN_CONFIRM_DEGRADE_REASONS: ReadonlySet<string> = new Set([
  "no_store",
  "no_plan",
  "plan_stale",
]);
// The client cannot see whether the server took the re-size lane, so its one
// backstop sits above the LONGER ceiling. Being above a DELIVERED server
// decision is the whole point — the backstop only ever rescues a degrade frame
// lost across a reconnect, never races a turn the server is still waiting on.
const BREAKDOWN_REVIEW_DEADLINE_MS = BREAKDOWN_RESIZE_CLIENT_BACKSTOP_MS;
// sizing_wait starts mid-turn (after plan_ready), so the server's own sizing
// decision is due within the same 480s wall clock the plan backstop already
// sits above — reuse it rather than minting a third constant that could drift.
const SIZING_WAIT_DEADLINE_MS = PLAN_CLIENT_BACKSTOP_MS;

// The same rule as those, applied to the WORKING state: a turn that comes back
// with nothing must not strand the spinner. The busy leg reuses the tail
// wall-clock backstop, so a turn that is genuinely still producing is never cut
// off (it never fired on dev: 0 of 75 thinking steps). The idle leg is a SERVER
// fact, not a silence bar: the workspace is up, the server has said idle and
// nothing of ours is held, for `IDLE_SETTLE_MS` (see the thinking effect).
const THINKING_BUSY_DEADLINE_MS = PLAN_CLIENT_BACKSTOP_MS;

export function RefineFlow({
  projectId,
  iid,
  issueTitle,
  issueWebUrl,
  seed,
  seedEvidence,
  resumed,
  onProjects,
  onRestart,
  onOpenIssue,
  onSwitchIssue,
  onFreshSession,
  issueCurrent,
}: RefineFlowProps) {
  // THIS issue's session: its own, the tab's pre-warm claimed on the spot, or
  // a new one — never a session another issue holds (`useProjectSession`).
  const session = useProjectSession(projectId, "issue", iid);
  const ws = useWsSession(session.sessionId);
  const t = useT();
  // The escape screens' way out: back to the project picker.
  const leaveLabel = t("focus.refine.fatal.goToProjects");
  // The OPERATOR's UI locale — used only to pick the story-clause connective +
  // its matching strip on the review screen. It is NOT the project's content
  // locale, which is what the server composes the written artifact at.
  const { locale: uiLocale } = useLocale();

  // Tab close / reload → DETACH the project sandbox (kept alive for the server's
  // reconnect grace so a reload re-adopts it). Same hook the wizard + chat use.
  // Opts into the unmount detach: this surface holds the session's WebSocket,
  // so a return re-attaches and clears the mark well inside the 90s grace.
  // Leaving refine by any in-app route change (a rail pick, the projects
  // escape, the breadcrumb) used to strand this sandbox billing until the idle
  // reaper.
  useSessionPagehideBeacon(session.sessionId, { detachOnUnmount: true });

  // …and the other half of that contract: an ADOPTED session (reload or
  // deep-link) never went through `POST /sessions`, so nothing triggered a
  // pipeline. Re-arm the bridge on open, or a refine URL opened after the
  // sandbox died sits at "starting" with no CI job behind it.
  useSessionBridgeEnsure(session.sessionId);

  // A persisted session binding must not outlive its own session's
  // death: once the server calls it dead — `expired` (gone) or `denied`
  // (refused) — drop the binding so the NEXT reload cold-boots instead of
  // re-adopting the corpse (the expiry-recovery trap). The current screen still
  // shows the expired / denied state — this only changes what a future reload
  // does.
  useEffect(() => {
    if (isSessionDead(ws.status)) clearPersistedSession(projectId, "issue", iid);
  }, [ws.status, projectId, iid]);
  const fieldCopy = useFieldCopy();
  const sourceLabel = useSourceLabel();
  // Localized sandbox-phase label, or undefined for an unmapped phase so the
  // call site can supply its own fallback copy (as the old SANDBOX_LABEL did).
  const phaseLabel = (phase: string): string | undefined =>
    SANDBOX_PHASES.has(phase)
      ? t(`focus.sandbox.${phase}` as Parameters<typeof t>[0])
      : undefined;

  // In-progress widget drafts, owned HERE because the widget subtree is
  // remounted out from under the user by design: FocusFrame's `key={stepKey}`
  // replays the fade-up animation by remounting the body, and the reconnect
  // timeline wipe flips derived state. The widgets re-seed from this store via
  // `useRemountSafeState` (site-prefixed keys — see the AnswerWidget sites).
  // Mount-scoped: RefineFlow is keyed by issue, so drafts never cross issues.
  const widgetDraftsRef = useRef<RemountSafeStore | null>(null);
  if (widgetDraftsRef.current === null)
    widgetDraftsRef.current = createRemountSafeStore();

  // Boot (or adopt) this issue's session. `session.ensure` is idempotent and
  // stable per (projectId, issue), and RefineFlow is keyed by issue, so one
  // mount asks for one issue.
  const ensure = session.ensure;
  useEffect(() => {
    ensure();
  }, [ensure]);

  // Proof that the SERVER still holds THIS issue's flow. Only consulted on a
  // resumed mount, where it is the gate on whether we may re-open the flow.
  //
  // The attach replay is the only channel a cold-mounting client has: on every
  // client attach the server replays its whole in-memory bus (rehydrated from
  // the durable event log when the session had been evicted), so everything
  // this issue's flow already produced comes back. The signals, all issue-bound
  // and all replay-worthy (`eventIsReplayWorthy` keeps them; the volatile
  // status/phase events it drops are exactly the ones that would lie here):
  //   - a `readiness_card` for this iid — the strongest. It is projected from
  //     the durable per-(session, issue) `CardState` and re-emitted from that
  //     store on every bridge attach, so its presence means the flow's
  //     server-side state is alive, not merely that a frame once flew past.
  //   - an `agent_ask` for this iid — the typed pre-card channel, made
  //     replay-worthy precisely so it survives a reload. This is the ONLY
  //     evidence that exists when the reload lands during the first turn,
  //     before any card has been proposed.
  //   - a `breakdown_card` for this iid — the same, for the end-of-flow tail.
  //   - the seed's own `user_message` echo — proof this exact flow was opened
  //     on this bus. Weakest of the four (it matches only while the persisted
  //     seed bytes match and the event survives the 4000-event ring cap), so it
  //     never has to stand alone — but it covers a reload during the silent
  //     opening turn, where nothing else has been emitted yet.
  //   - the LAST replayed `user_message`, when it is bound to this iid. This is
  //     what makes a history pick safe: the rail routes a session to refine
  //     precisely when the server derived `flowIssueIid` from the highest-seq
  //     user turn in the persisted log (`deriveFlowFromEvents`), and the client
  //     replay is FULL — so the very turn that decided the routing
  //     replays here and latches `flowAliveRef` before the grace can fire.
  //     Scoped to the LAST turn because that is the fact the server derives
  //     from: an intervening flow-less turn (free chat on the shared project
  //     session) or a turn on another issue CLOSES this flow server-side, and
  //     an earlier issue-bound turn still sitting in the replayed log would
  //     otherwise claim a flow the server no longer holds — a refine screen
  //     that never seeds and is permanently mute.
  //
  // NOT used: the bridge / sandbox phase. The pre-warm this issue claims boots
  // before any flow exists, so a warm sandbox says nothing about whether THIS
  // issue's flow exists — gating on it would keep the seed from ever going out
  // on a genuine cold start into a warm sandbox.
  const flowEvidence = useMemo(() => {
    // The timeline is appended in bus-seq order, so the last `user_message` it
    // holds IS the highest-seq one — the same row `deriveFlowFromEvents` reads.
    let latestUserIssueIid: number | null = null;
    for (const e of ws.timeline) {
      if (
        (e.kind === "readiness_card" ||
          e.kind === "agent_ask" ||
          e.kind === "breakdown_card") &&
        e.issueIid === iid
      ) {
        return true;
      }
      if (e.kind === "user_message") {
        if (e.text === seed) return true;
        latestUserIssueIid = typeof e.issueIid === "number" ? e.issueIid : null;
      }
    }
    return latestUserIssueIid === iid;
  }, [ws.timeline, iid, seed]);
  // …and the one thing that CLOSES the flow. An applied card is single-use: the
  // server stamps `consumedAtMs` and stops re-emitting it, but the last card
  // event stays in the replayed bus forever — so the signals above would keep
  // reading "alive" on an issue that is finished, and a later visit would park
  // the user on a ready screen whose Accept the server is bound to refuse. A
  // FAILED apply deliberately does not count: the server releases the consume
  // guard and re-offers the card, so that flow really is still live.
  //
  // DURABLE by construction, and that is the point: `readiness_apply_result`
  // rides the replayed event log, so this survives the reconnect timeline wipe
  // that `accepted` (plain component state) does not.
  const applied =
    ws.applyResult !== null &&
    ws.applyResult.issueIid === iid &&
    ws.applyResult.ok;

  // The TERMINAL condition for the screen ladder and for every effect that can
  // fire a billed turn.
  //
  // `accepted` alone was the terminal signal, and it dies on reload — so a
  // reload after a successful apply rebuilt the whole pre-apply ladder on top of
  // an issue GitLab already had: the plan-validate screen for a written issue
  // (whose "Yes, that's right" sent a real `readiness_plan_confirm`), then the
  // ready screen with `Accept & apply` armed again. Derive it from the applied
  // fact instead.
  //
  // Scoped to a RESUMED mount on purpose. A FRESH mount is the wizard
  // navigating in deliberately — a new round on this issue — and it carries its
  // own seed, whose fence (`seedIdx`) already excludes the applied card. Letting
  // a previous round's apply terminate that mount would make an applied issue
  // permanently un-refinable for the rest of the session.
  const flowTerminal = applied && resumed;

  // Seed the refine turn once the socket is actually open (send no-ops off-OPEN).
  const seededRef = useRef(false);
  // Read by the `refine.seed_send` trace only. Refs, so the seed effect below
  // keeps its exact dependency list: a trace must never change WHEN it runs.
  const mountedAtRef = useRef(Date.now());
  const timelineRef = useRef(ws.timeline);
  timelineRef.current = ws.timeline;
  const sandboxPhaseRef = useRef(ws.sandbox.phase);
  sandboxPhaseRef.current = ws.sandbox.phase;
  /**
   * `refine.seed_send` — the flow-opening seed went out (or, `why:
   * "nudge_after_reconnect"`, the same-flow nudge a resumed restart gets), and
   * what the timeline held when it did. `priorFlowIssueIid` is the issue of the newest
   * `user_message` already on this session's timeline: another issue's number
   * there is a seed landing in a session that was working on something else.
   */
  const traceSeedSend = (
    why: "mount" | "after_reconnect" | "nudge_after_reconnect" | "carry_on",
  ): void => {
    let priorFlowIssueIid: number | null = null;
    const timeline = timelineRef.current;
    for (let i = timeline.length - 1; i >= 0; i--) {
      const e = timeline[i]!;
      if (e.kind === "user_message" && e.issueIid !== undefined) {
        priorFlowIssueIid = e.issueIid;
        break;
      }
    }
    reportClientEvent("refine.seed_send", {
      issueIid: iid,
      resumed,
      waitedMs: Math.max(0, Date.now() - mountedAtRef.current),
      priorFlowIssueIid,
      sandboxPhase: sandboxPhaseRef.current,
      why,
    });
  };
  // Monotonic latch over `flowEvidence`. The attach effect WIPES the timeline
  // and replays it on every reconnect, so the derivation above drops
  // to false for the replay window — long enough for the grace timer to fire
  // into a gap and seed over a flow we had already proven alive. false→true
  // only, mount-scoped, and RefineFlow is keyed by issue, so it can never carry
  // across issues: the same shape (and the same reason) as the card latch below.
  const flowAliveRef = useRef(false);
  const [resumeGraceElapsed, setResumeGraceElapsed] = useState(false);

  // The grace clock. Armed on a resumed mount once the socket is OPEN — the
  // evidence rides that socket's attach replay, so there is nothing to listen
  // for before then and starting earlier would just burn the window on the
  // connect. It only flips a flag; the decision is re-made in the seed effect,
  // which re-reads the evidence at that instant. That is what makes the race
  // safe: evidence landing in the same commit as the timer still wins, and
  // there remains exactly ONE place that sends the seed.
  //
  // The DEADLINE is wall-clock and set once; the `setTimeout` is only a way to
  // wake up on it. This effect re-runs on every socket transition and its
  // cleanup tears the timer down, so a plain full-length restart here starved:
  // reconnect backoff caps at 8s, under the 12s grace, and a flapping socket
  // therefore pushed the flag out forever — a resumed mount that neither hears
  // from the server nor ever seeds is permanently mute, with no way out but a
  // reload. Time spent disconnected counts against the deadline for the same
  // reason: it is time the server had to replay evidence in and didn't.
  const resumeDeadlineRef = useRef<number | null>(null);
  useEffect(() => {
    if (!resumed || ws.status !== "open") return;
    const deadline = resumeDeadlineRef.current ?? Date.now() + RESUME_SEED_GRACE_MS;
    resumeDeadlineRef.current = deadline;
    const remaining = deadline - Date.now();
    if (remaining <= 0) {
      setResumeGraceElapsed(true);
      return;
    }
    const timer = setTimeout(() => setResumeGraceElapsed(true), remaining);
    return () => clearTimeout(timer);
  }, [resumed, ws.status]);

  useEffect(() => {
    if (seededRef.current || ws.status !== "open") return;
    if (resumed) {
      if (flowEvidence && !applied) flowAliveRef.current = true;
      // The server still has this conversation → NEVER seed. Leaving the
      // agent's context intact is the entire point of the resume path.
      if (flowAliveRef.current) return;
      // This issue's card was APPLIED. Re-opening the flow here used to be the
      // answer to "the replayed card would park the user on a dead ready
      // screen"; the terminal screen below is the better one, because a seed
      // carries `boundary: true` and bills a fresh sandbox round for nothing but
      // a reload. Re-refining is now a deliberate act: leave to the picker and
      // come back, which arrives as a FRESH mount whose seed fence excludes the
      // applied card.
      if (applied) return;
      // Nothing yet — keep listening. Seeding here is not recoverable, so an
      // undecided state waits rather than guesses.
      if (!resumeGraceElapsed) return;
      // The grace elapsed with nothing to find: the flow really is gone (a new
      // session, an evicted log, an issue this session never refined). Seeding
      // is now the only way the agent gets any context at all — withholding it
      // would hand the user a permanently dead session.
      focusDiag("seed_resume_fallback", { iid });
    }
    focusDiag("seed");
    traceSeedSend("mount");
    // The seed opens the flow (`boundary: true`): the server clears the prior
    // flow's CLI conversation and moves the evidence eviction horizon to this
    // turn, so issue A's chatter can't ground issue B's card.
    ws.send(seed, {
      scope: `refine:${iid}`,
      // The issue title rides the BOUNDARY send only — the label the server
      // writes onto the conversation's index row, so the history rail can name
      // it "#82 · <Issue-Titel>" instead of this English machine seed. It is
      // display metadata: never evidence, never part of the routed prompt.
      flow: { issueIid: iid, boundary: true, issueTitle },
      // The seed is MACHINE-COMPOSED, so it declares which of its bytes the user
      // actually wrote; the gate quotes only those. Spread on `!== undefined`,
      // never on truthiness — the refine seed's span is `""` and dropping it
      // would hand the whole composed turn back to the gate as user evidence.
      ...(seedEvidence !== undefined ? { evidenceText: seedEvidence } : {}),
    });
    seededRef.current = true;
  }, [
    ws.status,
    ws.send,
    seed,
    seedEvidence,
    iid,
    resumed,
    flowEvidence,
    applied,
    resumeGraceElapsed,
  ]);

  // The held batch as it was left in sessionStorage by a PRIOR mount of this
  // same (project, issue) — a reload, a nav-away-and-back, a deep link.
  // `useState` so it is read exactly once, at mount, in this component
  // instance's own first render — every state initializer below seeds off
  // this single snapshot rather than re-reading storage per field. `null`
  // (nothing persisted, or storage unavailable) leaves every field exactly
  // where it started before this fix.
  const [restoredBatch] = useState<HeldBatch | null>(() =>
    readHeldBatch(projectId, iid),
  );

  // Answers the user has given but we have NOT sent yet — the batch. Keyed by
  // STEP KEY (`stepKeyOf`: a field, or `ask:<askId>` for a field-less typed
  // ask); the value is the canonical send-text. A key present here is
  // suppressed from the queue (answered) and contributes to the next flush.
  const [answers, setAnswers] = useState<ReadonlyMap<string, string>>(() => {
    const m = new Map<string, string>();
    for (const row of restoredBatch?.answered ?? []) {
      m.set(heldBatchKey(row), row.text);
    }
    return m;
  });
  // Attribution for the held answers that settle a TYPED ASK: same step key as
  // `answers`, carrying which ask it answers. Kept SEPARATE from `answers` so
  // the composed turn text stays byte-identical to the plain-send path — the
  // attribution rides its own array on the frame and is never re-serialised
  // into the text the grounding scan quotes against.
  const [askAnswers, setAskAnswers] = useState<
    ReadonlyMap<
      string,
      {
        readonly askId: string;
        readonly field: string | null;
        // Rides to the server on the frame. A concern answer is a DECISION about
        // the field, not the field's new value, and the server composes a
        // different prompt for it — one that does not tell the agent to propose
        // the reply as the value.
        readonly kind: "question" | "concern";
      }
    >
  >(() => {
    const m = new Map<
      string,
      { askId: string; field: string | null; kind: "question" | "concern" }
    >();
    for (const row of restoredBatch?.answered ?? []) {
      if (row.askId === null) continue;
      m.set(heldBatchKey(row), {
        askId: row.askId,
        field: row.field,
        kind: row.concern ? "concern" : "question",
      });
    }
    return m;
  });
  // Optional typed asks the user SKIPPED, awaiting the flush that tells the
  // server to stop waiting on them (a decline still has to reach the agent —
  // unlike an optional card field, which is simply left unset locally).
  const [pendingDeclines, setPendingDeclines] = useState<ReadonlySet<string>>(
    () => new Set((restoredBatch?.declined ?? []).map((d) => d.askId)),
  );
  // `field`/`concern` for each id in `pendingDeclines` — `declineAsk` knows
  // both at the moment of the click, but `pendingDeclines` itself is bare
  // askIds (it rides the wire as `declinedAskIds`, an id array). Kept so the
  // persisted batch can restore `declined` below (a field-bound decline masks
  // its FIELD too, not just its ask — see `declineAsk`'s own comment) without
  // waiting for a card or the typed-ask list to re-derive it.
  const [declinedAskMeta, setDeclinedAskMeta] = useState<
    ReadonlyMap<string, { readonly field: string | null; readonly concern: boolean }>
  >(
    () =>
      new Map((restoredBatch?.declined ?? []).map((d) => [d.askId, { field: d.field, concern: d.concern }])),
  );
  /**
   * The sketch verdict's own receipt: what the user just said about the design
   * Loom read, and — after a "no" — what to do about it and what that costs.
   *
   * Mount-local on purpose. It is a receipt for an act the user has just taken,
   * not a state of the flow: nothing downstream reads it, nothing gates on it,
   * and losing it to a reload costs a sentence the user has already read. The
   * DECISION is durable (the server resolved the ask), which is the half that
   * has to survive.
   *
   * `linkCount` is the size of the flow's chip list at the moment of the
   * verdict, so a "no" can clear itself the instant the user does what it asks
   * — pastes another link — rather than sitting under the new design telling
   * them the old one was wrong.
   */
  const [previewEcho, setPreviewEcho] = useState<{
    readonly verdict: "right" | "wrong";
    readonly linkCount: number;
  } | null>(null);
  // Every ask the user has ACTED ON this mount (answered or declined). Permanent
  // and askId-keyed, which is exactly right for a one-shot ask: an ask id is
  // minted per question, so a genuine re-ask arrives as a NEW id and surfaces
  // normally. This is what keeps an ask from re-pinning in the window between
  // the flush and the server's `question_resolved`.
  const [handledAskIds, setHandledAskIds] = useState<ReadonlySet<string>>(
    () => {
      const s = new Set<string>();
      for (const row of restoredBatch?.answered ?? []) {
        if (row.askId !== null) s.add(row.askId);
      }
      for (const d of restoredBatch?.declined ?? []) s.add(d.askId);
      return s;
    },
  );
  // Fields the user has ACTED ON — answered themselves OR explicitly KEPT a
  // value Loom derived (the former `kept` map folds in here: keeping is acting).
  // Field-keyed and PERMANENT: it suppresses re-REVIEWING a value the user owns
  // ("Loom set this — keep it?" on the user's own/kept input is redundant; the
  // gate already quotes their words back, possibly reformatted — see the
  // "reformats the user's words" test) AND suppresses the grind-drift re-ASK
  // (a field flickering red/unavailable across the Stop-hook grind). It does
  // NOT suppress a genuine `needs_input` — that bypasses the latch (the ask
  // path checks `verdict !== "needs_input"`), so a real later re-ask still
  // surfaces. Phase 4's monotonic server store is what makes the permanent
  // latch honest: a committed green can no longer revert, so there is nothing
  // for a stale mask to hide (this is why the old `kept` + `provided_regressed`
  // band-aids were retired).
  const [provided, setProvided] = useState<ReadonlySet<string>>(() => {
    const s = new Set<string>();
    for (const row of restoredBatch?.answered ?? []) {
      if (row.field !== null) s.add(row.field);
    }
    return s;
  });
  // Fields the user explicitly SKIPPED — only ever OPTIONAL ones (e.g. milestone).
  // Unlike `provided`, this is a HARD suppression with no needs_input bypass: the
  // user declined the field, so it must not re-surface as a question this session,
  // even if the agent re-emits it `needs_input` on a later grind. Optional fields
  // never block `allGreen`, so declining one simply lets the flow proceed.
  const [declined, setDeclined] = useState<ReadonlySet<string>>(() => {
    // Same gate `declineAsk` applies live: only a non-concern, spec-bound,
    // OPTIONAL field's decline masks the field itself.
    const s = new Set<string>();
    for (const d of restoredBatch?.declined ?? []) {
      if (d.field !== null && !d.concern && isOptionalField(d.field)) {
        s.add(d.field);
      }
    }
    return s;
  });
  // Fields whose skip the user UNDID (see `declinedAll`). Overrides both the
  // mount-local `declined` and the card's durable `declined` flag, so the
  // question comes back and can be answered normally.
  //
  // Seeded from the restored batch, and it has to be: what it overrides is
  // DURABLE. The skip left the browser, the server stamps it onto the slot, and
  // every later card re-asserts it — so an override that died with the mount
  // meant the undo held until the next reload and then silently evaporated,
  // leaving the question masked with no surface left to un-skip it (a declined
  // field never reaches the ready screen, so there is no second door). Both
  // halves of that argument now survive the same remount.
  const [unDeclined, setUnDeclined] = useState<ReadonlySet<string>>(
    () => new Set(restoredBatch?.unDeclined ?? []),
  );
  // The raw send-text of an answer the user UNDID, by step key. `undoLast`
  // removes the answer from `answers` — that is what undoing IS — so without
  // this the restored step has nothing left of what was picked. A TEXT control
  // gets it back through the widget draft store (`undoLast` rewrites the draft);
  // a CHOICE control has no draft store, so an undone milestone came back as
  // twenty identical buttons with nothing marked. Cleared when the step is
  // answered again, so it can never mark a stale value as "your previous
  // answer" on a later re-ask.
  const [undone, setUndone] = useState<ReadonlyMap<string, string>>(
    () => new Map(),
  );

  // Write the batch back to sessionStorage on every change, so the NEXT mount
  // of this (project, issue) — a reload above all — restores it via
  // `restoredBatch` above. Fires on mount too (writing back the same
  // snapshot `restoredBatch` just seeded from, a no-op) and on every flush /
  // `ws.resetEpoch` clear, where `answers`/`askAnswers`/`pendingDeclines`/
  // `declinedAskMeta` all land back at empty and `writeHeldBatch` removes the
  // row instead of persisting an empty one.
  useEffect(() => {
    const answered: HeldBatchRow[] = [...answers].map(([key, text]) => {
      const attrib = askAnswers.get(key);
      return attrib
        ? { field: attrib.field, askId: attrib.askId, concern: attrib.kind === "concern", text }
        : { field: key, askId: null, concern: false, text };
    });
    const declinedRows: HeldBatchDecline[] = [...pendingDeclines].map((askId) => {
      const meta = declinedAskMeta.get(askId);
      return { askId, field: meta?.field ?? null, concern: meta?.concern ?? false };
    });
    // `unDeclined` rides along even though nothing about it is "held": it
    // outlives the flush on purpose. The flush empties `answers` and
    // `pendingDeclines`, and an un-skipped field the user has not answered YET
    // still needs its override the next time this component mounts, or the
    // durable card flag re-masks it.
    writeHeldBatch(projectId, iid, {
      answered,
      declined: declinedRows,
      unDeclined: [...unDeclined],
    });
  }, [
    answers,
    askAnswers,
    pendingDeclines,
    declinedAskMeta,
    unDeclined,
    projectId,
    iid,
  ]);

  // The PINNED on-screen step. Card churn never touches this — only user action
  // (answer / keep / change) clears it, after which the reconciliation effect
  // pulls the next item from the queue.
  const [current, setCurrent] = useState<Step | null>(null);
  // The most-recently-answered step, kept ONLY until its answer flushes, so the
  // user can pull back an answer they submitted too fast (see `undoLast`). `key`
  // is the `answers`-map key (`stepKeyOf`) — the presence of that key in
  // `answers` is the safety proof the flush hasn't fired. One-shot, not a stack:
  // every user action overwrites it, so only the very last action is undoable.
  const [lastAnswered, setLastAnswered] = useState<{
    readonly step: Step;
    readonly key: string;
    /** The act was a SKIP of an optional field, which holds no `answers` entry —
     *  so its undo window is proven by `lastAnswered` alone (a later act
     *  overwrites this record) rather than by the held answer. */
    readonly skipped?: true;
    /** The act was a DECLINE of an optional typed ask. Also holds no `answers`
     *  entry; its held state is the id in `pendingDeclines`, which the flush
     *  clears — so the window closes exactly when an answer's does. */
    readonly declinedAskId?: string;
  } | null>(null);
  const [editingReview, setEditingReview] = useState(false);
  // A green field the user opened for editing from the final "ready to apply"
  // preview. Independent of `current` — the all-green pin-release effect forces
  // `current` to null, so the preview keeps its own edit toggle.
  const [editField, setEditField] = useState<string | null>(null);
  // The ready row whose editor was just closed by a bare yes — it shows "Nothing
  // changed". Cleared by the next real edit or the next editor opened.
  const [keptField, setKeptField] = useState<string | null>(null);
  const [accepted, setAccepted] = useState(false);
  // Post-accept split offer (F6): sizing/breakdown that lands AFTER the user
  // already accepted (the raised server ceiling degraded, the user accepted in
  // the gap, then the real sizing arrived) can still be split — the server path
  // is legal (parent already consumed, executeApplyBreakdown runs off it).
  const [postAcceptSplitRequested, setPostAcceptSplitRequested] = useState(false);
  // The user has an OPEN split interaction on the APPLIED screen: they asked for
  // a split there, or they confirmed a proposal. Mount-scoped.
  const [splitEngaged, setSplitEngaged] = useState(false);
  // …and its opposite: the user backed out of a split proposal.
  //
  // `breakdownCard.phase` is derived from the latched `breakdown_card` entry, and
  // the entry is replayed from the timeline forever — so a DECLINED proposal
  // reads `review` for the rest of the session. Live, that put a "Create 2
  // sub-issues … no undo" button on the terminal screen one second after
  // `apply.write_ok`, for a split the user had already declined.
  const [splitDeclined, setSplitDeclined] = useState(false);
  const [postAcceptSplitDismissed, setPostAcceptSplitDismissed] = useState(false);
  // The post-accept split came back "one ticket is right". Separate from
  // `noSplitAffirmed`, which only covers the PRE-accept `breakdown_review` tail.
  const [postAcceptNoSplit, setPostAcceptNoSplit] = useState(false);
  // The post-accept split could not be drafted at all
  // (`readiness_breakdown_unavailable`). The SECOND of the two clauses that
  // silently unmount the spinner and the offer together — see the latch below.
  const [postAcceptDegraded, setPostAcceptDegraded] = useState(false);
  // True when an accept has been waiting on its `readiness_apply_result` past
  // the deadline — surfaces a Retry so a dropped accept frame (socket flap)
  // can't strand the user on "applying…".
  const [applyTimedOut, setApplyTimedOut] = useState(false);
  // End-of-refine tail round (see TailPhase). `businessSummary` is the only
  // plan text the client ever sees — the technical plan stays server-side.
  const [tail, setTail] = useState<TailPhase>("idle");
  const [businessSummary, setBusinessSummary] = useState<string | null>(null);
  // Which persisted plan revision the summary ON SCREEN belongs to. Set with
  // `businessSummary` from the same result, and sent back on the confirm so the
  // server can only ever mark confirmed the revision the user actually read: the
  // tail turn produces plan and sizing together, so a NEWER plan can land while
  // the validate screen is up — the consumer effect below drops it (it only acts
  // in `plan_loading`), which is exactly the skew this binding closes. `null`
  // against a server that echoes no revision → no confirm at all (fail closed:
  // apply then omits the section rather than writing an unvalidated plan).
  const [planRevision, setPlanRevision] = useState<number | null>(null);
  // Set when the sizing/plan tail degraded (timeout or unavailable) so the
  // Accept screen can say WHY the review never appeared, instead of the user
  // silently landing back on Accept with no explanation.
  const [tailDegraded, setTailDegraded] = useState(false);
  // The server refused the plan confirmation — a DIFFERENT failure from
  // `tailDegraded`, and it needs its own latch because it needs its own
  // sentence. `tailDegraded` renders "Loom couldn't finish sizing this", which
  // on this path is false twice over: sizing landed (a superseded confirm is
  // reached from `sizing_wait`/`breakdown_review`, where the size verdict is in
  // hand), and what the user actually lost is the implementation section their
  // "Yes" was for.
  const [planConfirmLost, setPlanConfirmLost] = useState<
    "superseded" | "unavailable" | null
  >(null);
  // A user-initiated re-run of the sizing round from the Accept screen, still in
  // flight. `readiness_plan_request` is the exact frame behind the 166-frames →
  // 260-respawns → OOMKilled incident, so the retry control must never be
  // re-clickable while its turn is running: the server's per-issue cap bounds
  // the blast radius, this latch removes the source. Cleared by a deadline
  // (below), never by a result — the note stays up either way.
  const [planRetryInFlight, setPlanRetryInFlight] = useState(false);
  // Set when the agent affirmatively decided NOT to split (noSplit) — the Accept
  // screen then says "checked, one ticket is right" instead of leaving a forced
  // split looking like it silently failed.
  const [noSplitAffirmed, setNoSplitAffirmed] = useState(false);
  // The last plan result we acted on — so a stale `ready` from the first pass
  // can't auto-advance a correction re-run before the revised plan lands.
  const consumedPlanRef = useRef<typeof ws.planResult>(null);
  // The plan result that was already on hand at the moment we asked for a new
  // one. A request is never satisfied by the result it was raised against.
  const planAtRequestRef = useRef<typeof ws.planResult>(null);
  // The confirm we are still waiting to hear about, and WHEN we sent it.
  //
  // The server answers a refused `readiness_plan_confirm` on the same socket,
  // synchronously — a mismatch re-emits `readiness_plan_ready` with the
  // revision it actually holds, and no_store / no_plan / plan_stale emit
  // `readiness_plan_unavailable`. Both frames used to die at the consumer
  // effect's `tail !== "plan_loading"` gate, because the Yes handler leaves
  // `validate` synchronously — so a "Yes, that's right" that the server refused
  // was, on the screen, indistinguishable from one it accepted.
  //
  // TIME-BOUNDED on purpose. It is a refusal listener, not a subscription:
  // `reemitPersistedReadinessPlan` fires a `readiness_plan_ready` on EVERY
  // bridge attach, and against an older server (which still bumped the plan's
  // revision on confirm) an unbounded ref would read that re-emit as a refusal
  // and bounce the user back to validate once per attach, forever.
  const pendingPlanConfirmRef = useRef<{ revision: number; atMs: number } | null>(
    null,
  );
  // The plan revision the user pressed "Not quite" ON, for as long as its
  // correction re-run is in flight (`null` otherwise).
  //
  // Object identity alone does not survive this screen. `readiness_plan_ready`
  // is re-emitted as a FRESH object by paths that have nothing to do with the
  // correction — the server self-heals a dropped plan from `CardState` on every
  // bridge attach, and the confirm path re-emits it on a revision mismatch — so
  // a re-emit of the plan the user just rejected passes both identity guards
  // and, in `plan_loading`, is consumed as if it were the answer. Live that
  // took 9ms: the correction screen exited its loader straight back to
  // `validate` showing the PRE-correction summary, the revised plan then
  // arrived to a `tail` that was no longer `plan_loading` and was dropped, and
  // "Yes, that's right" bound the confirm to a revision the server has
  // superseded (which it refuses, so apply omits the plan section entirely).
  // A revision at or below this one is by construction not the re-run's answer.
  // `null` = no correction in flight; `{fromRevision: null}` = one is in flight
  // against a server that echoes no revision (the guard then rests on identity
  // alone, and `confirmPlan` already fails closed there).
  //
  // PERSISTED, for the same reason the held answer batch is: the re-run takes
  // MINUTES, and a remount inside that window is a normal thing to do on this
  // screen — the resume branch below exists precisely because `tail` is
  // component-local and resets to "idle" when you leave to the projects and
  // come back, or reload. A mount-local marker is gone by then, `ws.planResult`
  // still holds the REJECTED plan (full replay re-materialises it), and the
  // resume branch consumed it straight onto the validate screen — the same
  // defect this guard closes, reached one step differently. Same per-tab,
  // per-issue lifetime and the same fail-open try/catch as `readHeldBatch`.
  // …and STAMPED, because persistence without a liveness bound is its own
  // stall. Every clearer above needs this component mounted at that instant
  // (the deadline effect's `clearTimeout` cleanup runs on unmount), so a tab
  // that leaves during a re-run whose degrade never lands — the pagehide detach
  // grace evicts the live session at 90s and cancels the sandbox pipeline, so
  // the server's own ≤480s degrade is emitted onto a bus nobody persists —
  // leaves the marker behind with nothing left to remove it. Coming back then
  // restored it, matched the rejected revision, and held the escape-less
  // "Reworking this with your correction" loader for the full client backstop
  // with no turn running anywhere. `requestedAtMs` is what makes that
  // answerable: past one backstop from the request, a re-run provably is not in
  // flight (the server hard-stops at TAIL_TURN_WALL_CLOCK_MAX_MS, below it).
  const [planCorrection, setPlanCorrectionState] =
    useState<PlanCorrection | null>(() => readPlanCorrection(projectId, iid));
  const setPlanCorrection = useCallback(
    (next: { readonly fromRevision: number | null } | null): void => {
      // Stamped HERE rather than by the caller: the one non-null call site is
      // `submitCorrection`, i.e. the send itself, so "now" is the request time.
      const stamped: PlanCorrection | null =
        next === null
          ? null
          : { fromRevision: next.fromRevision, requestedAtMs: Date.now() };
      writePlanCorrection(projectId, iid, stamped);
      setPlanCorrectionState(stamped);
    },
    [projectId, iid],
  );
  // PRIVACY BELT for the implementation-plan turn. The plan auto-prompt and the
  // agent's reply name full repository paths; both ride the ordinary assistant /
  // coding-progress stream, which this surface renders (`.focus-prose` + the
  // faded scrollback). Snapshotting the timeline-entry ids that exist BEFORE the
  // plan turn starts lets us treat every LATER assistant/coding entry as
  // "plan-era" and suppress it from both the prose box and the scrollback — so a
  // file path can never surface, live or resurfaced by a later turn. Id-based
  // (not index) so it survives the reconnect timeline wipe: the same events
  // replay with the same ids, so the partition holds. Captured once, on the
  // first plan_loading transition; `null` until then (no suppression pre-plan).
  const preplanIdsRef = useRef<ReadonlySet<string> | null>(null);
  const isPlanEra = useCallback(
    (e: TimelineEntry): boolean =>
      preplanIdsRef.current !== null && !preplanIdsRef.current.has(e.id),
    [],
  );
  // The epoch we last sent a flush into; while it is still current the agent has
  // not responded → "thinking". Advancing the epoch (a new card OR a new
  // assistant turn) clears it, so a card-less reply can never pin the spinner.
  const [pending, setPending] = useState<string | null>(null);
  // PER FIELD: the card that was the latest when that field's answer was
  // flushed, and that flush's delivery id. While the answer is in flight the
  // field is suppressed from re-surfacing as a question: the stale card still
  // shows it `needs_input`, so without this `flushNow` (which clears `answers`)
  // lets the pre-flush card immediately re-ask the field the user just answered
  // (the proven end-of-flow loop).
  //
  // KEYED BY FIELD, not one record for the latest batch. A single record is
  // overwritten wholesale by the next flush, so the fact that an EARLIER field
  // was ever answered is destroyed by any later batch — and that fact is the
  // only input to `sawAnswer`, which is the only thing that can surface a
  // gate-refused field the user already answered. One 12s fallback flush (the
  // user answering a second question while the turn still grinds) was enough to
  // strand the first field for the rest of the mount: needs_input forever, no
  // question on screen, `allGreen` unreachable, Accept unreachable. A map keeps
  // every field's own proof; the entries are per-field so an old flush's card id
  // can never be mistaken for the current batch's.
  //
  // Two suppression keys, strongest first:
  // 1. Seq-keyed (the durable one): once the server acks the flush
  //    (`ws.ackedIds[clientMsgId]` = the echo's bus seq), any card whose
  //    `groundedThroughSeq` is BELOW that seq provably predates the answer —
  //    its scan could not have seen it — so it must not re-ask the flushed
  //    fields, regardless of which cardId it carries or whether the agent is
  //    busy. A card stamped AT/ABOVE the ack seq saw the answer; if it still
  //    says needs_input that is a genuine re-ask and the suppression lifts.
  // 2. Legacy same-card clause (the never-strand fallback for unstamped cards
  //    or a not-yet-acked flush): while the agent is busy or the socket is
  //    down, the SAME pre-flush card can't re-ask the flushed fields; a NEW
  //    card or a genuine idle lifts it (see the openAsks clause).
  const [flushedAt, setFlushedAt] = useState<
    ReadonlyMap<string, { cardId: string | null; clientMsgId: string | null }>
  >(new Map());

  // The last free-text send that claimed NOTHING: no question was pinned (or
  // the user chose "Send as a note"), and no held answer went out with it.
  //
  // Every waiting screen used to read "Checking your answer against the code"
  // after such a send, over a body that renders no user text at all — so the
  // user saw neither an answer nor their note, and could not tell whether the
  // words had gone anywhere. The note is kept here to show it AS SENT and to
  // name the wait for what it is.
  //
  // `pinnedKey` is the step on screen when it was sent. The receipt belongs to
  // that step: a comment on a pinned question stays beside that question, and a
  // DIFFERENT step being pinned retires it (see the effect by the screen
  // ladder). Mount-local on purpose — the echo is durable, the receipt is a
  // glance, and a remount falls back to the plain waiting copy.
  const [lastNote, setLastNote] = useState<{
    readonly text: string;
    readonly pinnedKey: string | null;
  } | null>(null);

  // /reset rebuilds the server bus from seq 0 under the same sessionId — every
  // seq-keyed comparison above becomes meaningless across it. resetEpoch is the
  // documented invalidation key for exactly this. A reset is a fresh start, so a
  // previously-declined optional field is offered again.
  //
  // React fires this effect once after the FIRST render too, epoch unchanged —
  // there is no dependency-array way to tell "mounted" from "genuinely reset".
  // Before the reload fix this was harmless (every set below started empty
  // regardless), so nothing ever depended on the distinction. It stopped being
  // harmless the moment mount could seed `handledAskIds`/`pendingDeclines`/
  // `declined`/`declinedAskMeta` from a restored batch (`restoredBatch`
  // above): this effect ran right after and wiped the restore back out — the
  // held decline resurfaced the instant its sibling question was answered and
  // reconciliation went looking for what's next. Skip exactly the mount firing;
  // every LATER resetEpoch bump within this mount's lifetime still clears.
  const skippedMountResetRef = useRef(false);
  useEffect(() => {
    if (!skippedMountResetRef.current) {
      skippedMountResetRef.current = true;
      return;
    }
    setFlushedAt(new Map());
    setDeclined(new Set());
    setUnDeclined(new Set());
    setUndone(new Map());
    // A reset wipes the event log, so every ask that latch refers to is gone
    // too — keeping it would mask asks the rebuilt bus legitimately re-raises.
    setHandledAskIds(new Set());
    setPendingDeclines(new Set());
    // Its meta rides alongside `pendingDeclines` (see the state's own
    // comment) — clear together or a NEXT decline's persisted row would
    // wrongly carry a pre-reset ask's field/concern.
    setDeclinedAskMeta(new Map());
    // A reset wipes the plan along with everything else, so a pending
    // correction refers to a revision that no longer exists — and a marker
    // nothing can ever satisfy would hold the rebuilt flow in `plan_loading`
    // until its deadline.
    setPlanCorrection(null);
    setLastNote(null);
  }, [ws.resetEpoch, setPlanCorrection]);

  // Bind the hook's send-scope to this issue for the lifetime of the mount: a
  // queued answer that outlives the surface (user switched issues mid-flap) is
  // dropped at redelivery instead of grounding against the wrong issue's card.
  const sendScope = `refine:${iid}`;
  const setSendScope = ws.setSendScope;
  useEffect(() => {
    setSendScope(sendScope);
    return () => setSendScope(null);
  }, [setSendScope, sendScope]);

  // Only consider readiness cards emitted AFTER this mount's seed — a warm reused
  // project session replays older cards for the same iid on attach, and a stale
  // allGreen one would otherwise show (and let a stale acceptToken be re-applied)
  // before the fresh seed re-grounds.
  const seedIdx = useMemo(() => {
    for (let i = ws.timeline.length - 1; i >= 0; i -= 1) {
      const e = ws.timeline[i];
      if (e && e.kind === "user_message" && e.text === seed) return i;
    }
    return -1;
  }, [ws.timeline, seed]);

  const derivedCard: ReadinessEntry | null = useMemo(() => {
    if (seedIdx === -1) return null;
    for (let i = ws.timeline.length - 1; i > seedIdx; i -= 1) {
      const e = ws.timeline[i];
      if (e && e.kind === "readiness_card" && e.issueIid === iid) return e;
    }
    return null;
  }, [ws.timeline, iid, seedIdx]);

  // The flow this screen displaced, if any — the single most recent OTHER issue
  // this session has been mid-flow on. Derived from the replay the client
  // already holds rather than from a wire event, deliberately: the user needs a
  // fact that is still true 400 events later ("#79 is unfinished"), and a
  // point-in-time record cannot answer that without being re-derived anyway. It
  // also self-clears — finish or apply that issue and the bar goes with it.
  //
  // Two admitted limits, both degrading to today's silence rather than to a
  // wrong sentence:
  //   - the 4000-event ring cap can evict the other issue's card, and then
  //     there is no notice at all;
  //   - `ws.applyResult` is a single latest slot, not a per-issue set (the same
  //     limit `applied` above lives with), so applying B and then A leaves B
  //     nameable here. Contrived — it needs three issues in one session — and
  //     the cost is a bar about finished work, not a wrong claim about live work.
  const parkedFlow = useMemo<ParkedFlow | null>(() => {
    // By SEQ, not by array position: a `readiness_card` is upserted in place at
    // its first-insert index (`useWsSession`), so position is order of first
    // appearance and a backwards walk would name the issue seen first rather
    // than the one left last. A non-finite seq is DROPPED rather than compared —
    // `NaN <= x` is false, so one of them would win every comparison and then
    // poison the running maximum for everything after it.
    let other: number | null = null;
    let otherSeq = Number.NEGATIVE_INFINITY;
    const consider = (issueIid: number, at: number): void => {
      if (!Number.isFinite(at) || at <= otherSeq) return;
      otherSeq = at;
      other = issueIid;
    };
    for (const e of ws.timeline) {
      if (e.kind === "readiness_card" && e.issueIid !== iid) {
        consider(e.issueIid, entrySeq(e.id));
        continue;
      }
      // A PRE-CARD question the flow boundary retired. `superseded` is the only
      // reason that means "a switch took this away"; `answered` / `declined` /
      // `carded` are ordinary same-session history and must not raise a notice.
      if (
        e.kind === "agent_ask" &&
        e.issueIid !== iid &&
        e.resolvedReason === "superseded"
      ) {
        // An ask id is `${sessionId}#${seq}#${askId}` — its own reader.
        consider(e.issueIid, askEntrySeq(e.id));
      }
    }
    if (other === null) return null;
    const parkedIid: number = other;
    if (
      ws.applyResult !== null &&
      ws.applyResult.issueIid === parkedIid &&
      ws.applyResult.ok
    ) {
      // Applied, so finished — not parked. The bar must never nag about work the
      // user completed.
      return null;
    }
    const resumable = ws.timeline.some(
      (e) => e.kind === "readiness_card" && e.issueIid === parkedIid,
    );
    return { iid: parkedIid, resumable };
  }, [ws.timeline, ws.applyResult, iid]);

  // The Figma chips this FLOW produced. A session can hold turns from before
  // this issue was opened (one an older bundle shared across issues and chat),
  // so `ws.figmaLinks` can hold links that belong to that chat, not to this step.
  //
  // The fence is the seed echo's seq when this mount has one — a link the
  // wizard's own text carried is scanned from the seed turn itself, so its row
  // lands after that echo. A resumed mount with no seed anchor fences on the
  // FIRST turn of this issue's current run instead: walking back from the
  // newest user turn while the turns belong to this issue. A chat turn before
  // that run ends it, and its chips — whose rows land after ITS echo — stay out.
  // No user turn at all (an evicted log) leaves nothing to fence on, so every
  // chip shows rather than none.
  const flowFigmaLinks = useMemo<ReadonlyArray<FigmaLinkSnapshot>>(() => {
    const links = ws.figmaLinks;
    if (links.length === 0) return links;
    let fromSeq = Number.NEGATIVE_INFINITY;
    const seedEntry = seedIdx === -1 ? undefined : ws.timeline[seedIdx];
    if (seedEntry !== undefined) {
      fromSeq = entrySeq(seedEntry.id);
    } else {
      let sawUserTurn = false;
      let firstFlowSeq: number | null = null;
      for (let i = ws.timeline.length - 1; i >= 0; i -= 1) {
        const e = ws.timeline[i];
        if (!e || e.kind !== "user_message") continue;
        sawUserTurn = true;
        if (e.issueIid !== iid) break;
        firstFlowSeq = entrySeq(e.id);
      }
      if (firstFlowSeq !== null) fromSeq = firstFlowSeq;
      else if (sawUserTurn) fromSeq = Number.POSITIVE_INFINITY;
    }
    // A receipt, not a record: only the chips of the user's LATEST turn. The
    // panel sits under every conversational step, so a chip kept for the whole
    // flow (and its connect card with it) followed each later question and
    // competed with it. A link pasted again moves its chip to the turn that
    // carried it (the scan stamps a new `sourceSeq`), so it comes back then.
    // So does a chip whose row changed during this step — a read the model made
    // now, an Allow it now needs: the model points the user at it, so it must
    // be on screen. Both stay inside the flow fence above.
    let latestTurnSeq = Number.NEGATIVE_INFINITY;
    for (let i = ws.timeline.length - 1; i >= 0; i -= 1) {
      const e = ws.timeline[i];
      if (!e || e.kind !== "user_message") continue;
      const at = entrySeq(e.id);
      if (Number.isFinite(at)) latestTurnSeq = at;
      break;
    }
    return links.filter((l) => {
      const at = figmaSourceSeq(l);
      return at >= fromSeq && (at >= latestTurnSeq || l.seq > latestTurnSeq);
    });
  }, [ws.figmaLinks, ws.timeline, seedIdx, iid]);
  // What the connect card is decided over: this step's chips, plus every
  // decline the user gave at an earlier step. Not the whole session — the card
  // is for the first chip still waiting, and one hidden at an earlier step
  // would take it from the chip on screen.
  const flowFigmaConnectBasis = useMemo<ReadonlyArray<FigmaLinkSnapshot>>(() => {
    const onScreen = new Set(flowFigmaLinks.map((l) => l.linkId));
    const declines = ws.figmaLinks.filter(
      (l) =>
        !onScreen.has(l.linkId) &&
        (l.state === "declined" || l.declineScope !== undefined),
    );
    return declines.length === 0 ? flowFigmaLinks : [...flowFigmaLinks, ...declines];
  }, [flowFigmaLinks, ws.figmaLinks]);
  // The seed fence can LOSE its anchor after a reconnect: the seed
  // user_message can be ring-cap-evicted from the event log (4000 events), and
  // the readiness card is replaced IN PLACE at its original index — so a warm
  // replay can hold this issue's current card while `seedIdx === -1` pins the
  // fenced derivation at null forever (pre-latch this was a permanent boot
  // screen). The fenceless scan recovers it, but ONLY for cards at least as
  // new as one this mount already legitimately showed — a stale pre-seed card
  // on a fresh mount still can't enter (the fence's whole purpose).
  const fencelessCard = useMemo<ReadinessEntry | null>(() => {
    for (let i = ws.timeline.length - 1; i >= 0; i -= 1) {
      const e = ws.timeline[i];
      if (e && e.kind === "readiness_card" && e.issueIid === iid) return e;
    }
    return null;
  }, [ws.timeline, iid]);
  const shownCardSeqRef = useRef(-1);
  // A RESUMED mount opens the fence outright. It has no seed of its own to
  // anchor on — that is the whole point of the silent resume — and the PRIOR
  // mount's seed echo is only an anchor while two fragile conditions hold: the
  // persisted seed bytes still match (a deep link with no sessionStorage
  // regenerates a different seed for a create-originated flow) and the event
  // survived the 4000-event ring cap. Without this, a silent resume that misses
  // either would derive `null` forever and park the user on the boot screen
  // with a live, all-green card sitting one timeline entry away — a worse bug
  // than the re-seed this change removes. It is sound because the fence guards
  // a FRESH mount that is about to RE-OPEN the flow (its own new seed makes
  // every earlier card stale); a resume is JOINING the existing flow, and the
  // client keeps exactly ONE `readiness_card` entry per iid — upserted in place
  // by issueIid — so "the newest card for this iid" is precisely the state the
  // server last published for the flow we are rejoining.
  const derivedForLatch =
    derivedCard ??
    (fencelessCard !== null &&
    (resumed ||
      (shownCardSeqRef.current !== -1 &&
        entrySeq(fencelessCard.id) >= shownCardSeqRef.current))
      ? fencelessCard
      : null);

  // LATCH the derived card across the reconnect replay window. The attach
  // effect wipes the timeline and replays the full bus on every reconnect
  // (silent 1006 auto-reconnect, idle resume, reauth), so the derivation goes
  // null until the seed has replayed, then walks the card HISTORY oldest-first
  // until the latest re-lands. Without the latch the null window flips the
  // body into the `!card` boot branch AND flips the `card ? "card" : "boot"`
  // stepKey segment — both unmount the AnswerWidget subtree, destroying the
  // half-typed draft, and the user gets a boot-screen flash mid-question. The
  // substitution happens HERE, at the single derivation site, so every
  // consumer (openAsks, the all-green release, the flushedAt suppression,
  // `live`, progress, stepKey) sees one consistent card and the whole tree
  // stays put while the replay converges. Monotonic-by-seq with a stale-latch
  // self-heal — see useLatchedEntry.
  //
  // Mount-scoped key: RefineFlow is keyed by issue (`key={stage.iid}`) and a
  // session switch unmounts it, so the latch can never carry a card across
  // issues, and a card only enters via the fenced derivation (or the
  // shown-seq-bounded fenceless fallback above). A latched acceptToken can be
  // one card stale; the server re-verifies the token hash and single-use on
  // apply, so the worst case is the existing apply-fail + Retry screen, not a
  // wrong write.
  const card = useLatchedEntry(derivedForLatch, `refine:${iid}`);
  const cardId = card?.id ?? null;
  // The card's CURRENT acceptance-criteria count — feeds sizing-staleness so an
  // edit to the criteria (e.g. the piece elicitation below) re-runs the size.
  const currentAcCount = useMemo(() => {
    const v = card?.verdicts.find((x) => x.field === "acceptance_criteria");
    return card?.allGreen && typeof v?.value === "string"
      ? splitAcceptanceCriteria(v.value).length
      : 0;
  }, [card]);
  // Sizing + breakdown tail state (shared derivation with IssuePage). The plan
  // request THIS flow already sends carries the sizing half, so autoKick stays
  // off. Everything here is issue-scoped + latched across the replay wipe.
  const breakdownCard = useBreakdownCard(ws, session.sessionId, iid, {
    autoKickPlan: false,
    currentCriteriaCount: currentAcCount,
  });
  const latchDiagRef = useRef<string | null>(null);
  useEffect(() => {
    if (card !== null)
      shownCardSeqRef.current = Math.max(shownCardSeqRef.current, entrySeq(card.id));
    // Log once per divergence episode (wipe or older-card replay), not per render.
    const divergence =
      card !== null && card.id !== (derivedCard?.id ?? null)
        ? `${card.id}<-${derivedCard?.id ?? "wiped"}`
        : null;
    if (divergence !== null && divergence !== latchDiagRef.current) {
      focusDiag("card_latched", {
        cardId: card?.id ?? null,
        derivedId: derivedCard?.id ?? null,
      });
    }
    latchDiagRef.current = divergence;
  }, [card, derivedCard]);

  const lastAssistantEntry = useMemo(() => {
    for (let i = ws.timeline.length - 1; i >= 0; i -= 1) {
      const e = ws.timeline[i];
      if (e && isPlanEra(e)) continue; // never surface plan-turn prose (file paths)
      if (e && e.kind === "assistant_turn" && e.text.trim() !== "") return e;
    }
    return null;
  }, [ws.timeline, isPlanEra]);
  const lastAssistant = lastAssistantEntry?.text ?? null;

  // NO branch of this surface renders the assistant transcript any more.
  //
  // The recap strip and the `.focus-prose` box are gone rather than filtered,
  // and the reason is the one the `thinking` branch already gives: prose is
  // outranked, never suppressible sentence-by-sentence. There is no reliable
  // way to tell a line about the user's product from a line about Loom's own
  // mechanism — the grind screen showed users "Let me ask via the proper
  // question tool" and a sentence about resubmitting a `dod_criteria` citation.
  // Every screen carries its own copy (a headline, the grounded/total count, the
  // question card), so nothing that meant anything depended on the transcript.
  //
  // `lastAssistant` stays: it feeds `activitySig` (the loader gate) and the
  // boot-deadline high-water. Dropping the RENDER is the only change.

  const assistantCount = useMemo(
    () =>
      ws.timeline.reduce((n, e) => (e.kind === "assistant_turn" ? n + 1 : n), 0),
    [ws.timeline],
  );

  const fatal = useMemo(() => {
    const e = ws.timeline.find((x) => x.kind === "error" && x.terminal);
    // The catalog line for its code — never the wire message.
    if (e && e.kind === "error") return errorText(e, t);
    if (ws.status === "denied") return t("focus.refine.sessionDenied");
    if (ws.status === "expired") return t("focus.refine.sessionExpired");
    return null;
  }, [ws.timeline, ws.status, t]);

  // Latest ready-screen field-edit error, if it's newer than the last card — a
  // card landing after the error supersedes it (the edit did fold in). Scoped to
  // the whole flow (ErrorEvent carries no issueIid), rendered under the field
  // list.
  //
  // "Newer" is by SEQ, not by timeline position. A card is upserted IN PLACE by
  // issueIid (`useWsSession`), so the card that lands after the error keeps the
  // slot of the issue's first card, before the error, and a walk from the end
  // never met it: dev 2a8cd749 #231 showed "The workspace is waking up — your
  // edit is saved…" beside the landed value and an armed Accept (snapshot
  // 16:33:17, the edit landed 16:28:47).
  const editError = useMemo<{ code: string; message: string } | null>(() => {
    const seqOf = (id: string): number => Number(id.slice(id.lastIndexOf("#") + 1));
    let cardSeq = Number.NEGATIVE_INFINITY;
    for (const e of ws.timeline) {
      if (e.kind !== "readiness_card") continue;
      const s = seqOf(e.id);
      if (Number.isFinite(s) && s > cardSeq) cardSeq = s;
    }
    for (let i = ws.timeline.length - 1; i >= 0; i -= 1) {
      const e = ws.timeline[i];
      if (!e) continue;
      if (e.kind === "readiness_card") return null; // superseded by a fresh card
      if (
        e.kind === "error" &&
        e.code !== undefined &&
        EDIT_ERROR_CODES.has(e.code)
      ) {
        const s = seqOf(e.id);
        if (Number.isFinite(s) && s < cardSeq) return null;
        return { code: e.code, message: e.message };
      }
    }
    return null;
  }, [ws.timeline]);

  // Typed pre-card asks that are still open FOR THIS ISSUE, in arrival order.
  //
  // Deliberately INDEPENDENT of `card`: the whole point of the channel is that
  // it works before any card exists. Suppressions are the local ones only —
  // `handledAskIds` (the user acted; the server's `question_resolved` may not
  // have landed yet) — plus the server's own `resolved` fold on the entry.
  const typedAsks = useMemo<ReadonlyArray<Step>>(() => {
    const out: Step[] = [];
    for (const e of ws.timeline) {
      if (e.kind !== "agent_ask") continue;
      if (e.issueIid !== iid) continue;
      if (e.resolved) continue;
      if (handledAskIds.has(e.askId)) continue;
      // A SKETCH ask, told apart by its subject and by nothing else. Handled
      // before the tombstone guard's sibling checks below for one reason: its
      // headline is Loom's own catalog string, so an empty `question` is not a
      // tombstone for it — but an empty one still cannot be a live ask, and the
      // guard above (`resolved` / `handledAskIds`) has already run.
      if (e.subject?.kind === "figma_preview") {
        if (e.question.trim() === "") continue;
        out.push({
          kind: "preview",
          askId: e.askId,
          field: null,
          previewId: e.subject.previewId,
          // Always skippable. A drawing that cannot be built, or a user who
          // does not want to look, must never be a step with no way past it.
          optional: true,
        });
        continue;
      }
      if (e.question.trim() === "") continue; // tombstone (see applyEvent)
      out.push({
        kind: "ask",
        askId: e.askId,
        field: e.field,
        question: e.question,
        ...(e.help !== undefined ? { help: e.help } : {}),
        optional: e.optional === true,
        // Anything the fold didn't normalise to "concern" is a plain question.
        concern: e.askKind === "concern",
        ...(e.draft !== undefined && e.draft.length > 0 ? { draft: e.draft } : {}),
      });
    }
    return out;
  }, [ws.timeline, iid, handledAskIds]);

  // The newest ask this issue has seen, resolved or not. Feeds `epoch` — see
  // below — so it must NOT be filtered by `handledAskIds`/`resolved`: it is a
  // marker of "the agent responded", not of "something is open".
  const latestAskId = useMemo(() => {
    for (let i = ws.timeline.length - 1; i >= 0; i -= 1) {
      const e = ws.timeline[i];
      if (e && e.kind === "agent_ask" && e.issueIid === iid) return e.askId;
    }
    return null;
  }, [ws.timeline, iid]);

  // Agent-response generation: changes on a new card, a new assistant turn, OR
  // a new typed ask.
  //
  // The ask term is load-bearing, not symmetry. `pending`/`thinking` clears
  // ONLY when the epoch advances, and the new prompt tells the agent to answer
  // a flush with an ask ALONE — no card, no prose. Without the ask term that
  // reply moves neither of the other two terms, so `thinking` stays true
  // forever, the thinking branch wins the ladder, and the question the agent is
  // blocked on is never shown: a hang with no recovery short of a reload.
  const epoch = `${cardId ?? "none"}:${assistantCount}:${latestAskId ?? "none"}`;
  const thinking = pending !== null && pending === epoch;

  // LOOM'S ANSWER, CLOSED. A red (or unavailable) value is Loom's own wording,
  // and it is offered as Loom's answer only once SETTLED: the agent is idle and
  // no answer of ours is still in flight. Offered earlier, a refusal the model is
  // about to repair in its own turn would be put to the user as a question — a
  // gate refusal is not a question. Latched per (field, value), so it does not
  // vanish from under the user the moment the agent's next turn starts.
  //
  // Idle is a fact only on an OPEN socket whose busy state the server has told
  // it (`busyKnown`): `agentBusy` reads false on a first load and is carried
  // across a reconnect, so "connecting + not busy" offered a red that the latch
  // then held through the busy turn that followed. And a user turn on the
  // timeline newer than the card's stamp BLOCKS the offer and releases the
  // latch: the card that proposed the red has not read it, and a reload keeps
  // that turn (it is on the bus) where it drops `pending`.
  const redBlocked = useMemo(
    () => unreadUserTurns(ws.timeline, card, iid).length > 0,
    [ws.timeline, card, iid],
  );
  const settledNow =
    ws.status === "open" && ws.busyKnown && !ws.agentBusy && pending === null && !redBlocked;
  const redOfferLatch = useRedOfferLatch();
  const loomAnswerFor = (
    field: string,
    input: {
      readonly verdict?: "green" | "red" | "needs_input" | "unavailable";
      readonly value?: string;
      readonly draft?: ReadonlyArray<string>;
      readonly concern?: boolean;
    },
  ): LoomAnswer | null =>
    loomAnswerOf(field, input, {
      settled: redOfferLatch(field, input.verdict, input.value, settledNow, redBlocked),
    });
  // The bytes a click on "Use this text" sent, by step key — so `undoLast` can
  // put the step back CLOSED rather than as a box full of Loom's words. Never on
  // the wire: the server cannot tell a click from typing, and is not told.
  const loomTakenRef = useRef<Map<string, string>>(new Map());
  // Whether the widget on the pinned step is showing Loom's answer closed right
  // now (no box open). Reported by the widget itself: only it knows whether the
  // user chose "Write my own". Keyed by the step, so a stale report from the
  // previous step can never fold the composer of the next.
  const [closedReport, setClosedReport] = useState<{
    readonly key: string;
    readonly closed: boolean;
  } | null>(null);
  // The message bar's text and caret, owned HERE rather than by the bar: the
  // bar folds to a note link while Loom's answer is closed, and the full bar and
  // the note bar are different components, so a bar that owned its text lost it
  // the moment a card re-emit brought a draft for the pinned field. With the
  // text here, and the fold held still while the bar has text or the caret
  // (`barFoldedRef`), nothing the user typed can vanish under them.
  const [barText, setBarText] = useState("");
  const [barFocused, setBarFocused] = useState(false);
  const barFoldedRef = useRef<boolean | null>(null);

  // Everything the chat currently wants the user on, in story order — derived
  // fresh from the LATEST card. This drives the QUEUE; it does NOT directly
  // drive the screen (the pin does). A field the user already answered (in
  // `answers`) is suppressed: it's spoken for and waiting to be sent.
  // The server's record of what the user skipped, UNIONED with this mount's own.
  //
  // DERIVED, not an effect writing back into `declined`: an effect lands one
  // commit late, and the step-reconciliation effect pins `current` in that same
  // first commit — so the skipped question was already on screen by the time the
  // mask arrived, and a pin is sticky. `declined` alone is mount-local (a reset
  // clears it) and used to die on every remount; the projection is the durable
  // half, and a union is what lets a skip sent in THIS mount survive a card that
  // predates it.
  //
  // OPTIONAL fields only — the same narrowing the mount-local seed above,
  // `declineAsk` and the Skip button itself apply, all four folded against the
  // DEFAULT spec. The server writes the flag against the EFFECTIVE one
  // (`recordDeclinedFields` refuses a required field by name, resolved from
  // `.loom.yml`), and `projectCardOutput` re-stamps a stored row with no filter
  // of its own, so the two ends are only as aligned as those two specs are.
  //
  // Both skew directions land here, and they are not symmetric:
  //  · a row stamped when the DEFAULT spec called the field optional, replayed
  //    after a Loom release made it gating. That is the one this filter
  //    rescues: unfiltered, the row masks a gating field's question, it never
  //    reaches the screen, `allGreen` never lands and nothing says why.
  //  · a `.loom.yml readiness_spec` that widens the optional set beyond the
  //    default. There the server accepts and persists a skip this filter then
  //    discards, so the question re-pins on every card — and with no Skip
  //    button, since that button folds against the same default spec. Worse
  //    UX, but it fails toward ASKING, which is the direction to fail in; the
  //    honest fix is a spec the client can see, not a looser filter here.
  const cardDeclined = (card?.verdicts ?? [])
    .filter((v) => v.declined === true && isOptionalField(v.field))
    .map((v) => v.field)
    .join(",");
  // …minus what the user took back. A skip was the one act with no way out: the
  // undo bar is offered after an answer and not after a skip, and there is no
  // second chance anywhere — a declined field never reaches the ready screen
  // (only greens do), so "skip" was quietly irreversible. `unDeclined` is that
  // way out, applied LAST so it wins over both halves of the union: the server
  // keeps its own `declinedFields` record (there is no un-decline frame, and
  // that record's only job is to stop the AGENT re-asking — it never blocks a
  // value the user volunteers), and re-skipping clears the override so the two
  // can't fight. BOTH skip doors clear it, via `markFieldDeclined` — a door
  // that forgot would be silently out-voted here.
  //
  // It is persisted alongside the held batch for the same reason it is applied
  // last: what it out-votes is durable. An override that only lived as long as
  // the mount let the undo hold until the next reload and then vanish under the
  // re-asserted card flag — the undo bar promising something the next mount
  // quietly took back.
  const declinedAll = useMemo<ReadonlySet<string>>(() => {
    const next = new Set(declined);
    if (cardDeclined !== "") {
      for (const f of cardDeclined.split(",")) next.add(f);
    }
    for (const f of unDeclined) next.delete(f);
    return next;
  }, [declined, cardDeclined, unDeclined]);

  // The DURABLE half of `provided`, on exactly the terms `declinedAll` is the
  // durable half of `declined`.
  //
  // `provided` is mount-local: it is written only by this mount's own user
  // actions and is never seeded from the card, so every reload, every remount
  // and every route re-entry empties it while its sibling latch survives on the
  // wire. Within one mount that is invisible — by the time a card is all-green
  // every surfaced green is in `provided`. It becomes visible the moment
  // something makes `allGreen` false again on a card the user already finished:
  // the pin effect stops returning early, `openAsks` re-offers every green as a
  // "Loom set this — keep it?" review, and the user walks their own answers a
  // second time. Live: a park + un-park of ONE acceptance criterion re-posed
  // role, capability, outcome, acceptance criteria and milestone in sequence.
  //
  // A green whose `resolvedSource` is "user" IS the user's own bytes — that is
  // what the F1 grounding invariant means, and the review screen's own kicker
  // already says so ("from what you told Loom"). So it needs no review, in this
  // mount or any later one. A "code" green is agent-DECIDED and the review is
  // the only place the user ever sees it: never sweep those in. A "meeting"
  // green (a verified quote from the meeting that filed the issue)
  // is someone's words in a meeting, not this user's in this session: it is
  // reviewed like "code", never swept in.
  //
  // DERIVED, not an effect writing back into `provided`, for the reason spelled
  // out above `declinedAll`: an effect lands one commit late and the pin is
  // sticky, so the review would already be on screen when the mask arrived.
  //
  // A "meeting" green that is an ANSWER given on the meeting page or its link
  // (`meetingAnswer`, refine light) counts as given (owner decision
  // 2026-10-13): no "keep it?" step for it either. Its provenance shows on the
  // ready preview instead, where it stays editable.
  const cardUserGreens = (card?.verdicts ?? [])
    .filter(
      (v) =>
        v.verdict === "green" &&
        (v.resolvedSource === "user" ||
          (v.resolvedSource === "meeting" && v.meetingAnswer !== undefined)),
    )
    .map((v) => v.field)
    .join(",");
  const providedAll = useMemo<ReadonlySet<string>>(() => {
    if (cardUserGreens === "") return provided;
    const next = new Set(provided);
    for (const f of cardUserGreens.split(",")) next.add(f);
    return next;
  }, [provided, cardUserGreens]);

  // The fields the CARD says are green FROM THE USER'S OWN WORDS. A settled
  // field, by the grounding invariant: the value on it is the user's bytes and
  // the server's green is monotonic, so nothing about it is still open.
  //
  // Nothing may put one of these back in front of the user as a QUESTION. Live
  // (#1081): skip milestone → flush → the WHO question, already answered and
  // green from `source: "user"`, was re-pinned with the same draft, and only
  // released when the next card landed — the user watched a question they had
  // answered come back and then vanish on its own. The card channel can produce
  // that whenever a model re-asks a field it has already grounded (`agentAsked`
  // deliberately bypasses the `provided` suppression, and `provided` is empty
  // after a remount anyway); the typed channel can produce it whenever an ask
  // outlives the green that answered it.
  //
  // Applied in `openAsks` rather than at the pin, and that is load-bearing: the
  // batch flush is gated on `openAsks.length === 0`, so a step filtered only at
  // the pin would sit in the queue forever and strand every held answer.
  //
  // A CONCERN is exempt, exactly as it is exempt from the server's own re-ask
  // refusal: it is ABOUT the settled value ("these two criteria can't both
  // hold"), not a request for it again.
  const userGreenFields = useMemo<ReadonlySet<string>>(
    () => new Set(cardUserGreens === "" ? [] : cardUserGreens.split(",")),
    [cardUserGreens],
  );
  const settledByUserGreen = useCallback(
    (step: Step): boolean => {
      if (step.kind === "ask" && step.concern) return false;
      return step.field !== null && userGreenFields.has(step.field);
    },
    [userGreenFields],
  );

  const openAsks = useMemo<ReadonlyArray<Step>>(() => {
    // Typed asks come FIRST and own their step key: the agent asked them
    // explicitly and is blocked on them, whereas a card verdict is a standing
    // description of the field. A card step whose key an open ask already holds
    // is dropped, so the two channels can never put the same question up twice.
    //
    // Note they are NOT run through `policyFor`. That policy encodes "don't put
    // an agent-DECIDED value in front of the user" — but a typed ask IS the
    // agent deciding it needs the user. Filtering asks through it would let a
    // "derive" field (dod_criteria) be silently swallowed, leaving the agent
    // waiting on an answer no screen ever asks for.
    // A typed ask for a field the user already answered (held, awaiting flush)
    // must be suppressed exactly like the card-verdict loop below suppresses
    // its own re-ask of that field (`answers.has(field)`, a few lines down) —
    // otherwise a `question_pending` ask that arrived while a CARD question for
    // the same field was already on screen queues independently, and once the
    // card question is answered, reconciliation pulls the still-queued ask next:
    // the user is asked the identical thing twice through the two channels this
    // whole step-key-collapsing scheme exists to prevent. Live-reproduced: an
    // ask for `role` surfaced right after its card-driven question was answered.
    //
    // A SKIPPED field is settled. The card loop below hard-suppresses it with no
    // needs_input bypass — "they declined it, don't keep re-asking" — and the
    // typed channel filtered on the held answer alone, so an ask for an optional
    // the user had explicitly retired walked straight onto the screen. Same
    // suppression, same reason.
    //
    // The half of `declinedAll` that comes off the card is DURABLE, so this
    // suppression now outlives the mount, and that is a real cost as well as the
    // point: an ask the server's own skip sweep missed (it reads the in-memory
    // `pendingAsks` mirror, which an instance that never seeded it does not
    // have) used to come back on the next reload and could be cleared with
    // Skip. It stays hidden now. Bounded on purpose rather than accepted
    // blindly: only OPTIONAL fields can be declined at all, so no such ask can
    // gate Accept, undo takes the field back out (and survives the same
    // remount now), and a change of mind volunteered in the composer still
    // grounds and greens the field — which publishes a card, which retires the
    // ask. What is lost is the Skip button on a question the user already
    // skipped, which is not a door worth the re-ask it costs.
    //
    // A CONCERN is exempt, on the same terms the server's guard exempts it: a
    // concern is ABOUT the decision, not a re-ask of it.
    //
    // NOT filtered on `provided` or on the card's own green. Both would
    // duplicate `executeAskUser`'s server-side guard (which is the authority and
    // refuses exactly those), and both would break the declared widening — an
    // agent-authored ask on a settled field is the agent asking for a
    // re-confirmation, which is a thing it is allowed to do.
    //
    // It IS filtered on the card's user-sourced green (`settledByUserGreen`),
    // which is a narrower fact than either: not "the user acted on this in this
    // mount" but "the card's own verdict says this field holds the user's words
    // and is verified". A re-confirmation request over that is the double-ask
    // this whole scheme exists to prevent, the server refuses it anyway, and a
    // concern — the one shape that legitimately revisits a settled field — is
    // exempt.
    const liveTypedAsks = typedAsks
      .filter((a) => {
        if (answers.has(stepKeyOf(a))) return false;
        if (settledByUserGreen(a)) return false;
        // A sketch is bound to no field, so every field-level suppression below
        // is meaningless for it: it can only be retired by the user acting or
        // by the server resolving the ask.
        if (a.kind === "preview") return true;
        if (a.field === null) return true; // scoping — the server's text dedupe owns it
        if (a.kind === "ask" && a.concern) return true;
        return !declinedAll.has(a.field);
      })
      // Stable, three-rank sort. A CONCERN outranks everything — it is about a
      // decision already taken and the user may be acting on a wrong value
      // right now. A SKETCH comes next: confirming which screen Loom read is a
      // precondition for every question downstream of the design, and it is the
      // cheapest question on the queue (one look, two buttons). Field asks keep
      // their arrival order behind both.
      .map((a, i) => ({
        a,
        i,
        rank: a.kind === "ask" && a.concern ? 0 : a.kind === "preview" ? 1 : 2,
      }))
      .sort((l, r) => (l.rank === r.rank ? l.i - r.i : l.rank - r.rank))
      .map((x) => x.a);
    if (!card) return liveTypedAsks;
    const owned = new Set(typedAsks.map(stepKeyOf));
    const asks: Step[] = [...liveTypedAsks];
    for (const field of FIELD_ORDER) {
      const policy = policyFor(field);
      if (policy === "skip") continue;
      // An open typed ask already owns this field — don't ask it twice.
      if (owned.has(field)) continue;
      // User explicitly skipped this optional field — hard-suppress it (no
      // needs_input bypass): they declined it, don't keep re-asking.
      if (declinedAll.has(field)) continue;
      const v = card.verdicts.find((x) => x.field === field);
      if (!v) continue;
      if (answers.has(field)) continue; // answered, awaiting flush
      // Green from the user's own words on THIS card → settled; never a
      // question. Sits above the verdict branches on purpose: the `agentAsked`
      // clause below deliberately bypasses the `provided` suppression, so
      // without this a model that re-asks a field it already grounded re-pins
      // it (and `provided` is empty after a remount, so this is not a rare
      // shape). The review branch has its own, older suppression
      // (`providedAll`) — this one is about the QUESTION.
      if (userGreenFields.has(field)) continue;
      // Did the card that produced this verdict READ the user's answer? The
      // just-flushed suppression already computes it (`stamp >= ackSeq`) to
      // decide whether a `needs_input` is a genuine re-ask; the same fact tells a
      // SETTLED failure apart from the transient drift `provided` exists to hide.
      let sawAnswer = false;
      // Just-flushed suppression (see `flushedAt` for the full design):
      const flushOf = flushedAt.get(field);
      if (flushOf !== undefined) {
        const ackSeq =
          flushOf.clientMsgId !== null
            ? (ws.ackedIds.get(flushOf.clientMsgId) ?? null)
            : null;
        const stamp = card.groundedThroughSeq;
        if (ackSeq !== null && stamp !== undefined) {
          // Seq-keyed: the answer is ON THE BUS at `ackSeq` (server-acked).
          // A card whose grounding scan only saw the log through `stamp` <
          // ackSeq predates it — suppress, even across new cardIds and even
          // with the agent idle (the pre-answer grind emits 2-3 cards per
          // turn, each minting a new id; under the legacy clause every one
          // of them could lift the latch). A card with stamp >= ackSeq saw
          // the answer: needs_input from it is a GENUINE re-ask — let it
          // through (never strand the user behind a stale latch).
          if (stamp < ackSeq) continue;
          sawAnswer = true;
        } else if (
          // Legacy clause for an unstamped card or a not-yet-acked flush:
          // while the agent is STILL grinding (busy) and THIS is the same
          // card we flushed against, the answer is in flight and this stale
          // card still shows it needs_input — don't re-ask it mid-grind.
          // Once the agent goes IDLE without grounding (a genuine prose
          // re-ask / dead-end) this lifts and the question re-surfaces; a
          // newer card lifts it too. The off-OPEN clause holds the
          // suppression through a reconnect window: busy-ness is unknowable
          // until the server's agent_turn attach snapshot lands — lapsing
          // here would re-pin the question the user just answered. A genuine
          // idle re-ask always arrives on an OPEN socket, so never-strand is
          // unaffected.
          (ws.agentBusy || ws.status !== "open") &&
          flushOf.cardId === cardId
        ) {
          continue;
        }
      }
      if (v.verdict !== "green") {
        // A field the user already answered that comes back red/unavailable is
        // the agent's grounding DRIFT across the Stop-hook grind (it re-cited the
        // user's answer imperfectly), not a missing answer — don't re-ask it.
        // The server's monotonic user-green latch settles it back to green; this
        // just keeps the transient red from re-surfacing as a question. A genuine
        // `needs_input` still surfaces — that's the agent explicitly asking.
        //
        // ONE exception, and it is the whole of O3: the card that produced this
        // verdict READ the user's answer (`sawAnswer`) and still could not ground
        // it, and the agent has gone idle. That is SETTLED, not transient — the
        // drift case this suppression exists for is a card that PREDATES the
        // answer. Left hidden it reads as "6 of 7 verified" with nothing on
        // screen to act on and no channel out; surfaced it is a five-second fix.
        // `!ws.agentBusy` keeps it off screen mid-grind, where a red really is
        // still in motion.
        //
        // A `needs_input` the SERVER wrote is not the agent asking — it is the
        // gate refusing the value the model proposed, and it belongs in the same
        // bucket as the red above. The two were indistinguishable until
        // `gateRefusal` shipped, and that is the whole of Observation 1: the
        // user answered `role`, the model quoted their sentence, `isRoleShaped`
        // refused it, and the gate's own English repair instruction ("Name them
        // in a few words — a person, a team or a system, not a sentence.") was
        // posed to them as a question 38 seconds after they answered, under the
        // same kicker as the first ask. They never answered it; 27 seconds later
        // the model narrowed its own quote, the field greened and the question
        // vanished under their cursor. Nothing was lost — the refusal was
        // published. A refused proposal is the model's to repair.
        const agentAsked = v.verdict === "needs_input" && v.gateRefusal !== true;
        const settledUnverified =
          provided.has(field) && sawAnswer && !ws.agentBusy && !agentAsked;
        if (provided.has(field) && !settledUnverified && !agentAsked) continue;
        // An open question (needs_input) OR a value Loom tried but couldn't
        // verify (red/unavailable — re-offer the control so a failed grounding
        // can't strand the user). This is also the "derive" safety valve: an
        // agent-owned field (dod_criteria) that failed to code-ground re-surfaces
        // as a question rather than dead-ending the gate (DoD is required green).
        // Agent-authored ask — may carry Markdown markers, but it renders in
        // the <h1> headline where Markdown can't be formatted. Gate the
        // fallback on the STRIPPED text: a reason that was only syntax (a bare
        // fence, a lone ">") must fall back, not render an empty headline.
        //
        // Gated on `agentAsked`, not on the verdict: a gate refusal's `reason`
        // is server prose written for the model, so it falls back to the field's
        // OWN localized question. That fallback is what makes the "nothing may
        // strand the gate" rule survivable — a refusal on a field the user never
        // answered still surfaces, it just asks in the user's language instead
        // of quoting the gate at them.
        const strippedReason = agentAsked ? stripMarkdownInline(v.reason) : "";
        // NEVER `v.reason` on the settled-unverified path: that string is
        // `groundingDenyReason` or a reconstructor's shape sentence, both
        // written for the agent. Honest, user-facing copy instead — and two
        // different sentences, because the two failures are different. A red is
        // "Loom couldn't match its version to your words"; a shape refusal
        // matched the words fine and could not USE them, so the copy has to open
        // by acknowledging the answer or it reads as the first ask again.
        //
        // `gateRefusal` alone is NOT the shape discriminator: it is stamped on
        // every non-green resolved from a proposed slot, and that bucket holds
        // both a shape refusal (`needs_input` — the words matched, the form was
        // wrong) and a grounding MISS (`red`/`unavailable` — nothing matched).
        // The shape sentence opens by accepting the answer, which is a lie for a
        // miss, and the miss sentence says Loom couldn't match the words, which
        // is a lie for a refusal. The verdict tells them apart, so use it.
        const question = settledUnverified
          ? v.gateRefusal === true && v.verdict === "needs_input"
            ? t("focus.refine.shape.question", { label: fieldCopy(field).label })
            : t("focus.refine.unverified.question", { label: fieldCopy(field).label })
          : strippedReason !== ""
            ? strippedReason
            : fieldCopy(field).question;
        asks.push({
          kind: "question",
          field,
          question,
          ...(settledUnverified ? { unverified: true } : {}),
        });
        continue;
      }
      // Green. "derive" fields are agent-OWNED: the user never reviews a value
      // Loom decided for them (as "skip" hides story_points) — only the non-green
      // safety valve above surfaces them. Keeps the technical quality bar off the
      // business owner's plate while leaving an escape hatch if grounding fails.
      if (policy === "derive") continue;
      if (typeof v.value === "string" && v.value.trim() !== "") {
        // User answered or kept this field → don't re-review their own value.
        // `providedAll`, not `provided`: a user-sourced green is the user's own
        // bytes whether or not THIS mount watched them type it, and the
        // mount-local set is what re-walked five finished answers after a
        // remount (see `providedAll`).
        //
        // NO EXCEPTION for `revised` — the model rewriting a user green is real
        // and worth telling the user about, but a QUEUE STEP is the one shape
        // that cannot carry it. `revised` is server state; the only client act
        // that clears it is one that puts new bytes on the wire, and "Keep it"
        // is deliberately send-free. So a review pushed on `revised` survives
        // its own Keep, `openAsks` re-pushes it in the same commit, the pin
        // effect re-pins it, and the step is unkillable — while a non-empty
        // `openAsks` also blocks the held-answer flush, so the agent is never
        // woken either. The mirror failure is just as bad: on an all-green card
        // the pin effect releases every non-"ask" step, so the review never
        // renders at all — and an all-green card is exactly when the model is
        // most likely to have narrowed a quote to complete the gate.
        //
        // The notice lives on the READY PREVIEW instead (`revisedNote`), which
        // is the screen every applied value passes through: it shows the
        // rewritten value in full, next to an Edit control, and being a note
        // rather than a step it can neither loop nor block Accept.
        if (providedAll.has(field)) continue;
        asks.push({ kind: "review", field, value: v.value, source: v.resolvedSource });
      }
    }
    return asks;
  }, [card, cardId, typedAsks, answers, provided, providedAll, declinedAll, userGreenFields, settledByUserGreen, flushedAt, ws.agentBusy, ws.status, ws.ackedIds, fieldCopy, t]);

  // The steps queued BEHIND what's on screen (display + count only). Keyed by
  // `stepKeyOf`, not `field`: two field-less asks both carry `field === null`,
  // so a field comparison would collapse them and hide the second one.
  const currentKey = current !== null ? stepKeyOf(current) : null;
  const queue = useMemo(
    () => openAsks.filter((a) => stepKeyOf(a) !== currentKey),
    [openAsks, currentKey],
  );

  // Pin reconciliation: when nothing is on screen and the agent isn't mid-reply
  // to a flush, pull the next item from the queue. Never replaces a non-null
  // `current` from background churn — that's the whole point: cards can't switch
  // the question. `current` goes null via a user action below, OR here when the
  // card is all-green (terminal): an all-green card has nothing to ask, so a
  // lingering pin can only strand the user short of Accept. This is the recovery
  // path for a reconnect replay that briefly regresses the card to an older,
  // non-green snapshot and pins a now-resolved question before converging back.
  //
  // `flowTerminal` joins `accepted` here for the reload case: a replayed
  // `agent_ask` from before the apply would otherwise pin a question on a
  // finished flow, and answering it sends nothing the server still wants.
  useEffect(() => {
    if (accepted || flowTerminal || fatal !== null || thinking) return;
    // A pinned ask the SERVER resolved (`question_resolved` — it carded the
    // question, superseded it, or another surface answered) is no longer live.
    // Release it: nothing is waiting on the answer, so leaving it up asks the
    // user to do work that will be thrown away. This is the ONE release path an
    // ask has besides the user acting — every card-driven release below exempts
    // asks, so without it a resolved ask would stay pinned forever.
    if (current !== null && (current.kind === "ask" || current.kind === "preview")) {
      const askId = current.askId;
      // A sketch rides the same channel, so it has the same one release path —
      // and needs it for the same reason: a `question_resolved` the server sent
      // (the flow moved on, another surface answered) must take the picture off
      // the screen, or it goes on asking for work nothing is waiting for.
      if (
        !typedAsks.some(
          (a) => (a.kind === "ask" || a.kind === "preview") && a.askId === askId,
        )
      ) {
        focusDiag("pin_released", { field: stepKeyOf(current), reason: "ask_resolved" });
        setCurrent(null);
        return;
      }
    }
    if (card?.allGreen) {
      // A TYPED ASK survives an all-green card. The card describes the issue's
      // fields; an ask the agent raised (e.g. "which of these two flows did you
      // mean?") is orthogonal to it, so "every field is green" is no evidence
      // that the question was answered. Releasing it here would drop the agent's
      // question on the floor and strand it waiting.
      // A typed ask AND a sketch both survive an all-green card, for the same
      // reason: the card describes the issue's fields, and neither "which flow
      // did you mean?" nor "is this the screen you meant?" is one of them, so
      // "every field is green" is no evidence that either was answered.
      if (
        current !== null &&
        current.kind !== "ask" &&
        current.kind !== "preview"
      ) {
        focusDiag("pin_released", { field: current.field, reason: "all_green" });
        setCurrent(null);
        return;
      }
      if (current !== null) return;
      // Terminal card, nothing to review — but an open ask still has to be put
      // in front of the user (the ask branch sits above the ready screen).
      // Except one bound to a field the card already greens from the USER's own
      // words: that is not a question, it is the same question again (see
      // `settledByUserGreen`). This branch reads `typedAsks` directly rather
      // than `openAsks`, so it needs its own copy of the guard.
      const nextAsk = typedAsks.find((a) => !settledByUserGreen(a)) ?? null;
      if (nextAsk !== null) {
        focusDiag("current_set", { field: stepKeyOf(nextAsk), kind: "ask" });
        setCurrent(nextAsk);
      }
      return;
    }
    // Release a pinned QUESTION the USER ANSWERED once the latched card greens
    // that field — the answer grounded, so keeping them on the now-settled
    // question only strands them short of the next ask (the double-ask tail:
    // a field that re-pinned during a delivery gap and is now resolved). The
    // pin previously released only on allGreen (d8b241b — deliberately narrow
    // against churn). Gated on `provided` so it CANNOT fire on a code-green
    // during agent grind (the question-switching bug these repro tests guard):
    // a field the user never engaged with stays pinned even when code grounds
    // it. Keys off the monotonic latched card so a mid-replay older snapshot
    // can't fire it; review steps are exempt (green is their precondition).
    //
    // `provided`, NOT `providedAll`, and the difference is the whole guard. The
    // precondition is not "the value is the user's" — it is "the user has
    // already DEALT WITH this pin", which only a mount-local act can witness.
    // `providedAll` unions in every user-sourced green on the card, including
    // one the model grounded against an EARLIER turn with no new user answer at
    // all (`findQuoteTurn` scans every turn and takes the first match, so the
    // intake seed alone can green a field mid-grind). Releasing on that fires
    // against a question the user has open and is typing into: the screen
    // changes under the cursor and the half-typed draft is orphaned — the
    // vanishing-question symptom, on a new path. `providedAll` belongs to the
    // review suppression in `openAsks` and nowhere else.
    if (
      current !== null &&
      current.kind === "question" &&
      provided.has(current.field)
    ) {
      const v = card?.verdicts.find((x) => x.field === current.field);
      if (v?.verdict === "green") {
        focusDiag("pin_released", { field: current.field, reason: "field_green" });
        setCurrent(null);
        return;
      }
      // …and one whose answer is ON THE BUS and not yet read: the server acked
      // the flush that carried it, and the card on screen was stamped before
      // that seq, so its `needs_input` is the question the user already
      // answered, not a new one. The queue already drops the field on exactly
      // this proof (`flushedAt`, seq-keyed); a pin taken a moment earlier —
      // before the ack, off a card that replayed ahead of it — kept asking it,
      // one click from sending the answer twice (live, #255, 2026-09-25: the
      // answered User step came back after a reload and stayed ~70 s). Keyed on
      // the ACK, so a genuine re-ask — a card that has read the answer — keeps
      // its pin, and a box the user is typing a second answer into stays put.
      if (!answers.has(current.field)) {
        const flushOf = flushedAt.get(current.field);
        const ackSeq =
          flushOf !== undefined && flushOf.clientMsgId !== null
            ? (ws.ackedIds.get(flushOf.clientMsgId) ?? null)
            : null;
        const stamp = card?.groundedThroughSeq;
        if (ackSeq !== null && stamp !== undefined && stamp < ackSeq) {
          focusDiag("pin_released", { field: current.field, reason: "answer_in_flight" });
          setCurrent(null);
          return;
        }
      }
    }
    if (current !== null) return;
    const next = openAsks[0] ?? null;
    if (next !== null) {
      focusDiag("current_set", { field: stepKeyOf(next), kind: next.kind });
      setCurrent(next);
    }
  }, [
    openAsks,
    typedAsks,
    current,
    thinking,
    accepted,
    flowTerminal,
    fatal,
    card,
    provided,
    settledByUserGreen,
    answers,
    flushedAt,
    ws.ackedIds,
  ]);

  // Clear the "awaiting a flush response" marker once the agent has answered.
  // `pending` is set to the epoch we flush/send into; `thinking` stays true only
  // while `pending === epoch`. But `pending` was never reset, so after the FIRST
  // flush or message-bar send it stayed a non-null stale epoch forever — and the
  // auto-flush gate below requires `pending === null`, so every later batch of
  // held answers stranded (the user answers everything and nothing is sent).
  // Once the epoch advances past the flushed one, the response has arrived →
  // clear it so the next batch can flush.
  useEffect(() => {
    if (pending !== null && pending !== epoch) setPending(null);
  }, [pending, epoch]);

  // Refs so the flush callback reads the latest held answers + epoch without
  // re-creating itself (and without racing a stale closure).
  const answersRef = useRef(answers);
  answersRef.current = answers;
  const askAnswersRef = useRef(askAnswers);
  askAnswersRef.current = askAnswers;
  const pendingDeclinesRef = useRef(pendingDeclines);
  pendingDeclinesRef.current = pendingDeclines;
  const epochRef = useRef(epoch);
  epochRef.current = epoch;
  const cardIdRef = useRef(cardId);
  cardIdRef.current = cardId;
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flushNow = useCallback(() => {
    if (flushTimerRef.current !== null) {
      clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }
    const held = answersRef.current;
    const heldAskAnswers = askAnswersRef.current;
    const heldDeclines = pendingDeclinesRef.current;
    // A decline carries no text but still has to go out — it is how the agent
    // learns to stop waiting on an optional ask.
    if (held.size === 0 && heldDeclines.size === 0) return;
    const sent = composeBatch(held);
    focusDiag("flush", {
      fields: [...held.keys()],
      count: held.size,
      combined: held.size > 1,
      ...(heldAskAnswers.size > 0 ? { asks: heldAskAnswers.size } : {}),
      ...(heldDeclines.size > 0 ? { declined: [...heldDeclines] } : {}),
      // The EXACT turn text we send (truncated) — so a non-grounding answer can
      // be told apart from a stale-snapshot race in the diag/logs.
      sent: sent.slice(0, 160),
    });
    // Latch the flushed fields against the current card so the stale pre-flush
    // card can't re-ask them while the answer is in flight (see `flushedAt`).
    // The send returns the delivery id; once the server's ack maps it to the
    // echo's bus seq (ws.ackedIds) the suppression upgrades from the same-card
    // clause to the seq-keyed one. Scoped to this issue so a redelivery after
    // an issue switch is dropped instead of grounding the wrong card.
    setPending(epochRef.current);
    setAnswers(new Map());
    setAskAnswers(new Map());
    setPendingDeclines(new Set());
    setDeclinedAskMeta(new Map());
    // The answers just went out — the undo window is closed for all of them.
    setLastAnswered(null);
    // An answer batch is not a note: the waiting screen names it as an answer.
    setLastNote(null);
    // A batch that settles a typed ask rides the structured frame so the answer
    // is ATTRIBUTABLE — which ask each fragment closes — instead of arriving as
    // anonymous prose the server has to guess at. `text` is composed identically
    // either way, so the grounding scan sees the same bytes on both paths.
    //
    // But `touchesAsk` is per-BATCH, and the server composes the prompt it
    // routes to the sandbox from the attributed arrays — NOT from `text`. So a
    // card-slot answer batched alongside an ask used to be echoed (groundable,
    // seq advanced, every instrument green) yet never reach the model, which
    // then re-asked it. Measured live on dev #59: five answers in one turn, two
    // of them asks, and exactly the two asks came back proposed — three round
    // trips for one issue. Send the card answers on the same frame, attributed
    // the same way. `sent` is untouched, so the gate still quotes identical bytes.
    //
    // The gate is now `held`, not `heldAskAnswers`, and that closes the half of
    // the same hole the fix left open: the MAJORITY refine flush answers only
    // card questions, touches no ask, and took the raw `ws.send` leg — so the
    // model received N anonymous adjacent paragraphs (`composeBatch` joins in
    // FIELD_ORDER with no labels) and none of the attributed lane's work. No
    // per-field attribution, no "you now have what you need for X" list, no
    // "quote the user's words EXACTLY, byte for byte" paragraph, and no
    // `answer.dropped_fragments` tripwire. That does not LOSE an answer — the
    // echo carries every byte and the gate can still ground them — but it is why
    // a mis-chosen span is easy to produce: `role` and `ends` both use the plain
    // text widget, so they arrive as two unlabelled adjacent paragraphs and
    // over-grabbing across the boundary is the natural mistake. It is the shape
    // that produced the refused sentence-role.
    //
    // No deploy-skew risk in the widening: the frame is byte-identical to the
    // one the ask path already sends, and the server's schema deliberately
    // dropped `.min(1)` on `answers` (see `ws/serialize.ts`) — an ask-less
    // `question_answer` has been accepted since before this change.
    const structured =
      held.size > 0 || heldAskAnswers.size > 0 || heldDeclines.size > 0;
    // WIRE CAP and attribution: see `questionAnswerParts` — the one builder the
    // issue page's held batch uses too, so the two surfaces send the same frame
    // for the same batch.
    const parts = questionAnswerParts(held, heldAskAnswers);
    if (parts.capped !== null) focusDiag("attribution_capped", parts.capped, true);
    const clientMsgId = structured
      ? ws.sendQuestionAnswer(iid, sent, parts.answers, [...heldDeclines], {
          cardAnswers: parts.cardAnswers,
          answeredCount: held.size,
          scope: sendScope,
        })
      : ws.send(sent, { scope: sendScope, flow: { issueIid: iid } });
    // MERGE, never replace: a field answered in an earlier batch keeps its own
    // proof-of-answer. Replacing the whole record is what let one later flush
    // erase `sawAnswer` for every earlier field and strand a gate-refused one
    // with no question on screen for the rest of the mount.
    const entry = {
      cardId: cardIdRef.current,
      clientMsgId: clientMsgId === "" ? null : clientMsgId,
    };
    setFlushedAt((prev) => {
      const next = new Map(prev);
      for (const field of held.keys()) next.set(field, entry);
      return next;
    });
  }, [ws.send, ws.sendQuestionAnswer, sendScope, iid]);

  // Flush: the user has addressed everything currently asked (queue drained) and
  // we hold answers → send them as ONE turn. `openAsks` already excludes
  // answered fields, so an empty `openAsks` with non-empty `answers` means
  // "caught up". We hold the batch until the agent is IDLE so the send doesn't
  // SIGTERM-kill a turn that's still grinding (which would restart it on partial
  // context) — with a fallback timer so a missed idle signal can't strand the
  // answers. When the agent stopped to ask (the Stop hook lets it), it's already
  // idle and this fires immediately. Single answer → verbatim; many → combined.
  useEffect(() => {
    const drainedWithHeld =
      !accepted &&
      !flowTerminal &&
      fatal === null &&
      pending === null && // not already awaiting a prior flush
      current === null && // nothing on screen
      (answers.size > 0 || pendingDeclines.size > 0) && // something to send
      openAsks.length === 0; // nothing left to ask
    if (!drainedWithHeld) {
      if (flushTimerRef.current !== null) {
        clearTimeout(flushTimerRef.current);
        flushTimerRef.current = null;
      }
      return;
    }
    // Idle is a FACT only once this socket's snapshot AND the replay behind it
    // have landed (`busyKnown`). Before that, an empty queue is not "every
    // question answered" — the card and the asks have not replayed yet — and a
    // batch restored after a reload went out half-done, into a turn it could
    // not see, while the card that replayed next re-asked what it carried.
    if (!ws.busyKnown) {
      if (flushTimerRef.current !== null) {
        clearTimeout(flushTimerRef.current);
        flushTimerRef.current = null;
      }
      return;
    }
    if (!ws.agentBusy) {
      flushNow(); // agent is waiting on the user — send now
      return;
    }
    // Agent still busy (a late card mid-grind). Hold until the server's idle
    // edge: a send now would SIGTERM the turn that is working. The backstop only
    // bounds a busy flag that never clears, and it fires FIRST: 300 s here,
    // before the server's watchdog (360 s with no bridge frame) or the
    // servant's 10-minute wall clock would clear it. The longest sandbox turn
    // measured on dev is 268 s.
    focusDiag("flush_deferred", { heldFields: [...answers.keys()] }, true);
    if (flushTimerRef.current === null) {
      flushTimerRef.current = setTimeout(() => {
        flushTimerRef.current = null;
        focusDiag("flush_fallback");
        flushNow();
      }, FLUSH_BUSY_BACKSTOP_MS);
    }
  }, [
    accepted,
    flowTerminal,
    fatal,
    pending,
    current,
    answers,
    pendingDeclines,
    openAsks,
    ws.agentBusy,
    ws.busyKnown,
    flushNow,
  ]);

  // Clear a pending deferred-flush timer on unmount so it can't fire against a
  // gone component.
  useEffect(
    () => () => {
      if (flushTimerRef.current !== null) {
        clearTimeout(flushTimerRef.current);
        flushTimerRef.current = null;
      }
    },
    [],
  );

  // Refs so the per-card diagnostic effect can read the latest derivation +
  // pin without re-subscribing on every render.
  const openAsksRef = useRef(openAsks);
  openAsksRef.current = openAsks;
  const currentRef = useRef(current);
  currentRef.current = current;
  const [cardRev, setCardRev] = useState(0);

  // Per-card diagnostics. Captures the FULL per-field verdict (verdict + reason
  // + value + source) so `fieldHistory(field)` can replay a field's evolution.
  // Flags `would_have_switched` (the old pure derivation would have yanked the
  // pinned question). The `provided_regressed` flag is retired: phase 4's
  // monotonic store means a user-answered field can no longer revert, so the
  // loop signal it watched for is structurally impossible.
  useEffect(() => {
    if (cardId === null || card === null) return;
    setCardRev((n) => n + 1);
    const derived = openAsksRef.current[0] ?? null;
    const cur = currentRef.current;
    focusDiag(
      "card",
      {
        cardId,
        rev: cardRev + 1,
        allGreen: card.allGreen,
        v: card.verdicts.map((v) => ({
          f: v.field,
          v: v.verdict,
          ...(v.verdict !== "green" && v.reason
            ? { r: v.reason.slice(0, 80) }
            : {}),
          ...(typeof v.value === "string" && v.value.trim() !== ""
            ? { val: v.value.slice(0, 50) }
            : {}),
          ...(v.resolvedSource ? { s: v.resolvedSource } : {}),
        })),
      },
      true,
    );
    if (
      cur !== null &&
      derived !== null &&
      (cur.field !== derived.field || cur.kind !== derived.kind)
    ) {
      focusDiag("would_have_switched", {
        pinned: `${cur.kind}:${cur.field}`,
        derived: `${derived.kind}:${derived.field}`,
      });
    }
  }, [cardId]); // fire once per card id; reads latest via refs

  // Reset the per-step editing toggle whenever the surfaced step changes. Keyed
  // by `stepKeyOf` — `ask:null` would collapse every field-less ask into one
  // signature, so a second scoping question would not count as a step change.
  const stepSig = current ? `${current.kind}:${stepKeyOf(current)}` : "none";
  useEffect(() => setEditingReview(false), [stepSig]);

  // Activity-gated loader. Once Loom is "live" (sandbox ready OR a card has
  // landed) the spinner is hidden while ANYTHING is still visibly changing. It
  // only re-appears after a quiet stretch (no visible change for STALL_MS) so a
  // silent "Loom is thinking" gap still reassures. Before live it stays on.
  const STALL_MS = 10_000;
  // An open typed ask counts as live: the agent is demonstrably working and is
  // waiting on the USER. Without it the pre-card spinner sits on top of the very
  // question it is blocking.
  const live =
    ws.sandbox.phase === "ready" || card !== null || typedAsks.length > 0;
  const activitySig =
    `${ws.timeline.length}|${(lastAssistant ?? "").length}|${cardId ?? ""}|` +
    `${latestAskId ?? ""}|${typedAsks.length}|` +
    `${ws.sandbox.phase}|${ws.status}|${stepSig}|${accepted ? 1 : 0}|${editingReview ? 1 : 0}`;
  const [stalled, setStalled] = useState(false);
  useEffect(() => {
    setStalled(false);
    const timer = setTimeout(() => setStalled(true), STALL_MS);
    return () => clearTimeout(timer);
  }, [activitySig]);
  // The dots SAY Loom is working ("Loom is working through that…"), so only a
  // server fact may put them up: once live, the server's busy flag; before, a
  // workspace that is starting (or a socket still making its first connect).
  // A quiet 10 s is not work. Live on dev #280 the dots sat beside "Loom lost
  // the line to its workspace" for two minutes of `degraded` with depth 0, and
  // under a restart's boot before any turn existed; on #283 they came up 16 s
  // after the server said idle.
  const showLoader = live
    ? stalled && ws.agentBusy
    : ws.sandbox.phase !== "degraded" &&
      ws.sandbox.phase !== "terminated" &&
      ws.sandbox.phase !== "suspended";

  // A dead sandbox. Terminated is final in the state machine, and the phase is
  // reset to `idle` on every attach, so this reads true only while the server is
  // actively saying so.
  const sandboxDead = ws.sandbox.phase === "terminated";

  // The edge out of `thinking` — the discipline the tail deadlines already
  // follow, one screen over: a turn that returns NOTHING must not strand the
  // spinner forever.
  //
  // `pending` clears only when `epoch` advances, and `epoch` moves on a card, an
  // assistant turn or an ask. A turn that ends `subtype=success` with no tool
  // calls, no prose and no card moves none of the three, so the marker it was
  // set against never clears — and every branch below `thinking` in the ladder
  // (the split card, the ready card, the pinned question) is off screen for as
  // long as that lasts. Live: "Skip this question" on a split-time question
  // produced exactly that empty turn, and the flow sat on "Loom is working
  // through that…" for 12+ minutes while its own debug panel read `agent idle /
  // your turn`. A reload restored the card, the rename and the removed piece
  // intact, so what is stuck is step state, not data.
  //
  // Three legs, strongest signal first:
  //   - the sandbox is TERMINATED. Nothing can answer; release on the spot.
  //   - the SERVER says the turn is over: the socket is open, busy is known and
  //     idle, nothing of ours is held, and the workspace is up (`ready`) — or
  //     absent altogether (`idle`, no supervisor: an instance recycle), which
  //     waits longer — held still for `IDLE_SETTLE_MS` / `NO_WORKSPACE_SETTLE_MS`.
  //     The same fact the `agent_stopped` screen reads. NOT `degraded`: the
  //     router QUEUES the answer there and the servant answers it once the phase
  //     goes terminated (every degraded episode on dev since 09-24 lasted 120 s),
  //     so releasing would put the answered question back ~100 s before its
  //     answer comes. And not a starting phase: the answer rides the handoff.
  //   - the agent is BUSY. Held to the tail wall-clock backstop, the same
  //     ceiling the plan and sizing waits sit above; `activitySig` re-arms it.
  // Releasing retracts nothing: a reply that lands afterwards advances the epoch
  // and renders normally.
  const thinkingIdleFact =
    thinking &&
    ws.status === "open" &&
    ws.busyKnown &&
    !ws.agentBusy &&
    answers.size === 0 &&
    pendingDeclines.size === 0 &&
    (ws.sandbox.phase === "ready" || ws.sandbox.phase === "idle");
  const thinkingIdleSettled = useHeldFor(
    thinkingIdleFact,
    ws.sandbox.phase === "idle" ? NO_WORKSPACE_SETTLE_MS : IDLE_SETTLE_MS,
  );
  useEffect(() => {
    if (!thinking) return;
    if (sandboxDead) {
      focusDiag("thinking_released", { reason: "sandbox_terminated", epoch });
      setPending(null);
      return;
    }
    if (thinkingIdleSettled) {
      focusDiag("thinking_released", { reason: "agent_idle", epoch });
      setPending(null);
      return;
    }
    if (!ws.agentBusy) return;
    const timer = setTimeout(() => {
      focusDiag("thinking_released", { reason: "turn_wall_clock", epoch });
      setPending(null);
    }, THINKING_BUSY_DEADLINE_MS);
    return () => clearTimeout(timer);
    // `activitySig` is a re-arm trigger, not a read — same shape as the stall
    // timer above it.
  }, [thinking, sandboxDead, thinkingIdleSettled, ws.agentBusy, activitySig, epoch]);

  /* ── user actions (the ONLY things that advance the pin) ──────────────── */

  // A step is being answered — retire any record of what an earlier undo pulled
  // back from it, so the marked option is always the LAST thing the user chose.
  const forgetUndone = (key: string): void => {
    setUndone((m) => {
      if (!m.has(key)) return m;
      const n = new Map(m);
      n.delete(key);
      return n;
    });
  };
  // Answer the current question (or a "Change"d review). Hold it locally and
  // advance; the flush effect sends the batch once the queue drains.
  const answerCurrent = (field: string, text: string, meta?: AnswerMeta): void => {
    focusDiag("answer", { field, queued: queue.length });
    if (meta?.via === "loom") loomTakenRef.current.set(field, text);
    else loomTakenRef.current.delete(field);
    if (current !== null) setLastAnswered({ step: current, key: field });
    setAnswers((m) => new Map(m).set(field, text));
    // The typed ask this card question COLLAPSED WITH, if there is one.
    //
    // `stepKeyOf` keys a spec-bound non-concern ask by its FIELD, so an ask for
    // `role` and a card verdict for `role` are ONE step — but only `answerAsk`
    // recorded the attribution, and the pin effect never replaces a non-null
    // `current`. So whenever the CARD lands first, the field is pinned as a card
    // question, the ask arrives behind it and can never take the pin, and the
    // answer shipped in `cardAnswers` — which the server documents as closing no
    // ask. Live on dev #1102: card at 13:41:45, asks 5.1s later, `role` answered
    // 13:44:13 with only `means`+`priority` tombstoned, `role` re-asked and
    // answered a second time at 13:44:29.
    //
    // It is not cosmetic. `propose_card`'s R2-E refuses to green ANY field with
    // an open ask "regardless of provenance or grounding", so an orphaned ask
    // makes its field permanently ungreenable — the second answer was the only
    // thing that unblocked the gate. Suppressing the re-ask WITHOUT closing the
    // ask would turn a visible double-ask into a silent stall.
    //
    // Attributing here (rather than masking the ask at flush time) puts the
    // answer on the `payload.answers` lane `answerAsk` already uses, which every
    // deployed server tombstones from — so it is skew-safe in both directions,
    // where a client-side suppression against an old server is a hard stall.
    //
    // `typedAsks`, not `openAsks`: `openAsks` drops anything already in
    // `answers`, and this runs as the answer goes in. A CONCERN is excluded on
    // the same terms `stepKeyOf` excludes it — it is ABOUT the value, not a
    // request for it, so a plain field answer must not retire it.
    const twin = typedAsks.find(
      (a) =>
        a.kind === "ask" &&
        !a.concern &&
        a.field !== null &&
        FIELD_ORDER_SET.includes(a.field) &&
        stepKeyOf(a) === field,
    );
    if (twin !== undefined && twin.kind === "ask") {
      focusDiag("answer_attributed_twin", { field, askId: twin.askId });
      setAskAnswers((m) =>
        new Map(m).set(field, {
          askId: twin.askId,
          field: twin.field,
          kind: "question",
        }),
      );
      setHandledAskIds((s) => new Set(s).add(twin.askId));
    }
    forgetUndone(field);
    setProvided((s) => new Set(s).add(field)); // never re-review the user's own input
    setEditingReview(false);
    setCurrent(null); // advance — reconciliation pulls the next (or flush fires)
  };
  // Answer a TYPED ASK. Same batching as a card answer — the send-text is held
  // under the step key and flushed with everything else — plus the attribution
  // that turns it into a structured `question_answer`.
  const answerAsk = (
    ask: Extract<Step, { kind: "ask" }>,
    text: string,
    meta?: AnswerMeta,
  ): void => {
    const key = stepKeyOf(ask);
    if (meta?.via === "loom") loomTakenRef.current.set(key, text);
    else loomTakenRef.current.delete(key);
    focusDiag("answer_ask", {
      askId: ask.askId,
      field: ask.field,
      concern: ask.concern,
      queued: queue.length,
    });
    setLastAnswered({ step: ask, key });
    setAnswers((m) => new Map(m).set(key, text));
    forgetUndone(key);
    setAskAnswers((m) =>
      new Map(m).set(key, {
        askId: ask.askId,
        field: ask.field,
        kind: ask.concern ? "concern" : "question",
      }),
    );
    setHandledAskIds((s) => new Set(s).add(ask.askId));
    // A spec-bound ask the user answered is the user's own input for that field
    // — the card must not turn around and offer it back for review.
    if (ask.field !== null) setProvided((s) => new Set(s).add(ask.field!));
    setEditingReview(false);
    setCurrent(null);
  };
  // Record a field-level skip. BOTH skip doors go through here, and that is the
  // point: `declinedAll` applies `unDeclined` LAST (it has to — it out-votes a
  // durable card flag there is no un-decline frame for), so a door that adds to
  // `declined` without retiring an earlier undo of the SAME field adds nothing
  // at all. The mask does not apply, the ask leaves `typedAsks` via
  // `handledAskIds`, the field drops out of `owned`, and the card loop pushes
  // the identical question straight back — the #1081 shape, and with the
  // override now persisted alongside the held batch it would survive every
  // reload for the life of the tab rather than dying with the mount.
  //
  // Same both-doors consolidation the server half of this work is built on:
  // `retireAsksForDeclinedFields` sits inside `recordDeclinedFields` precisely
  // so neither skip door can be the one that forgets.
  const markFieldDeclined = (field: string): void => {
    setUnDeclined((s) => {
      if (!s.has(field)) return s; // identity-preserving: no sessionStorage churn
      const n = new Set(s);
      n.delete(field);
      return n;
    });
    setDeclined((s) => new Set(s).add(field));
  };
  // Skip an OPTIONAL typed ask. Unlike an optional CARD field (which is simply
  // left unset locally), a declined ask must reach the agent or it keeps
  // waiting — so it rides the next flush as `declinedAskIds`.
  const declineAsk = (ask: Extract<Step, { kind: "ask" }>): void => {
    focusDiag("decline_ask", { askId: ask.askId, concern: ask.concern });
    // Undoable on exactly the terms an ANSWER is, which is the resolution of the
    // "undo after an answer, nothing after a skip" split: the decline is HELD
    // (it rides the next flush as `declinedAskIds`), so while it is still in the
    // browser it can be taken back, and the flush closes the window for it the
    // same way it closes the window for every answer in the batch.
    setLastAnswered({ step: ask, key: stepKeyOf(ask), declinedAskId: ask.askId });
    // Same rule as `answerAsk`: a fresh decision about this ask retires
    // whatever an earlier undo pulled back for the same step key, or that
    // stale value could resurface and mislabel the choice widget once this
    // decline itself gets undone.
    forgetUndone(stepKeyOf(ask));
    setPendingDeclines((s) => new Set(s).add(ask.askId));
    // `field`/`concern` for this askId — `pendingDeclines` itself is bare ids
    // (what the wire wants), so the reload-persistence write-back needs this
    // to restore the field-level mask below without them.
    setDeclinedAskMeta((m) =>
      new Map(m).set(ask.askId, { field: ask.field, concern: ask.concern }),
    );
    setHandledAskIds((s) => new Set(s).add(ask.askId));
    // A FIELD-BOUND ask is a skip of that field, not just of that ask — mask the
    // field exactly as `skipCurrent` does, or the card's own question for it
    // comes straight back.
    //
    // Live #1081, step 2: the card carried `milestone: needs_input` and the
    // agent also raised `ask_user{field:"milestone", optional:true}`. The typed
    // ask owns the step key, so the user's Skip landed here — and masking only
    // the askId took the ask out of `typedAsks`, which took "milestone" out of
    // `owned`, which let the card loop push the identical question back. It also
    // deadlocked the decision: the auto-flush is gated on `openAsks.length === 0`
    // and that re-surfaced question keeps it non-empty, so `declinedAskIds` never
    // went out and the server's `recordDeclinedFields` never ran. Two skips
    // before anything at all reached the wire.
    //
    // No `sendReadinessFieldDecline` here — that would be a second frame for one
    // decision. The held `declinedAskIds` carries it, and the server records the
    // field from it (`recordDeclinedFields`, ws/stream.ts); `declinedAll` picks
    // up the durable half on the next card. This mask only has to survive until
    // then, which is the same contract `skipCurrent`'s does.
    //
    // Two narrowings, both mirroring a rule that already exists elsewhere:
    //  · OPTIONAL fields only, the same filter `recordDeclinedFields` applies
    //    server-side. `optional` is a MODEL-supplied flag on the ask, so an
    //    `ask_user{field:"role", optional:true}` would otherwise let one skip
    //    mask a gating field — the card question for it would never surface
    //    again in this mount and the gate would stall with nothing on screen.
    //  · not a CONCERN. A concern is ABOUT a settled value, not a request for
    //    it (the same exemption `openAsks` and the server's re-ask guard make),
    //    and masking its field would swallow the "Loom set this — keep it?"
    //    review that is the user's only sight of a code-decided green.
    if (
      ask.field !== null &&
      !ask.concern &&
      isOptionalField(ask.field)
    ) {
      markFieldDeclined(ask.field);
    }
    setEditingReview(false);
    setCurrent(null);
  };
  /**
   * "Is this the screen you meant?" — yes or no.
   *
   * Deliberately NOT routed through the held answer batch, and that is the
   * whole point of giving it its own step kind. The batch exists to gather
   * ATTRIBUTED TEXT into one grounding turn; a sketch verdict has no text, no
   * field and nothing to ground. Writing it into `answers` would put it in
   * front of `composeAnswerPrompt`, and a `user_message` echo of an approval
   * sentence is exactly what the readiness gate's word-boundary-free quote scan
   * would then be able to match. So: its own frame, no text, and nothing
   * touched on the card.
   *
   * `handledAskIds` retires the step the instant the user acts, so the picture
   * stops competing with the next question without waiting for the server's
   * `question_resolved` to come back.
   */
  const answerPreview = (
    step: Extract<Step, { kind: "preview" }>,
    verdict: "right" | "wrong",
  ): void => {
    focusDiag("figma_preview_verdict", { askId: step.askId, verdict });
    setHandledAskIds((s) => new Set(s).add(step.askId));
    ws.sendFigmaPreviewVerdict(step.previewId, step.askId, verdict);
    setPreviewEcho({ verdict, linkCount: flowFigmaLinks.length });
    setCurrent(null);
  };
  /**
   * "Carry on" — the sketch is skipped, whether the user did not want to look
   * or the drawing could not be built. The ask is optional by construction, so
   * this is the ordinary decline path; it sends a decline-only batch exactly as
   * `declineAskDirect` does, because the server must stop waiting on it either
   * way.
   */
  const skipPreview = (step: Extract<Step, { kind: "preview" }>): void => {
    focusDiag("figma_preview_skipped", { askId: step.askId });
    setHandledAskIds((s) => new Set(s).add(step.askId));
    ws.sendQuestionAnswer(iid, "", [], [step.askId], {
      answeredCount: 0,
      scope: sendScope,
    });
    setCurrent(null);
  };
  // Answer / skip an ask rendered on the POST-ACCEPT screen, straight onto the
  // wire instead of into the held batch.
  //
  // The batch is unreachable there and must stay that way: the flush effect is
  // gated on `!accepted`, and the pin-reconciliation effect bails on `accepted`
  // too — both guards exist so a late verdict can't auto-fire a billed turn
  // after the issue was written. Holding the answer under those guards would
  // render a question that looks answerable and then silently eats the answer,
  // which is strictly worse than not rendering it. So the post-accept host
  // sends its own turn: same structured frame, same byte-exact text, same
  // attribution — only the batching is dropped.
  const answerAskDirect = (
    ask: Extract<Step, { kind: "ask" }>,
    text: string,
  ): void => {
    focusDiag("answer_ask_direct", {
      askId: ask.askId,
      field: ask.field,
      concern: ask.concern,
    });
    setHandledAskIds((s) => new Set(s).add(ask.askId));
    ws.sendQuestionAnswer(
      iid,
      text,
      [
        {
          askId: ask.askId,
          field: ask.field,
          text,
          kind: ask.concern ? "concern" : "question",
        },
      ],
      [],
      { answeredCount: 1, scope: sendScope },
    );
  };
  const declineAskDirect = (ask: Extract<Step, { kind: "ask" }>): void => {
    focusDiag("decline_ask_direct", { askId: ask.askId, concern: ask.concern });
    setHandledAskIds((s) => new Set(s).add(ask.askId));
    // Decline-only batch: no text, one closed ask. The server routes it as an
    // explicit "don't wait for this" — without that routing a skipped ask is a
    // permanent park, since the breakdown deadline is cleared while an ask is
    // open.
    ws.sendQuestionAnswer(iid, "", [], [ask.askId], {
      answeredCount: 0,
      scope: sendScope,
    });
  };
  // Skip an OPTIONAL field (e.g. milestone). Hard-declines it so it leaves
  // `openAsks` and never re-surfaces; sends nothing (the field stays unset, which
  // the apply path simply omits from the GitLab write). Optional fields don't
  // gate `allGreen`, so this just unblocks the queue.
  const skipCurrent = (field: string): void => {
    focusDiag("skip_optional", { field });
    // The decision LEAVES the browser. Without this the agent never learned,
    // the persisted card kept the slot at `needs_input` forever, and the mask
    // died on the next remount — so the question came straight back. The server
    // refuses a required field by name, so this cannot retire a gating one.
    ws.sendReadinessFieldDecline(iid, field);
    // Same rule as `answerCurrent`: a fresh decision about this field retires
    // whatever an EARLIER undo pulled back for it, or a stale pre-skip value
    // could resurface and mark a choice widget with a pick the user did not
    // just make (undo the skip → the old `undone` entry is still there).
    forgetUndone(field);
    // The one-shot undo marker, MARKED as a skip. A skip holds no `answers`
    // entry, so it used to be un-undoable — the undo bar appeared after an
    // answer and not after a skip, and nothing else in the flow could bring a
    // skipped field back (the ready screen lists greens only). It is a decision
    // like any other and gets the same one-shot window.
    if (current !== null)
      setLastAnswered({ step: current, key: field, skipped: true });
    // Masks the field and retires any previous undo override for it (see
    // `markFieldDeclined` — a re-skip that left the override standing would be
    // out-voted by it and change nothing).
    markFieldDeclined(field);
    setEditingReview(false);
    setCurrent(null); // advance — reconciliation pulls the next (or apply shows)
  };
  const keepReview = (field: string, value: string): void => {
    focusDiag("keep", { field });
    // Keeping a derived value is ACTING on the field — fold into `provided`
    // (the former `kept` map) so it's never re-reviewed. `value` is unused now
    // that the latch is field-keyed; phase-4 monotonicity keeps the kept green
    // from reverting, so a value-keyed re-surface is no longer needed.
    void value;
    // Same one-shot bookkeeping as `skipCurrent`: a keep holds no `answers`
    // entry, so it isn't undoable, but it does consume any prior undo.
    if (current !== null) setLastAnswered({ step: current, key: field });
    setProvided((s) => new Set(s).add(field));
    setCurrent(null); // green value accepted as-is; nothing to send — advance
  };
  // Undo the just-submitted answer, while it is still HELD locally (before the
  // batch flush sends it). Reverses exactly what `answerCurrent` / `answerAsk`
  // added and restores the step to the screen, pre-filled with the undone text.
  // Gated on the answer still being in `answers` (`lastAnswered.key`) — the same
  // proof the flush hasn't fired that the button visibility uses. One-shot.
  const undoLast = (): void => {
    const la = lastAnswered;
    if (la === null) return;
    const key = la.key;
    const step = la.step;
    // A SKETCH verdict is never undoable and never gets here: it leaves on its
    // own frame the instant the user presses a button, so there is no held
    // answer to pull back and nothing local to reverse. Guarded rather than
    // assumed — `lastAnswered` is the one place a step of any kind can be
    // parked, and everything below this line reads a field.
    if (step.kind === "preview") return;
    // A SKIP has no held answer to restore: take the field out of the declined
    // mask (both halves, see `declinedAll`) and put the question back. The
    // decline frame already left the browser and there is no un-decline frame —
    // it only tells the AGENT to stop asking, so the user answering the
    // re-opened question is still written normally. `unDeclined` is the half
    // that has to outlive this mount: the server's record does, and the two are
    // read against each other on every card.
    if (la.skipped === true) {
      const f = step.field;
      focusDiag("undo", { key, kind: step.kind, skipped: true });
      if (f !== null) {
        setDeclined((s) => {
          const n = new Set(s);
          n.delete(f);
          return n;
        });
        setUnDeclined((s) => new Set(s).add(f));
      }
      setCurrent(step);
      setLastAnswered(null);
      return;
    }
    // A DECLINED typed ask: the decision is still in the browser (it rides the
    // next flush), so taking it back is just un-holding it.
    if (la.declinedAskId !== undefined) {
      const askId = la.declinedAskId;
      if (!pendingDeclines.has(askId)) return; // already flushed
      focusDiag("undo", { key, kind: step.kind, declinedAsk: true });
      setPendingDeclines((s) => {
        const n = new Set(s);
        n.delete(askId);
        return n;
      });
      setHandledAskIds((s) => {
        const n = new Set(s);
        n.delete(askId);
        return n;
      });
      setDeclinedAskMeta((m) => {
        if (!m.has(askId)) return m;
        const n = new Map(m);
        n.delete(askId);
        return n;
      });
      // …and the field mask the decline laid down goes with it. Nothing left the
      // browser, so unlike `skipCurrent`'s undo this needs no `unDeclined`
      // override: there is no server-side record to out-vote, only this mount's
      // own set to take the field back out of.
      if (step.field !== null) {
        setDeclined((s) => {
          if (!s.has(step.field!)) return s;
          const n = new Set(s);
          n.delete(step.field!);
          return n;
        });
      }
      setCurrent(step);
      setLastAnswered(null);
      return;
    }
    const held = answers.get(key);
    if (held === undefined) return; // already flushed — the window is closed
    focusDiag("undo", { key, kind: step.kind });
    // Remember what was pulled back. A text control re-opens filled from the
    // draft store a few lines down; a choice control has no draft store, so this
    // is the only thing that can mark the option the undo is walking back.
    setUndone((m) => new Map(m).set(key, held));
    // Put the submitted text back into the widget's draft store (in its native
    // shape) so the restored control re-opens filled, not blank: the widget's
    // own submit wrote an EMPTY draft to the store, which would otherwise shadow
    // the `initialValue`/`initialRows` seed on remount.
    //
    // …unless the answer WAS Loom's: a click on "Use this text" took the bytes
    // on screen, and undoing it returns the step CLOSED — Loom's answer and the
    // two buttons again — never a box holding Loom's words as if the user had
    // typed them.
    const tookLoom = loomTakenRef.current.get(key) === held;
    loomTakenRef.current.delete(key);
    // Counted beside `loom_answer_use`, so an adoption count can subtract the
    // clicks the user took back (#253: a Use then an Undo counted as adopted).
    if (tookLoom) {
      reportClientEvent("ui.click", {
        control: "loom_answer_undo",
        surface: step.kind === "ask" ? "focus_ask" : "focus_question",
      });
    }
    const store = widgetDraftsRef.current;
    if (store !== null) {
      const base =
        step.kind === "ask"
          ? `ask:${step.askId}`
          : step.kind === "review"
            ? `review:${step.field}`
            : `q:${step.field}`;
      const seedField =
        step.kind === "ask" ? (step.field ?? "agent_ask") : step.field;
      if (tookLoom) {
        store.set(base, "");
        store.set(`${base}:rows`, []);
        store.set(`${base}:draft`, "");
        store.set(`${base}:fromLoom`, false);
        store.set(`${base}:ownFor`, null);
      } else if (defaultWidgetForField(seedField).type === "list_builder") {
        store.set(`${base}:rows`, splitListRows(held));
        store.set(`${base}:draft`, "");
      } else {
        store.set(base, prettyValue(seedField, held));
      }
    }
    setAnswers((m) => {
      const n = new Map(m);
      n.delete(key);
      return n;
    });
    // Attribution is keyed by STEP KEY and now exists for a CARD step too (see
    // the twin lookup in `answerCurrent`), so it must be cleared unconditionally.
    // Scoping it to `step.kind === "ask"` left an `askAnswers` row behind whose
    // held text had just been deleted — and the next flush sends that ask
    // `text: ""`, which the wire schema's `.min(1)` rejects for the WHOLE frame,
    // not just the empty fragment.
    const attributedAskId =
      askAnswers.get(key)?.askId ?? (step.kind === "ask" ? step.askId : null);
    setAskAnswers((m) => {
      const n = new Map(m);
      n.delete(key);
      return n;
    });
    if (attributedAskId !== null) {
      setHandledAskIds((s) => {
        const n = new Set(s);
        n.delete(attributedAskId);
        return n;
      });
    }
    if (step.kind === "ask") {
      const askField = step.field;
      if (askField !== null) {
        setProvided((s) => {
          const n = new Set(s);
          n.delete(askField);
          return n;
        });
      }
    } else {
      const f = step.field;
      setProvided((s) => {
        const n = new Set(s);
        n.delete(f);
        return n;
      });
    }
    setCurrent(step);
    setLastAnswered(null);
  };
  // Edit a green field from the final "ready to apply" preview. Sends the
  // field's canonical (self-labelled) text as a STRUCTURED `readiness_field_edit`
  // frame (not a plain chat turn): the server resolves widget fields
  // (priority / milestone) itself in one round-trip, and records a pending edit
  // + drives a directed re-propose for free-text fields — so the edit provably
  // enters the card instead of relying on the agent voluntarily re-calling
  // propose_card. NO `setPending(epoch)`: the ready card stays mounted; the
  // per-field pending state arrives on the next readiness_card event (the
  // server-set `pendingEdits`).
  // A bare yes in a ready-screen editor: the value stands, nothing is sent, and
  // the row says so. The server keeps its own backstop for a yes that gets
  // through (`field_edit.assent_kept`), but on the client it never leaves.
  const keepGreen = (field: string): void => {
    focusDiag("edit_green_kept", { field });
    setEditField(null);
    setKeptField(field);
  };
  const editGreen = (field: string, text: string): void => {
    focusDiag("edit_green", { field });
    setKeptField(null);
    setProvided((s) => new Set(s).add(field)); // don't re-review the edited value
    setEditField(null);
    ws.sendReadinessFieldEdit(iid, field, text);
  };
  // Free text from the message bar is a deliberate single utterance. Flush any
  // held answers WITH it (one turn) so the agent never runs on partial context.
  //
  // `mode: "comment"` is the SECOND send: the same bytes, claiming nothing.
  //
  // The composer is the "in your own words" box for whatever is pinned, which is
  // right for an ANSWER and wrong for an objection: the bytes land in a real user
  // turn, so the gate greens them and apply writes the objection into the
  // description as the story's "so that". A comment must therefore claim nothing
  // AND leave the question pinned — the user still owes that answer. Byte-exact
  // either way: both paths echo `text` verbatim; only the attribution differs.
  //
  // This also SKIPS `isFlowBoundary`: a comment does not re-open the flow. That
  // is deliberate — a note about the current issue should not move the evidence
  // eviction horizon or drop the CLI resume id. It falls out of taking the
  // structured frame, which carries no `flow` block at all.
  const sendFree = (text: string, mode: "answer" | "comment" = "answer"): void => {
    // A bare "yes" typed into the bar while a VALUE question is pinned names
    // nothing, and attributed as that field's answer it became the value (s11,
    // dev 2026-09-25: "ja" to Outcome, three rounds for one field). It goes out
    // as a NOTE instead: the words still reach Loom, the question stays pinned,
    // and nothing claims them as the answer. A yes/no question, a concern and a
    // field-less ask keep "ja" as a real answer.
    const pinnedValueField =
      current === null
        ? null
        : current.kind === "question"
          ? current.field
          : current.kind === "ask" && !current.concern
            ? current.field
            : null;
    const assentOnValue =
      mode === "answer" &&
      pinnedValueField !== null &&
      isValueField(pinnedValueField) &&
      isBareAssent(text);
    if (assentOnValue) focusDiag("bar_assent_as_note", { field: pinnedValueField });
    // The milestone is PICKED from the project's list or skipped — nothing
    // typed sets it (the user's decision, 2026-10-02; live, a line typed into
    // this bar under the milestone step became the milestone on dev db9a1433).
    // So whatever is typed here while a milestone step is pinned — its card
    // question, a typed ask, a concern, the review — goes out as a NOTE: the
    // words still reach Loom, the step stays pinned, nothing claims them as
    // the milestone. On the wire it is exactly "Send as a note". The server
    // holds the same line on its side (the gate grounds the milestone only
    // from a pick).
    const barOnMilestone =
      mode === "answer" && current !== null && current.field === "milestone";
    if (barOnMilestone) focusDiag("bar_milestone_as_note", {});
    const asComment = mode === "comment" || assentOnValue || barOnMilestone;
    // A comment claims nothing, so it does not retire the pinned ask either.
    const pinnedAsk =
      !asComment && current !== null && current.kind === "ask" ? current : null;
    focusDiag("send_free", {
      withHeld: answers.size,
      ...(asComment ? { mode: "comment" } : {}),
      ...(pinnedAsk !== null ? { answersAsk: pinnedAsk.askId } : {}),
    });
    if (flushTimerRef.current !== null) {
      clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }
    const combined = composeBatch(answers, text);
    const heldAskAnswers = askAnswers;
    const heldDeclines = pendingDeclines;
    setAnswers(new Map());
    setAskAnswers(new Map());
    setPendingDeclines(new Set());
    setDeclinedAskMeta(new Map());
    setLastAnswered(null); // held answers go out with this turn — undo closes
    // A comment does NOT advance the pin: the question is still owed.
    if (!asComment) setCurrent(null);
    setPending(epoch);
    // Held widget answers carry their own field; then the currently-pinned typed
    // ask (if any) is attributed to this free utterance. The composer IS the "in
    // your own words" box for the pinned question, so a message typed here MUST
    // retire that ask — a plain `ws.send` would leave it pinned forever (the
    // server only tombstones asks reached via the structured frame; a field the
    // agent grounds green is caught by the server carded-release, but a
    // `field: null` scoping ask or an answer the agent can't ground has no green
    // field to stand in, so the frame is the only path that retires it). Scoped
    // to `current` ALONE — never every open ask — so a comprehensive message
    // can't silently resolve a scoping ask the user didn't address; a still-open
    // required field re-surfaces through the card question channel.
    let pinnedAskTookText = false;
    const askAnswersOut: Array<{
      askId: string;
      field: string | null;
      text: string;
      kind: "question" | "concern";
    }> = [...heldAskAnswers.entries()].map(([key, a]) => ({
      askId: a.askId,
      field: a.field,
      text: answers.get(key) ?? "",
      kind: a.kind,
    }));
    if (
      pinnedAsk !== null &&
      !askAnswersOut.some((a) => a.askId === pinnedAsk.askId) &&
      !heldDeclines.has(pinnedAsk.askId)
    ) {
      askAnswersOut.push({
        askId: pinnedAsk.askId,
        field: pinnedAsk.field,
        text,
        // The composer is the "in your own words" box for whatever is pinned —
        // including a pinned concern, whose reply must not reach the model as
        // the field's new value.
        kind: pinnedAsk.concern ? "concern" : "question",
      });
      pinnedAskTookText = true;
    }
    // Carrying held ASK answers out on the plain path would drop their
    // attribution — same structured frame as flushNow, with the free text
    // riding along as the trailing fragment of the one composed turn.
    //
    // Held CARD answers need the same treatment for the same reason: this frame
    // routes an attributed prompt, not `combined`, so a card answer left out is
    // one the agent never reads. Identical bug to the one in `flushNow`.
    const cardAnswersOut = [...answers.entries()]
      .filter(([key]) => !heldAskAnswers.has(key) && FIELD_ORDER_SET.includes(key))
      .map(([field, value]) => ({ field, text: value }));
    // The composer is the "in your own words" box for whatever is pinned, so
    // when a CARD question is pinned the free text answers that slot — exactly
    // the reasoning that already attributes it to a pinned ask above. Without
    // this the utterance reached the echo and nothing else.
    const pinnedField =
      !asComment &&
      current !== null &&
      current.kind !== "ask" &&
      // A sketch has no field for free text to land on, and its answer is a
      // button, never a sentence. Typing while it is pinned is an ordinary
      // message, exactly as it is under a typed ask.
      current.kind !== "preview" &&
      FIELD_ORDER_SET.includes(current.field) &&
      !answers.has(current.field)
        ? current.field
        : null;
    if (pinnedField !== null) cardAnswersOut.push({ field: pinnedField, text });
    // Whether the free text was consumed above — tracked as a FLAG, not by
    // comparing strings. A held ask answer that happens to be byte-identical to
    // the utterance would otherwise look like it had claimed it, leaving the
    // remainder unsent and tripping a false `answer.dropped_fragments`.
    const textClaimed = pinnedField !== null || pinnedAskTookText;
    // Nothing pinned to attribute it to — but the frame still routes only what
    // is attributed, so an unowned utterance would vanish. The flush is
    // idle-gated, so "queue drained, answers held, user types one more thought"
    // is a real window, not a corner case. Carry it explicitly.
    const freeText = !textClaimed && text.length > 0 ? text : undefined;
    // Nothing on this turn is attributed to a question — the text is a NOTE,
    // whichever lane it takes below. Anything attributed makes it an answer.
    if (
      text.length > 0 &&
      askAnswersOut.length === 0 &&
      cardAnswersOut.length === 0
    ) {
      setLastNote({
        text,
        pinnedKey: current !== null ? stepKeyOf(current) : null,
      });
    } else {
      setLastNote(null);
    }
    // A COMMENT must take the structured frame even with nothing else on it: the
    // plain `ws.send` below carries no `freeText`, so `composeAnswerPrompt` would
    // never render the "The user also wrote:" block and the note would reach the
    // echo and nothing else. It is also what keeps the `flow` boundary off the
    // wire.
    // `cardAnswersOut.length > 0` on the same terms as `flushNow`'s `structured`
    // gate: this frame is the only path that ATTRIBUTES a card answer, and a
    // batch of them with no ask in it took the plain send and reached the model
    // as unlabelled prose. `pinnedField` puts the utterance itself in there
    // whenever a card question is on screen, which is the common shape here.
    if (
      askAnswersOut.length > 0 ||
      cardAnswersOut.length > 0 ||
      heldDeclines.size > 0 ||
      asComment
    ) {
      const clientMsgId = ws.sendQuestionAnswer(
        iid,
        combined,
        askAnswersOut,
        [...heldDeclines],
        {
          cardAnswers: cardAnswersOut,
          ...(freeText !== undefined ? { freeText } : {}),
          // The held answers plus this utterance — what the server checks its
          // own attribution against.
          answeredCount: answers.size + (text.length > 0 ? 1 : 0),
          scope: sendScope,
        },
      );
      // The SAME bookkeeping `flushNow` does at its own send, and it was missing
      // here entirely: this path discarded the delivery id and wrote no
      // `flushedAt` row, so none of the just-flushed suppression existed for the
      // fields it sent. That was invisible while a card answer never retired its
      // twin ask — the ask kept the field in `owned` and the card loop never got
      // to it. The moment the ask IS retired (by the twin attribution above or
      // by the server backstop) the field leaves `owned`, `flushedAt.get(field)`
      // is `undefined`, both suppression clauses are skipped, and `agentAsked`
      // deliberately bypasses `provided` — so the identical question comes
      // straight back through the CARD channel. Swapping an ask-channel
      // double-ask for a card-channel one is not a fix.
      const entry = {
        cardId: cardIdRef.current,
        clientMsgId: clientMsgId === "" ? null : clientMsgId,
      };
      setFlushedAt((prev) => {
        const next = new Map(prev);
        for (const a of cardAnswersOut) next.set(a.field, entry);
        return next;
      });
      // `sendFree` is the one answer path that recorded nothing for the field it
      // answers — `answerCurrent` latches `provided`, this did not.
      if (pinnedField !== null) {
        setProvided((s) => new Set(s).add(pinnedField));
      }
      return;
    }
    ws.send(combined, { scope: sendScope, flow: { issueIid: iid } });
  };
  // Raise the ceiling and carry on. A fixed +$5 step: the point is to unblock a
  // session that is one step from done, not to hand out an open tab, and a
  // second wall simply shows this screen again with a fresh choice.
  const BUDGET_STEP_USD = 5;
  const onKeepGoing = (): void => {
    focusDiag("budget_keep_going", { addUsd: BUDGET_STEP_USD });
    ws.sendBudgetExtend(BUDGET_STEP_USD);
  };
  const onAccept = (surface: AcceptSurface): void => {
    if (!card?.acceptToken) return;
    // Structural, not layout-dependent: the terminal screen renders no Accept,
    // but a stale render or a control hoisted into another branch must not be
    // able to re-send an accept for a card the server already consumed.
    if (flowTerminal) return;
    focusDiag("accept");
    // Report the CLICK, not just the frame, and report it to the SERVER.
    //
    // "I pressed Apply and nothing happened" is the report this whole seam
    // exists to answer, and it needs three facts an ordinary server log cannot
    // supply: the user clicked, the socket's state when they did, and whether
    // the frame that followed was handed to an open socket. This is the first;
    // `dispatchFrame`'s `frame_send` is the third.
    //
    // The old line printed `tokenTail: acceptToken.slice(-8)` to the browser
    // console. A token fragment is still a token — presence is all a log ever
    // needs, so it is gone rather than shortened.
    reportClientEvent("frame_send", {
      kind: "accept_click",
      delivered: ws.status === "open",
      // WHICH screen's Accept this was — the ready card hides its Accept while
      // an edit is pending, the budget / sandbox / tools screens do not — and
      // how many edits were pending when it was pressed.
      surface,
      pendingEditCount: card.pendingEdits?.length ?? 0,
    });
    // Deterministic apply: structured frame → server applies directly and
    // replies with `readiness_apply_result`. Survives an instance recycle that
    // orphaned the sandbox supervisor (the old free-text path fell through to
    // the triage-only servant and hung forever).
    ws.sendReadinessAccept(iid, card.acceptToken);
    setApplyTimedOut(false);
    setAccepted(true);
  };
  const retryApply = (): void => {
    if (!card?.acceptToken) return;
    setApplyTimedOut(false);
    ws.sendReadinessAccept(iid, card.acceptToken);
  };
  const startOver = (): void => {
    pendingPlanConfirmRef.current = null;
    setPlanConfirmLost(null);
    // Evict the (possibly dead) session so the next mount cold-boots a fresh one.
    // DESTRUCTIVE and demoted everywhere it is still offered: `session.reset()`
    // drops the persisted binding without a DELETE, so the old sandbox keeps
    // running (and billing) until idle eviction while the next flow cold-boots
    // and re-grinds everything. `reconnectSandbox` is the non-destructive
    // alternative and is the primary on both terminal screens.
    session.reset();
    onRestart();
  };

  /**
   * A fresh session that STAYS on this issue.
   *
   * Same eviction as `startOver` — this issue's binding is dropped and the
   * next mount cold-boots — but the flow re-enters on the SAME iid instead of
   * unwinding to the mode picker. From the budget wall the picker was the whole
   * defect: the wall is a property of the SESSION, not of the issue, so the one
   * thing the user wanted to keep (the issue they were mid-refine on) was the
   * one thing the button threw away. The greens do NOT survive (the persisted
   * card is keyed by (sessionId, issueIid)); the copy says so.
   */
  const startFreshHere = (): void => {
    focusDiag("session_fresh_same_issue", { iid });
    session.reset();
    if (onFreshSession !== undefined) onFreshSession();
    else onRestart();
  };

  /**
   * Re-trigger THIS session's sandbox in place.
   *
   * Keeps the session id, the history, the persisted `CardState` and every
   * answered field — the server forwards `resume: hasCodingState`, so a session
   * that ever reached a live bridge gets `claude --resume` and the agent
   * recovers its own conversation. The idle-kill dialog in this file drives the
   * same call; it lives here so the terminal screens can offer it too instead of
   * throwing the session away.
   *
   * `resumed` is the server's answer to "was there anything to resume": false
   * means the new sandbox boots with no conversation at all, which is what the
   * re-kick below exists for.
   */
  const reconnectSandbox = async (): Promise<{
    ok: boolean;
    resumed: boolean;
    /** There is nothing to reconnect TO, so trying again cannot help. A 429, a
     *  5xx and a dropped connection are the opposite of that and say so. */
    terminal: boolean;
  }> => {
    const sid = session.sessionId;
    if (sid === null) return { ok: false, resumed: false, terminal: true };
    try {
      const res = await fetch(`/sessions/${sid}/reconnect`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: reconnectBody(),
      });
      if (!res.ok) {
        return {
          ok: false,
          resumed: false,
          terminal: res.status !== 429 && res.status < 500,
        };
      }
      const body = (await res.json().catch(() => ({}))) as {
        resumed?: unknown;
      };
      return { ok: true, resumed: body.resumed === true, terminal: false };
    } catch {
      // The request never landed. Nothing has been revoked.
      return { ok: false, resumed: false, terminal: false };
    }
  };

  /**
   * A reconnect alone does NOT restart the lost turn.
   *
   * `seededRef` is already true for this mount and the resumed-mount fence sees
   * the replayed seed as `flowEvidence`, so the fresh sandbox boots ready and
   * silent. What it is owed once it is up depends on what it still knows:
   *
   *   - `"seed"` — nothing: no card, and the server did not resume the old
   *     conversation (`resumed: false`). The boundary seed opens the flow anew.
   *   - `"nudge"` — the server RESUMED the conversation (`resumed: true`, the
   *     bridge boots `claude --resume`) but no card exists yet. A boundary seed
   *     would wipe exactly what the resume restored — the reload-reseed failure
   *     mode — and sending nothing left the resumed sandbox idle, so 20 s after
   *     `ready` the page said "Loom isn't working on this right now" and its
   *     "Start again" then sent that boundary seed. `makeRefineNudge` goes out
   *     as an ordinary same-flow turn instead.
   *   - null — nothing to send: a card exists, and it carries the flow.
   *
   * Only ever set from a successful reconnect.
   */
  const [reKick, setReKick] = useState<"seed" | "nudge" | null>(null);
  /** The last restart attempt failed for a reason that trying again can fix. */
  const [restartRetryable, setRestartRetryable] = useState(false);
  /** A restart's `POST /reconnect` is out. */
  const [restarting, setRestarting] = useState(false);
  /** "Refine another" on the done screens — traced, then the unchanged exit. */
  const refineAnother = (surface: "applied" | "apply_failed"): void => {
    reportClientEvent("ui.click", {
      control: "refine_another",
      surface,
      issueIid: iid,
    });
    onRestart();
  };
  /**
   * Restart from one of the three screens that may offer it — each one a
   * server fact: the workspace is TERMINATED (`sandbox_dead`), the workspace is
   * up and the server says nothing is running (`agent_stopped`), or there is no
   * workspace at all (`no_workspace`). Never while the server says busy or
   * starting: on a healthy bridge `/reconnect` re-arms nothing
   * (`reconnect.reuse`), and the boundary seed that follows would SIGTERM the
   * running turn (dev job 6396056: `propose_card` "tool use was rejected", exit
   * 143, the first card ~4 minutes late). The seed itself waits for the
   * server's idle too — see the re-kick effect below.
   */
  const restartSandbox = (
    surface: "sandbox_dead" | "agent_stopped" | "no_workspace",
  ): void => {
    reportClientEvent("ui.click", {
      control:
        surface === "agent_stopped"
          ? "start_again"
          : surface === "no_workspace"
            ? "start_workspace"
            : "restart_workspace",
      surface,
      issueIid: iid,
    });
    setRestartRetryable(false);
    setRestarting(true);
    void (async () => {
      const { ok, resumed, terminal } = await reconnectSandbox();
      setRestarting(false);
      if (!ok) {
        // 409 session_revoked / 403 / 404: there is nothing to reconnect TO, so
        // the destructive path is the only one left. A 429 or a 5xx or a
        // dropped request is NOT that, and used to take the same exit — live,
        // one rate-limited POST cleared the binding and dropped the user on the
        // mode picker, under a button whose own copy promises "your issue
        // hasn't been changed, and nothing you've answered is lost".
        if (terminal) startOver();
        else setRestartRetryable(true);
        return;
      }
      // `boundary: true` is safe ONLY under `!card && !resumed`: nothing
      // verified exists and the old CLI conversation is provably gone. With a
      // card nothing is owed; when the server resumed the coding state, a
      // boundary seed would discard context the agent still has, so it gets a
      // same-flow nudge instead.
      setReKick(card !== null ? null : resumed ? "nudge" : "seed");
    })();
  };
  // The user holds the turn: an open question is waiting on THEM, and their
  // answer is the next turn. A nudge on top of it would only make the resumed
  // agent ask again.
  const userHoldsTurn =
    current !== null || typedAsks.length > 0 || openAsks.length > 0 || answers.size > 0;
  useEffect(() => {
    if (reKick === null) return;
    if (ws.sandbox.phase !== "ready") return;
    // A card carries the flow on its own — nothing is owed.
    if (card !== null) {
      setReKick(null);
      return;
    }
    // Both kicks are prompts, and a prompt that lands on a running turn
    // SIGTERMs it. They go out only once the server has said, on this socket,
    // that nothing is running.
    if (!ws.busyKnown || ws.agentBusy) return;
    if (reKick === "nudge") {
      if (userHoldsTurn) {
        focusDiag("nudge_after_reconnect_dropped", { iid });
        setReKick(null);
        return;
      }
      focusDiag("nudge_after_reconnect", { iid });
      traceSeedSend("nudge_after_reconnect");
      // NOT a boundary: the conversation `--resume` restored stays. Machine
      // words, so nothing in it is the user's (`evidenceText: ""`).
      ws.send(makeRefineNudge(iid), {
        scope: sendScope,
        flow: { issueIid: iid },
        evidenceText: "",
      });
      setReKick(null);
      return;
    }
    focusDiag("seed_after_reconnect", { iid });
    traceSeedSend("after_reconnect");
    ws.send(seed, {
      scope: sendScope,
      // The issue title rides the BOUNDARY send only — the label the server
      // writes onto the conversation's index row, so the history rail can name
      // it "#82 · <Issue-Titel>" instead of this English machine seed. It is
      // display metadata: never evidence, never part of the routed prompt.
      flow: { issueIid: iid, boundary: true, issueTitle },
      ...(seedEvidence !== undefined ? { evidenceText: seedEvidence } : {}),
    });
    setReKick(null);
  }, [
    reKick,
    ws.sandbox.phase,
    ws.busyKnown,
    ws.agentBusy,
    card,
    userHoldsTurn,
    ws.send,
    seed,
    seedEvidence,
    sendScope,
    iid,
  ]);

  // Deterministic apply outcome for THIS issue (null until the server replies).
  const applyResult =
    ws.applyResult && ws.applyResult.issueIid === iid ? ws.applyResult : null;
  // Arm a deadline once we're applying with no result yet; surface a Retry if
  // it elapses (e.g. a dropped accept frame). Cleared as soon as a result lands.
  useEffect(() => {
    if (!accepted || applyResult !== null) return;
    const timer = setTimeout(() => setApplyTimedOut(true), 15_000);
    return () => clearTimeout(timer);
  }, [accepted, applyResult]);

  // Kick the implementation-simulation round the first time the card is
  // all-green. The auto-prompt must reach the live sandbox agent (the only one
  // with the repo cloned); if the frame doesn't reach an OPEN socket we skip
  // straight to Accept rather than stranding the user on a loader.
  //
  // `flowTerminal` guards the reload: the replayed card is still all-green with
  // its (already consumed) token, so without it a reload after apply fired a
  // fresh plan+size turn and then showed the plan-validate screen for an issue
  // that was already written.
  const { sendReadinessPlanRequest } = ws;
  useEffect(() => {
    if (accepted || flowTerminal || fatal !== null) return;
    if (tail !== "idle") return;
    if (!(card?.allGreen && card.acceptToken)) return;
    // Resume, don't re-bill: if this issue's plan already ran (persisted in the
    // session + re-emitted on attach), advance off the existing result instead
    // of firing a fresh sandbox turn. Leaving to the projects and returning
    // used to re-run the whole 6-minute plan+size turn because `tail` is
    // component-local and resets to "idle" on remount.
    const pr = ws.planResult;
    // …but a plan the user REJECTED is not a result to resume onto. The
    // correction marker outlives this mount (sessionStorage) exactly so this
    // branch can see it: leaving the screen during the multi-minute re-run and
    // coming back otherwise lands here with `ws.planResult` still holding the
    // pre-correction plan, and it would be shown as the answer — with "Yes,
    // that's right" then binding a revision the server has superseded. Wait for
    // the re-run instead; the `plan_loading` deadline below is the backstop if
    // it never lands. A server that echoes no revision has nothing to compare,
    // so it resumes as before.
    //
    // …but ONLY while that re-run could still be running. See
    // `correctionExpired` for what a marker past its budget now refuses.
    const correctionExpired =
      planCorrection !== null &&
      Date.now() - planCorrection.requestedAtMs >= PLAN_LOADING_DEADLINE_MS;
    if (
      pr !== null &&
      pr.issueIid === iid &&
      pr.kind === "ready" &&
      planCorrection !== null &&
      planCorrection.fromRevision !== null &&
      pr.planRevision !== undefined &&
      pr.planRevision <= planCorrection.fromRevision
    ) {
      if (correctionExpired) {
        // WHAT THIS REFUSES, and whether it should:
        //  - a marker restored a full client backstop or more after its
        //    correction went out, with the rejected plan still the newest one
        //    on hand. The server hard-stops the tail turn at
        //    TAIL_TURN_WALL_CLOCK_MAX_MS (below this budget) and emits
        //    `readiness_plan_unavailable`, which is replay-worthy and survives
        //    eviction — so either that degrade arrived (then `pr` is not this
        //    stale `ready` and we are not here) or it was LOST with the live
        //    session, and nothing is coming. Refusing to wait is right.
        //  - a re-run that genuinely ran longer than the backstop: cannot
        //    happen; the server's ceiling sits below it, and the mounted
        //    deadline effect already gives up at exactly this age anyway.
        //  - a clock that jumps FORWARD (laptop wake, NTP step) mid-re-run:
        //    expires early, so the user reaches the degraded Accept — which
        //    carries the sizing retry — instead of a loader with no escape.
        //    That is the safe direction of the two.
        // A clock that jumps BACKWARD keeps waiting, i.e. the prior behaviour.
        //
        // We do NOT fall through to a plain `sendReadinessPlanRequest(iid)`
        // here: that re-runs the plan WITHOUT the correction text and then
        // presents the result as the answer to the correction. And we do not
        // resume onto `pr` either — it is still the plan the user rejected, and
        // "Yes, that's right" would confirm it. Consuming it and degrading is
        // the only honest end: Accept & apply writes no implementation section,
        // and the degrade note's Retry re-runs the round.
        focusDiag("plan_correction_expired", {
          iid,
          revision: pr.planRevision,
          correctedFrom: planCorrection.fromRevision,
          ageMs: Date.now() - planCorrection.requestedAtMs,
        });
        setPlanCorrection(null);
        consumedPlanRef.current = pr;
        setTailDegraded(true);
        setTail("accept");
        return;
      }
      focusDiag("plan_correction_resume_wait", {
        iid,
        revision: pr.planRevision,
        correctedFrom: planCorrection.fromRevision,
      });
      setTail("plan_loading");
      return;
    }
    // An expired marker with no rejected plan left to hold back is litter, and
    // litter that LIES: the loader copy below is keyed on `planCorrection !==
    // null`, so a fresh request fired past this point would say "Reworking this
    // with your correction" over a turn carrying no correction at all.
    if (correctionExpired) setPlanCorrection(null);
    if (pr !== null && pr.issueIid === iid && !breakdownCard.sizingStale) {
      consumedPlanRef.current = pr;
      // Resuming ONTO a plan settles any correction that was outstanding: the
      // marker has done its job, and leaving it in storage would keep a
      // superseded revision in the comparison for the rest of the tab's life.
      if (planCorrection !== null) setPlanCorrection(null);
      if (pr.kind === "ready") {
        setBusinessSummary(pr.businessSummary);
        setPlanRevision(pr.planRevision ?? null);
        setTail("validate");
      } else {
        setTailDegraded(true);
        setTail("accept");
      }
      return;
    }
    // Fresh size needed (first pass, or the criteria changed → stale sizing).
    // Remember which result was already on hand when we asked. A plan turn takes
    // minutes, so anything the consumer effect below sees before a NEW object
    // lands is by definition the one we just decided was not good enough — and
    // acting on it would advance the flow off a result we ourselves rejected.
    planAtRequestRef.current = ws.planResult;
    focusDiag("plan_request", { iid });
    const ok = sendReadinessPlanRequest(iid);
    // Freeze the pre-plan timeline before the plan turn streams in: every entry
    // that arrives after this is plan-era and gets suppressed from the prose box
    // + scrollback (see `preplanIdsRef`). Capture only when the request actually
    // went out — an unsent request never produces a plan turn to hide.
    if (ok && preplanIdsRef.current === null) {
      preplanIdsRef.current = new Set(ws.timeline.map((e) => e.id));
    }
    setTail(ok ? "plan_loading" : "accept");
  }, [
    card,
    tail,
    accepted,
    flowTerminal,
    fatal,
    iid,
    sendReadinessPlanRequest,
    ws.timeline,
    ws.planResult,
    breakdownCard.sizingStale,
    planCorrection,
    setPlanCorrection,
  ]);

  // The criteria changed after sizing ran (e.g. the piece elicitation turned one
  // big criterion into several) → the sizing is stale. Drop back to idle so the
  // trigger above re-runs the size against the new criteria instead of showing
  // the stale verdict.
  //
  // Do NOT null `consumedPlanRef` here. It used to, and that closed a hot loop:
  // nulling it un-consumes the STALE `ws.planResult`, so the requester below
  // fires, sets `plan_loading`, and the consumer effect immediately re-consumes
  // that same stale result and advances to `validate` — where this effect resets
  // to `idle` again. Measured live on dev issue #60: 138,137 request/ready pairs
  // in 205s (672/s, peak 2,820/s), in strict alternation with zero consecutive
  // duplicates. 129 of them escaped to the server, where the per-issue in-flight
  // cap absorbed them. Nulling was redundant anyway: the requester's resume
  // branch is already gated on `!sizingStale`.
  useEffect(() => {
    if (breakdownCard.sizingStale && (tail === "accept" || tail === "validate")) {
      setTail("idle");
    }
  }, [breakdownCard.sizingStale, tail]);

  // Consume the plan result while we're awaiting one. `ready` carries the
  // non-technical business summary → move to validation; `unavailable` (no live
  // sandbox, or the agent produced no plan) → fall through to Accept. Only acts
  // on a NEW result object, so a correction re-run waits for its revised plan.
  useEffect(() => {
    const pr = ws.planResult;
    if (pr === null || pr === consumedPlanRef.current) return;
    // The result that was already there when we asked is not an answer to the
    // ask. This is the structural guard: whatever else nulls `consumedPlanRef`,
    // a request can never be satisfied by the result it was raised against.
    if (pr === planAtRequestRef.current) return;
    // THE SERVER'S ANSWER TO A CONFIRM, which the gate below cannot see.
    //
    // A "Yes, that's right" leaves `validate` synchronously, so by the time the
    // server's refusal lands the tail is `sizing_wait`, `accept` or
    // `breakdown_review` and the `tail !== "plan_loading"` gate drops the frame.
    // That made every refusal reason silent — no_store, no_plan, plan_stale and
    // the revision mismatch alike — and the mismatch is not rare: the agent
    // keeps running through the tail turn, so a second
    // `propose_implementation_plan` can land while the user is still reading
    // the first summary. The issue was then written with no
    // `## Implementation details` and nothing on either side said so.
    //
    // STRICTLY GREATER, not merely different. `readiness_plan_ready` is
    // replay-worthy, and a reconnect replays every historical revision as its
    // own frame — so an inequality test would fire on the OLD revisions in the
    // log, throw a finished user back onto a validate screen showing a
    // superseded summary, and make their next Yes name the older plan. A real
    // supersede always carries a later `Date.now()`.
    const pendingConfirm = pendingPlanConfirmRef.current;
    if (
      pendingConfirm !== null &&
      pr.issueIid === iid &&
      // `applied`, not just `accepted`: an agent-driven MCP apply lands a
      // successful `readiness_apply_result` on a mount whose local accept latch
      // was never set, and nothing may re-open a validate screen for an issue
      // GitLab already has.
      !accepted &&
      !applied &&
      !flowTerminal &&
      Date.now() - pendingConfirm.atMs < PLAN_CONFIRM_ANSWER_WINDOW_MS &&
      (tail === "sizing_wait" || tail === "accept" || tail === "breakdown_review")
    ) {
      if (
        pr.kind === "ready" &&
        pr.planRevision !== undefined &&
        pr.planRevision > pendingConfirm.revision
      ) {
        focusDiag("plan_confirm_superseded", {
          iid,
          sent: pendingConfirm.revision,
          now: pr.planRevision,
          tail,
        });
        pendingPlanConfirmRef.current = null;
        consumedPlanRef.current = pr;
        // A live split proposal is not something to yank the user out of, and
        // the parent write is still ahead of them — latch the loss so the
        // Accept screen carries the note when they get there, and let the
        // apply-time `planDropReason` be the honest end if they go straight
        // through.
        if (tail === "breakdown_review") {
          setPlanConfirmLost("superseded");
          return;
        }
        setBusinessSummary(pr.businessSummary);
        setPlanRevision(pr.planRevision);
        setTail("validate");
        return;
      }
      // ONLY the reasons the confirm handler itself sends. The `ready` leg has
      // a strictly-greater revision to tell a replay from an answer; a degrade
      // frame carries no revision at all, so the reason IS the discriminator.
      // `readiness_plan_unavailable` is replay-worthy and survives eviction, so
      // without this a historical `plan_timeout` or `no_sandbox` from the plan
      // ROUND — replayed on a reconnect inside the window — was read as the
      // server refusing this confirm: it cancelled the sizing wait (the
      // resolver and its backstop both bail once the tail has moved), dropped
      // the split step, and consumed the frame so a real refusal landing after
      // it was dropped by the gate below. The two sets are disjoint by
      // construction — see `handleReadinessPlanConfirm`.
      if (pr.kind !== "ready" && PLAN_CONFIRM_DEGRADE_REASONS.has(pr.reason)) {
        focusDiag("plan_confirm_degraded", { iid, reason: pr.reason, tail });
        pendingPlanConfirmRef.current = null;
        consumedPlanRef.current = pr;
        // NOT `tailDegraded`: that latch's only copy says Loom couldn't finish
        // SIZING, which on this path is a false statement on a screen whose
        // sizing succeeded. What went wrong is the plan confirmation, and the
        // note has to say that.
        setPlanConfirmLost("unavailable");
        if (tail !== "breakdown_review") setTail("accept");
        return;
      }
    }
    if (pr.issueIid !== iid || tail !== "plan_loading") return;
    // A correction re-run is only answered by a NEWER revision. Deliberately
    // scoped to `kind === "ready"`: a `readiness_plan_unavailable` during the
    // re-run is a real degrade for THIS request and still falls through to
    // Accept, and a server that echoes no revision is judged as before (there
    // is nothing to compare) — `confirmPlan` already fails closed on that.
    // Not consumed, so the revised plan is still consumable when it lands; the
    // `plan_loading` deadline below remains the backstop if it never does.
    if (
      pr.kind === "ready" &&
      planCorrection !== null &&
      planCorrection.fromRevision !== null &&
      pr.planRevision !== undefined &&
      pr.planRevision <= planCorrection.fromRevision
    ) {
      focusDiag("plan_stale_revision_ignored", {
        iid,
        revision: pr.planRevision,
        correctedFrom: planCorrection.fromRevision,
      });
      return;
    }
    consumedPlanRef.current = pr;
    if (pr.kind === "ready") {
      focusDiag("plan_ready", { iid });
      setBusinessSummary(pr.businessSummary);
      setPlanRevision(pr.planRevision ?? null);
      setPlanCorrection(null);
      setTail("validate");
    } else {
      focusDiag("plan_unavailable", { iid, reason: pr.reason });
      setPlanCorrection(null);
      setTailDegraded(true);
      setTail("accept");
    }
  }, [ws.planResult, iid, tail, planCorrection, accepted, applied, flowTerminal]);

  // Client-side plan_loading deadline — the backstop for a LOST degrade frame.
  // The server arms its own `plan_timeout` (~90s), but if that
  // `readiness_plan_unavailable` is dropped on a reconnect window, the plan
  // round only ever leaves `plan_loading` on a `planResult` — so without this
  // the user hangs forever on "Working out how this would be built…", escapable
  // only by startOver (which discards the all-green card). Slightly longer than
  // the server deadline so a delivered server degrade wins normally; this just
  // catches the dropped-frame case and falls through to Accept. Re-armed on each
  // entry into plan_loading (incl. a correction re-run), cleared on exit.
  //
  // Measured from the REQUEST, not from this mount. A resumed correction marker
  // carries `requestedAtMs`, so a remount inside the re-run inherits what is
  // LEFT of the one backstop; arming a fresh full one on top of the time
  // already elapsed could hold this escape-less screen for nearly two of them,
  // which is worse than the stall the marker's liveness bound just closed.
  //
  // PAUSED while the plan turn's own question is pinned. The plan prompt sends a
  // point the user never decided to `ask_user` instead of into the plan, and the
  // server parks its deadlines until the answer (`parkPlanTailForAsk`): the wait
  // is on the user, so this backstop must not drop them to Accept mid-answer.
  // Once the question goes, the server re-arms a full wall clock, so this one
  // re-arms in full too — above it, as everywhere.
  const planAskPinned = current?.kind === "ask";
  const planAskPausedRef = useRef(false);
  useEffect(() => {
    if (tail !== "plan_loading") {
      planAskPausedRef.current = false;
      return;
    }
    if (planAskPinned) {
      planAskPausedRef.current = true;
      return;
    }
    const delay =
      planCorrection === null || planAskPausedRef.current
        ? PLAN_LOADING_DEADLINE_MS
        : Math.max(
            0,
            planCorrection.requestedAtMs + PLAN_LOADING_DEADLINE_MS - Date.now(),
          );
    const timer = setTimeout(() => {
      focusDiag("plan_loading_timeout", { iid });
      setPlanCorrection(null);
      setTailDegraded(true);
      setTail("accept");
    }, delay);
    return () => clearTimeout(timer);
  }, [tail, iid, planCorrection, setPlanCorrection, planAskPinned]);

  // The user said the business outcome doesn't match → send their clarification
  // and re-run the simulation; the revised plan re-opens the validation step.
  const submitCorrection = (text: string): void => {
    focusDiag("plan_correction", { iid, from: planRevision });
    // The SAME structural guard the requester effect arms, for the same reason:
    // the result already on hand when we ask is not an answer to the ask. It
    // was missing here, so a plan re-emitted between "Not quite" and this send
    // sat in `ws.planResult` and was consumed by the consumer effect the moment
    // this set `plan_loading` — measured at 9ms, straight back to the
    // pre-correction summary.
    planAtRequestRef.current = ws.planResult;
    // …and the revision bound, which covers a re-emit that lands DURING the
    // multi-minute re-run (a bridge re-attach in that window would otherwise
    // deliver the rejected plan as a brand-new object).
    setPlanCorrection({ fromRevision: planRevision });
    setTail("plan_loading");
    sendReadinessPlanRequest(iid, text);
  };

  // Re-run the sizing round from the Accept screen after it degraded. Before
  // this the degrade note was terminal: "Loom couldn't finish sizing this" with
  // nothing to press, on a screen the user reached precisely because a
  // multi-minute turn timed out or its frame was lost.
  //
  // The note deliberately STAYS on screen and `tailDegraded` is not cleared: the
  // re-run takes minutes, and a note that vanished on click would read as
  // "finished" and get clicked again. What changes is the control — disabled
  // while the turn is in flight, which is the client half of the plan-request
  // flood bound (the server's per-issue in-flight cap is the other half).
  // The degrade note, told apart by the reason the SERVER sent. A degrade
  // reached by the client's own deadline carries no frame and so no reason —
  // that, and any reason nobody has written copy for, keeps the generic line.
  const degradedNoteKey: MessageKey =
    ws.breakdownUnavailable?.issueIid === iid
      ? ws.breakdownUnavailable.reason === "no_sandbox" ||
        ws.breakdownUnavailable.reason === "route_error"
        ? "focus.refine.tail.degradedNoSandbox"
        : ws.breakdownUnavailable.reason === "breakdown_timeout"
          ? "focus.refine.tail.degradedTimeout"
          : ws.breakdownUnavailable.reason === "not_green" ||
              ws.breakdownUnavailable.reason === "no_card" ||
              ws.breakdownUnavailable.reason === "no_store"
            ? "focus.refine.tail.degradedNotGreen"
            : "focus.refine.tail.degradedNote"
      : "focus.refine.tail.degradedNote";

  const retrySizing = (): void => {
    if (planRetryInFlight) return;
    focusDiag("plan_retry", { iid });
    if (sendReadinessPlanRequest(iid)) setPlanRetryInFlight(true);
  };

  // Release the retry latch after the same deadline the plan round uses. It is
  // never released by a RESULT: a landed sizing unmounts the whole degrade block
  // (its `sizing === null` clause goes false), and a second degrade is
  // indistinguishable from the first — so a deadline is the only honest release.
  // Same shape as the post-accept split spinner's backstop.
  useEffect(() => {
    if (!planRetryInFlight) return;
    const timer = setTimeout(() => {
      focusDiag("plan_retry_timeout", { iid });
      setPlanRetryInFlight(false);
    }, PLAN_LOADING_DEADLINE_MS);
    return () => clearTimeout(timer);
  }, [planRetryInFlight, iid]);

  // Record the ONE user decision behind the implementation plan: "yes, this
  // business summary matches". Apply appends `## Implementation details` only
  // for a plan the server holds as confirmed, so without this frame the section
  // is simply omitted — model prose the browser never rendered stays out of the
  // issue.
  //
  // Called from the validate button's own onClick and NOWHERE else. Deliberately
  // NOT folded into `proceedFromValidate` below: that function is also the
  // Accept screen's split affordance (`splitRecommended ? proceedFromValidate :
  // forceSplit`) and is re-entered from `sizing_wait`, so "every branch of it
  // means the user said yes to a plan" is false — it would confirm plans nobody
  // validated.
  const confirmPlan = (): void => {
    // Same reason as `onAccept`: nothing may confirm a plan for an issue that
    // has already been written. Live, a reload after apply put the
    // plan-validate screen back on screen and its "Yes, that's right" sent a
    // real `readiness_plan_confirm` against the written issue.
    if (flowTerminal) return;
    if (planRevision === null) {
      // No revision to bind to (a server predating the echo). Fail CLOSED: the
      // plan section is dropped rather than confirmed against a revision we
      // cannot name — the degrade direction is always "less unvalidated prose".
      focusDiag("plan_confirm_skipped", { iid });
      return;
    }
    focusDiag("plan_confirm", { iid, revision: planRevision });
    // A fresh decision clears the previous one's verdict: the user is
    // re-validating (the bounce put them back on this screen), so the note
    // about the last confirm is no longer the state of the world.
    setPlanConfirmLost(null);
    // Arm the refusal listener BEFORE the send: the server answers on the same
    // socket and the Yes handler leaves `validate` synchronously, so the frame
    // can be in hand before this function returns.
    pendingPlanConfirmRef.current = { revision: planRevision, atMs: Date.now() };
    ws.sendReadinessPlanConfirm(iid, planRevision);
  };

  // Validate-yes → offer the split when the server's sizing policy recommended
  // one (or a proposal already landed); otherwise straight to Accept. The
  // breakdown is ADVISORY: every degrade path in the review effect below falls
  // through to the plain accept, never blocks it.
  //
  // The size verdict can still be IN FLIGHT here: plan and sizing are separate
  // events from the same tail turn, and sizing lags by minutes on big issues
  // (per-criterion explorer fan-out). Reading `sizing` at click time and falling
  // through would silently drop the split path for exactly the issues the
  // feature targets — the recommendation then surfaces too late, as a manual
  // offer on the Accept screen. Wait visibly instead; the sizing_wait effect
  // below makes this same decision the moment the verdict lands.
  const proceedFromValidate = (): void => {
    if (
      breakdownCard.sizing === null &&
      !breakdownCard.sizingUnavailable &&
      !breakdownCard.breakdownUnavailable
    ) {
      focusDiag("sizing_wait_enter", { iid });
      setTail("sizing_wait");
      return;
    }
    // A STALE verdict is not a "no". The server rebases the sizing map when the
    // user parks or restores a must-have and marks the verdict untrustworthy;
    // reading it as `splitRecommended === false` silently skipped the split step
    // for exactly the issues the feature targets. Go to Accept — where the split
    // affordance is offered with the honest note — rather than deciding.
    const verdictKnown = breakdownCard.sizing?.verdictStale !== true;
    const split =
      verdictKnown &&
      breakdownCard.sizing?.splitRecommended === true &&
      !breakdownCard.breakdownUnavailable &&
      breakdownCard.entry?.noSplit !== true;
    if (!split) {
      // Reaching here with NO sizing means a degrade already landed (the
      // pending case took the wait branch above) — surface the honest
      // degraded note on Accept instead of silently omitting the review.
      if (breakdownCard.sizing === null) setTailDegraded(true);
      setTail("accept");
      return;
    }
    if (breakdownCard.entry === null) {
      focusDiag("breakdown_request", { iid });
      breakdownCard.requestBreakdown();
    }
    setTail("breakdown_review");
  };

  // Resolve sizing_wait: the deferred proceedFromValidate decision. A landed
  // verdict decides split-vs-accept exactly as an in-hand one would have; a
  // sizing degrade falls through to Accept with the honest degraded note.
  // Guarded like the trigger effect: once the user applied (the wait screen's
  // "Apply now"), hit the budget wall, or the flow is fatal, a late verdict
  // must NOT auto-fire a billed breakdown turn the user never asked for —
  // the post-accept split stays behind its explicit consent offer.
  const { sendReadinessBreakdownRequest } = ws;
  useEffect(() => {
    if (tail !== "sizing_wait") return;
    if (accepted || flowTerminal || fatal !== null || ws.budgetExhausted !== null)
      return;
    if (breakdownCard.sizing !== null) {
      // Same rule as `proceedFromValidate`: a verdict the server has marked
      // stale is not a decision, so it must not auto-skip the split step.
      const split =
        breakdownCard.sizing.verdictStale !== true &&
        breakdownCard.sizing.splitRecommended === true &&
        !breakdownCard.breakdownUnavailable &&
        breakdownCard.entry?.noSplit !== true;
      if (!split) {
        setTail("accept");
        return;
      }
      if (breakdownCard.entry === null) {
        focusDiag("breakdown_request", { iid });
        sendReadinessBreakdownRequest(iid);
      }
      setTail("breakdown_review");
      return;
    }
    if (breakdownCard.sizingUnavailable || breakdownCard.breakdownUnavailable) {
      focusDiag("sizing_wait_unavailable", { iid });
      setTailDegraded(true);
      setTail("accept");
    }
  }, [
    tail,
    accepted,
    flowTerminal,
    fatal,
    ws.budgetExhausted,
    breakdownCard.sizing,
    breakdownCard.sizingUnavailable,
    breakdownCard.breakdownUnavailable,
    breakdownCard.entry,
    sendReadinessBreakdownRequest,
    iid,
  ]);

  // Lost-frame backstop for sizing_wait, in its OWN effect on identity-stable
  // deps (mirrors the plan_loading backstop). It must not live in the decision
  // effect above: the elapsed counter re-renders every second and any unstable
  // dep there would clear + re-arm the deadline each tick — a timer that can
  // never fire, exactly when a lost `readiness_sizing_unavailable` frame is
  // the only thing this deadline exists to rescue.
  useEffect(() => {
    if (tail !== "sizing_wait") return;
    const timer = setTimeout(() => {
      focusDiag("sizing_wait_timeout", { iid });
      setTailDegraded(true);
      setTail("accept");
    }, SIZING_WAIT_DEADLINE_MS);
    return () => clearTimeout(timer);
  }, [tail, iid]);

  /**
   * A split proposal exists, is a real split, and has not yet been executed.
   *
   * (see `splitCommitReachable` for why the terminal screens consult it)
   *
   * `reemitPersistedSizingAndBreakdown` restores any `status:"proposed"`
   * breakdown on every bridge attach and the client latches it — but the card
   * only renders under the `breakdown_review` tail, so on the Accept screen a
   * live proposal was invisible AND (because the affordance was gated on
   * `entry === null`) blocked the only way back to it. This is what makes the
   * Accept screen able to offer the proposal it is already holding, at no turn.
   */
  const splitProposalLive =
    breakdownCard.entry !== null &&
    !breakdownCard.entry.noSplit &&
    breakdownCard.phase === "review";

  // Backing out of the split: the flow carries on to the plain Accept, and the
  // proposal stops being something the applied screen may offer to execute.
  const declineSplit = (): void => {
    setSplitDeclined(true);
    setTail("accept");
  };

  // User-forced split from the Accept preview (the policy didn't recommend one).
  const forceSplit = (): void => {
    focusDiag("breakdown_force", { iid });
    breakdownCard.requestBreakdown(true);
    setTail("breakdown_review");
  };

  // "Big but one criterion": hand the agent a piece-elicitation directive. It
  // asks for the distinct pieces (with a done-condition each) and re-refines the
  // acceptance criteria into them — which re-opens the criteria (card drops out
  // of allGreen, flow returns to the grind). When it re-greens with several
  // criteria the sizing is stale (see the effect above) → it re-sizes → the
  // split becomes available. No AC-framing, no wasted forced-split turn.
  const requestPieceElicitation = (): void => {
    focusDiag("breakdown_piece_elicit", { iid });
    ws.send(buildPieceElicitationMessage(), {
      scope: sendScope,
      flow: { issueIid: iid },
    });
  };

  // Split AFTER the parent was already applied (F6 accept-race recovery). Force
  // only when the policy didn't recommend; both authorize via the pending marker.
  const requestSplitFromAccepted = (): void => {
    focusDiag("breakdown_post_accept", { iid });
    if (breakdownCard.requestBreakdown(breakdownCard.sizing?.splitRecommended !== true)) {
      setPostAcceptSplitRequested(true);
      setSplitEngaged(true);
    }
  };

  // Keep a POST-ACCEPT split as one ticket, from the escape footer under a
  // split-time question. The parent issue is already written and the proposal is
  // on screen beside the question, so this is the landed card's own decision —
  // there is no "apply as is" leg to mirror.
  //
  // ONE frame, not two: the keep-single sentence IS the answer to the question
  // the agent is blocked on, so it rides the structured `question_answer` that
  // closes that ask. A decline followed by a separate `send` would be two user
  // turns, and the second SIGTERM-kills the turn the first just started.
  const declineSplitFromAccepted = (
    ask: Extract<Step, { kind: "ask" }>,
  ): void => {
    const text = buildKeepSingleMessage(iid);
    focusDiag("breakdown_decline_post_accept", { iid, askId: ask.askId });
    setPostAcceptSplitDismissed(true);
    answerAskDirect(ask, text);
  };

  // The SAME decision, PRE-accept, from the escape footer under a split-time
  // question that preempted the `breakdown_review` screen.
  //
  // The card stopped sending the keep-single sentence itself, and three hosts
  // were updated to send it explicitly — this footer was not, so the button that
  // reads "Keep as one ticket" set two local flags and told the model nothing:
  // it kept proposing, and the next card could put the split back on screen.
  //
  // ONE lane, for the reason `declineSplitFromAccepted` states: an ask is open,
  // so the sentence rides that ask's answer rather than a second user turn. Here
  // that is the BATCHED lane (`answerAsk`) — pre-accept the flush owns the wire,
  // and a bare `ws.send` beside a held batch is the double-turn this surface
  // spends its guards avoiding.
  const declineSplitFromAsk = (ask: Extract<Step, { kind: "ask" }>): void => {
    focusDiag("breakdown_decline_from_ask", { iid, askId: ask.askId });
    answerAsk(ask, buildKeepSingleMessage(iid));
    declineSplit();
  };

  // Confirm the split: ONE frame — the server applies the parent card first
  // (idempotent) then creates the children (receipts-resume). `accepted` takes
  // over the screen; the BreakdownCard rides it with per-child progress.
  //
  // The token's absence is a REACHABLE state, not an impossibility: park/unpark
  // strip the token trio as their first act and `runCardMethodology` re-mints
  // asynchronously. The screen used to be gated on the token too, so
  // this guard was unreachable — but that gate had to go, because parking a
  // must-have made the one surface offering "don't build this at all" vanish the
  // instant it was used. The gate moved off the screen and was never put back
  // onto the button, which left a confirm that renders and does nothing. Live:
  // the press created nothing, sent no frame, showed nothing, and
  // `__loomFocusDiag` carried no row that could say why.
  //
  // Two halves. The card disarms Confirm and says WHY when no token reaches it,
  // so the token is handed over only when it is one — the hosts used to forward
  // any DEFINED value, and the card's own test is `!== undefined`, so an empty
  // string satisfied the card and failed this guard: an enabled confirm over a
  // handler that refuses, which is the exact shape being fixed. The diag row is
  // what proves it if the two ever disagree again.
  const onBreakdownConfirm = (): void => {
    if (!card?.acceptToken) {
      focusDiag("breakdown_confirm_blocked", { iid, reason: "no_accept_token" });
      return;
    }
    focusDiag("breakdown_confirm", { iid });
    breakdownCard.acceptBreakdown(card.acceptToken);
    setApplyTimedOut(false);
    setSplitEngaged(true);
    setAccepted(true);
  };

  // Degrades while awaiting the proposal: an unavailable frame or the client
  // deadline (backstop for a lost degrade) falls through to the plain Accept.
  useEffect(() => {
    if (tail !== "breakdown_review") return;
    if (breakdownCard.entry !== null && !breakdownCard.entry.noSplit) return;
    if (breakdownCard.breakdownUnavailable || breakdownCard.entry?.noSplit === true) {
      focusDiag("breakdown_unavailable_fallthrough", { iid });
      if (breakdownCard.breakdownUnavailable) setTailDegraded(true);
      if (breakdownCard.entry?.noSplit === true) setNoSplitAffirmed(true);
      setTail("accept");
      return;
    }
    const timer = setTimeout(() => {
      focusDiag("breakdown_review_timeout", { iid });
      setTailDegraded(true);
      setTail("accept");
    }, BREAKDOWN_REVIEW_DEADLINE_MS);
    return () => clearTimeout(timer);
  }, [tail, breakdownCard.entry, breakdownCard.breakdownUnavailable, iid]);

  // Backstop for the POST-ACCEPT split request (F6): the accepted screen's
  // "drafting…" spinner rides `postAcceptSplitRequested`, which never sets `tail`
  // so the breakdown_review deadline above doesn't cover it. A lost degrade frame
  // (transient, not reconnect-re-emitted) would otherwise strand the spinner
  // forever — revert to the offer after the same deadline so the user can retry.
  // ...and it LATCHES the degrade, exactly as the `unavailable` effect below
  // does. Clearing the flag alone put the offer back with no word about the
  // press that came before it: `canOfferSplit` is still true here (nothing
  // landed), so the screen silently rewound to the button. Live that read as a
  // split that was still drafting eleven minutes later, because the only thing
  // that had changed on screen was a spinner going away.
  useEffect(() => {
    if (!postAcceptSplitRequested) return;
    const timer = setTimeout(() => {
      // Only claim a degrade for a request NOTHING answered. The release effect
      // below clears the flag the moment a proposal lands, so this guard is the
      // belt for an ordering race — and it is the one that makes the note a
      // fact rather than a timer's opinion.
      const answered = breakdownCard.entry !== null;
      focusDiag("breakdown_post_accept_timeout", { iid, answered });
      setPostAcceptSplitRequested(false);
      if (!answered) setPostAcceptDegraded(true);
    }, BREAKDOWN_REVIEW_DEADLINE_MS);
    return () => clearTimeout(timer);
  }, [postAcceptSplitRequested, breakdownCard.entry, iid]);

  // ANY proposal answers the request — a real partition as much as a
  // "no split needed". The flag was only released on the `noSplit` leg, so a
  // successful split left it set for the rest of the session and the deadline
  // below eventually fired on a request that had been answered in twelve
  // seconds. Live on #103 that printed "Loom couldn't work out a split this
  // time. Your issue is applied and unchanged." under the card showing the two
  // sub-issues it had just created.
  //
  // The `noSplit` leg additionally LATCHES the verdict, because every surface
  // that could have shown it disappears the instant it lands: `canOfferSplit`
  // requires `entry === null`, so the offer AND its spinner unmount, and
  // `BreakdownCard` renders null for a `noSplit` entry. The user pressed the
  // button and got silence — observed live on a `noSplit:true, children:0`
  // proposal that the server delivered correctly. A real partition needs no
  // latch: the card itself is the answer.
  useEffect(() => {
    if (!postAcceptSplitRequested) return;
    if (breakdownCard.entry === null) return;
    const noSplit = breakdownCard.entry.noSplit === true;
    focusDiag(noSplit ? "breakdown_post_accept_no_split" : "breakdown_post_accept_ready", {
      iid,
    });
    setPostAcceptSplitRequested(false);
    if (noSplit) setPostAcceptNoSplit(true);
  }, [postAcceptSplitRequested, breakdownCard.entry, iid]);

  // ...and the OTHER way `canOfferSplit` goes false while the spinner is up:
  // `readiness_breakdown_unavailable`. `entry === null` and
  // `!breakdownUnavailable` are two separate clauses of the same condition, so
  // latching only the first left the identical silence reachable through the
  // second — which is the branch that actually fired live on dev (#59, seq 194:
  // the request was answered with `unavailable`, never with a `noSplit`
  // proposal). The timeout backstop below does not cover it either: it clears
  // `postAcceptSplitRequested`, but `canOfferSplit` stays false, so the offer
  // cannot come back to explain itself. Latch the degrade so the user learns
  // the split did not happen and the issue is untouched.
  useEffect(() => {
    if (!postAcceptSplitRequested) return;
    if (!breakdownCard.breakdownUnavailable) return;
    focusDiag("breakdown_post_accept_unavailable", { iid });
    setPostAcceptSplitRequested(false);
    setPostAcceptDegraded(true);
  }, [postAcceptSplitRequested, breakdownCard.breakdownUnavailable, iid]);

  // A live "still working" signal for the silent tail-loading screens (plan
  // simulation / sizing / breakdown drafting): no prose streams there, so an
  // elapsed counter is the only feedback that the multi-minute turn is alive.
  // It counts the SERVER's busy stretch (`tailElapsedSec`, below), so this only
  // re-renders it once a second.
  const tailLoading =
    tail === "plan_loading" ||
    tail === "sizing_wait" ||
    (tail === "breakdown_review" &&
      (breakdownCard.entry === null || breakdownCard.entry.noSplit));
  const [, setTailTick] = useState(0);
  useEffect(() => {
    if (!tailLoading) return;
    const timer = setInterval(() => setTailTick((n) => n + 1), 1000);
    return () => clearInterval(timer);
  }, [tailLoading]);

  // "Open #N" always goes to the issue on GitLab — never Loom's own editor. The
  // freshest URL is the apply result's (written this turn); otherwise the URL the
  // issue carried into focus. Falls back to the in-app route only if neither is
  // known (a created issue whose webUrl the server didn't return).
  const openIssue = (): void => {
    const url = applyResult?.webUrl ?? issueWebUrl;
    if (url) window.open(url, "_blank", "noopener,noreferrer");
    else onOpenIssue(iid);
  };

  /* ── chrome state ─────────────────────────────────────────────────── */
  const progress = (accepted || flowTerminal)
    ? 1
    : tailLoading
      ? // The card is green (grounding is done) but the flow isn't — a full bar
        // here reads "finished" during a multi-minute wait. Hold it near-complete.
        0.9
      : card
        ? cardProgress(card).fraction
        : typedAsks.length > 0
          ? // Pre-card, but the agent is far enough in to be asking — a bar
            // still pinned at "just connected" understates that.
            0.15
          : ws.status === "open"
            ? 0.08
            : 0.04;
  const budgetDead = ws.budgetExhausted !== null;
  const toolsDead = ws.toolsUnavailable !== null;
  /**
   * The session is OVER: no frame this screen sends will be answered again.
   *
   * `fatal` already takes the whole screen for the two server refusals. The
   * idle hard-kill deliberately does not — it leaves the flow rendered under
   * the "Session closed" dialog, because it is resumable in place and the work
   * on screen is the thing being resumed. That is exactly why the live surfaces
   * underneath have to be told: the split card stayed fully interactive there —
   * ⋯ menus, "+ Add a piece", Confirm, Decline — with the header reading
   * "thinking…" and the commit caption promising a re-check on a socket that
   * was gone.
   */
  const sessionClosed = ws.status === "idle_killed" || isSessionDead(ws.status);
  /**
   * The user is looking at a split they can still commit WITHOUT a sandbox.
   *
   * `readiness_breakdown_accept` is server-direct — the server reconstructs
   * every GitLab write from the persisted `CardState` — exactly like
   * `readiness_accept`, which is why the terminal screens below offer
   * "apply anyway". The same argument holds here, and the stake is larger: the
   * pieces are already drafted, adjusted and read. A terminal screen that
   * outranks this one would hide a working Confirm behind a dead end.
   */
  const splitCommitReachable = tail === "breakdown_review" && splitProposalLive;
  /**
   * A tail screen whose next move is the USER's, with no turn in flight.
   *
   * The pill once read `showLoader`, the generic "no visible `activitySig`
   * change for 10s" stall timer, which cannot tell a silent agent grind from a
   * user reading a screen: 10s of reading flipped it to "thinking…" while the
   * flow's own debug line (`ws.agentBusy`) still said idle. It reads
   * `loomWorking` now, and this screen stays excluded on top of it. The plan-confirm and its correction box
   * were already excluded as literals; the LANDED split proposal is the same
   * screen one step later — its render body shows no ThinkingDots either (only
   * the drafting branch does).
   *
   * `tailLoading` is exactly "the silent tail screens where a turn IS in
   * flight", so the drafting / post-Decline `breakdown_review` keeps its
   * "thinking…". `!ws.agentBusy` is the narrow half: an ADJUST turn (free text,
   * "Adjust the split…", split-further) runs with the card still on screen, and
   * there the pill SHOULD read "thinking…" — `BreakdownCard` is handed
   * `agentBusy` for precisely that state.
   */
  const tailDecisionScreen =
    tail === "validate" ||
    tail === "correct" ||
    (tail === "breakdown_review" && !tailLoading && !ws.agentBusy);
  // The page's OWN socket — the one fact it knows first-hand. "Connection
  // lost" is only true of a socket this mount HAD: the first connect starts at
  // `connecting`, and nothing was lost then.
  const hadOpenRef = useRef(false);
  if (ws.status === "open") hadOpenRef.current = true;
  const offlineSettled = useHeldFor(
    hadOpenRef.current && (ws.status === "closed" || ws.status === "connecting"),
    OFFLINE_GRACE_MS,
  );
  // …and a first connect that never opens is its own fact: without this line a
  // socket that cannot reach Loom left the page on "Loom is waking up…" and a
  // spinner for good. Only once there IS a session to connect to — while
  // `POST /sessions` is out the hook holds no socket and reads `closed`.
  const unreachableSettled = useHeldFor(
    session.sessionId !== null &&
      !hadOpenRef.current &&
      (ws.status === "closed" || ws.status === "connecting"),
    FIRST_CONNECT_GRACE_MS,
  );
  // Either one is said on EVERY screen, pill included. It used to be a line in
  // the wait line only, which the question, grind and tail screens do not
  // draw: live on #280 and #283 (dev, Playwright offline) the page read
  // "thinking…" and "Working… 240s" for minutes after the server had gone idle
  // with a question waiting, and never said the connection was gone.
  const socketFact: "offline" | "unreachable" | null =
    isSessionDead(ws.status) || ws.status === "idle_killed"
      ? null
      : offlineSettled
        ? "offline"
        : unreachableSettled
          ? "unreachable"
          : null;
  // The tail screens' "Working… Ns" is the SERVER's busy stretch, said only
  // while the server says busy and the page can hear it. It used to count from
  // this mount: a reload started it again at 0 a minute into the plan turn (dev
  // #286, "Working… 6s"), and over a dropped socket it counted on to "Working…
  // 240s" while the server had been idle since 15:55:56 with a question
  // waiting (#283).
  const tailBusySinceMs = ws.agentWork?.busySinceMs ?? null;
  const tailElapsedSec =
    tailLoading && ws.agentBusy && socketFact === null && tailBusySinceMs !== null
      ? Math.max(0, Math.floor((Date.now() - tailBusySinceMs) / 1000))
      : 0;
  /**
   * A turn is in flight: the one thing "thinking…" may claim.
   *
   * The pill used to read `showLoader || thinking`, and neither of those is a
   * turn signal. `showLoader` is "no visible change for 10s": a chip row or a
   * card landing mid-turn reset it to "live", and ten seconds of reading after
   * a local-only OK (the answer HELD, nothing sent) set it to "thinking…".
   * `thinking` is "the epoch has not moved since we sent": a card the model
   * proposes mid-turn moves it, so it ends before the turn does. Live on #82
   * the pill went live → thinking → live → thinking → live inside one turn; on
   * #83 it read "live" 56s before turn_end, and "thinking…" with no turn at all.
   *
   * `ws.agentBusy` is the server's own turn edge (`agent_turn`: prompt forwarded
   * → `loom_stream_end`), and it holds for the length of a turn. `thinking`
   * stays as the bridge between our send and that edge; `tailLoading` covers
   * the tail screens that wait on a round of their own. `showLoader` still
   * drives the body's dots. It no longer speaks for the header.
   */
  const loomWorking = ws.agentBusy || thinking || tailLoading;
  const status: FocusStatus = fatal
    ? { tone: "bad", label: t("focus.status.stopped") }
    : accepted || flowTerminal
      ? applyResult?.ok
        ? { tone: "live", label: t("focus.status.applied") }
        : applyResult
          ? { tone: "bad", label: t("focus.status.applyFailed") }
          : { tone: "live", label: t("focus.status.applying") }
      : sessionClosed
        ? // Below `accepted` (an issue that was written stays written) and above
          // every "working on it" label: live, the pill read "thinking…" over a
          // session the idle timer had already closed.
          { tone: "bad", label: t("focus.status.sessionClosed") }
      : sandboxDead
        ? // A DEAD sandbox outranks every working label below it. The phase is
          // only consulted through `!live`, and `live` is satisfied by
          // `card !== null` alone — deliberately, so a warm card keeps the flow
          // usable across a reconnect — so once ANY card has landed the phase
          // stops being read at all and `loomWorking` wins the
          // ladder. Live: the GitLab job hit its 1h `activeDeadlineSeconds`
          // (`job_execution_timeout`, 3601s), the debug panel read `sandbox
          // terminated · agent idle`, and the pill still read "thinking…".
          // Placed with the other terminal states, below `accepted` (an issue
          // that was written stays written) and above `toolsDead` (a sandbox
          // that is gone is the stronger fact than the tools it can't reach).
          { tone: "bad", label: t("focus.status.stopped") }
        : toolsDead
        ? { tone: "bad", label: t("focus.status.stopped") }
        : budgetDead
        ? { tone: "bad", label: t("focus.status.budgetStopped") }
        : socketFact === "offline"
        ? // A socket that is down cannot hear a turn end: "thinking…" over it
          // is a guess, and "live" is not true either.
          { tone: "work", label: t("focus.status.reconnecting") }
        : socketFact === "unreachable"
        ? { tone: "work", label: t("focus.status.connecting") }
        : !live
          ? { tone: "work", label: phaseLabel(ws.sandbox.phase) ?? t("focus.status.starting") }
          : // The plan-confirm ("does this match?") and its "Not quite" correction
            // box are pure client-side decision points — no turn is in flight, and
            // their OWN render bodies already know it (neither shows ThinkingDots,
            // unlike `sizing_wait`/`breakdown_review` beside them). `showLoader` /
            // `thinking` don't know that: `showLoader` is the generic "no visible
            // activitySig change for 10s" stall timer, and a screen the user is
            // simply reading trips it exactly like a silent agent grind would.
            // Live: the pill read "thinking…" on both screens while the flow's own
            // debug line read `agent idle / your turn` — the ONE signal that tells
            // a user whether to wait or act, disagreeing with the source that knows.
            // See `tailDecisionScreen` for the third screen with that property,
            // and `loomWorking` for why the stall timer no longer decides it.
            loomWorking && !tailDecisionScreen
            ? { tone: "work", label: t("focus.status.thinking") }
            : { tone: "live", label: t("focus.status.live") };

  // The composer stays ENABLED throughout — during a 30-60s boot it is genuinely
  // the only way to add context, and `sendFree` binds the flow + sends byte-exact
  // text. But with NO question on screen the default "Or tell Loom in your own
  // words…" reads as an invitation to answer one, which is precisely how an
  // unattributable answer gets typed. Swap it for a context-adding prompt there;
  // when a step IS pinned the "or" has its antecedent and stays.
  // The second send exists only while something IS pinned: with no question on
  // screen the primary send already claims nothing, so "doesn't answer the
  // question" would name a question that isn't there.
  //
  // FOLDED while Loom's answer is on screen closed (and on a concern, and on a
  // choice whose option carries Loom's mark): then no text box is open anywhere
  // on the screen, and the bar is one "Tell Loom something else" link that opens
  // a NOTE-only bar. The plan-confirm screen already drops the bar; this keeps a
  // way to say something without any of it being taken as the answer.
  //
  // The fold never flips while the bar holds text or the caret: the form on
  // screen is kept until the user has sent or cleared it and moved away. Focus is
  // re-read from the DOM, so a bar that unmounted with the caret in it (the
  // plan-confirm screen drops the bar) does not keep claiming it.
  const renderMessageBar = (wantFolded: boolean): ReactNode => {
    const caretInBar =
      barFocused &&
      typeof document !== "undefined" &&
      document.activeElement?.closest(".focus-msgbar") != null;
    const holding = barText !== "" || caretInBar;
    const folded =
      holding && barFoldedRef.current !== null ? barFoldedRef.current : wantFolded;
    barFoldedRef.current = folded;
    return folded ? (
      <NoteLinkComposer
        onSendNote={(text) => sendFree(text, "comment")}
        disabled={ws.status !== "open"}
        value={barText}
        onValueChange={setBarText}
        onFocusChange={setBarFocused}
      />
    ) : (
      <MessageBar
        onSend={(text) => {
          sendFree(text);
          setBarText("");
        }}
        disabled={ws.status !== "open"}
        value={barText}
        onValueChange={setBarText}
        onFocusChange={setBarFocused}
        {...(current === null
          ? { placeholder: t("focus.refine.composer.addContext") }
          : {
              secondary: {
                label: t("focus.refine.composer.asComment"),
                onSend: (text: string) => {
                  sendFree(text, "comment");
                  setBarText("");
                },
              },
            })}
      />
    );
  };
  // Set by the step branch that rendered: whether the composer folds. The
  // pinned ask's rendering (computed ahead of the ladder) parks its answer in
  // `askFolded`, and only the ask branch of the ladder adopts it.
  let composerFolded = false;
  let askFolded = false;
  const reportClosed = (key: string, closed: boolean): void =>
    setClosedReport((prev) =>
      prev !== null && prev.key === key && prev.closed === closed
        ? prev
        : { key, closed },
    );
  /**
   * Does this step fold the composer? A text or list answer folds it while the
   * widget shows it CLOSED (its own report; until the first report lands, the
   * widget mounts closed exactly when it has an answer and no text of the
   * user's). A choice folds it only while an option actually carries Loom's
   * mark, and a draft that names no option leaves the full bar.
   *
   * A MILESTONE step always folds it, whatever Loom drafted: the milestone is
   * picked from the list or skipped, and the bar under it only ever sends a
   * note (`sendFree`). Folded, the bar is the "Tell Loom something else" note
   * link, so the screen says what typing there does.
   */
  const foldsComposer = (
    key: string,
    field: string,
    answer: LoomAnswer | null,
  ): boolean => {
    if (field === "milestone") return true;
    if (answer === null) return false;
    if (answer.kind === "choice") {
      if (field !== "priority") return false;
      const pick = answer.value.replace(/^[^:]+:\s*/u, "").trim().toLowerCase();
      return PRIORITY_OPTIONS.some(
        (o) =>
          o.value.trim().toLowerCase() === answer.value.trim().toLowerCase() ||
          o.label.trim().toLowerCase() === pick,
      );
    }
    return closedReport !== null && closedReport.key === key ? closedReport.closed : true;
  };

  /* ── screen selection ─────────────────────────────────────────────── */
  const heldCount = answers.size;
  // A different step pinned since the note went out: the note was sent about
  // something that is no longer on screen, so its receipt goes. The question it
  // was a comment on keeps it; a waiting screen (`current === null`) keeps it.
  useEffect(() => {
    if (lastNote === null || current === null) return;
    if (stepKeyOf(current) !== lastNote.pinnedKey) setLastNote(null);
  }, [current, lastNote]);
  // …and any act on a step since — an answer, a skip, a keep — retires it too.
  // While the note's own turn is still running, answering the pinned question
  // leaves `current` null rather than pinning the next one, so the rule above
  // never sees the user move on; the waiting screen would keep saying Loom is
  // reading the note over an answer the user just gave. Every one of those acts
  // sets `lastAnswered`, and `sendFree` clears it in the same batch it records
  // a note, so a note never retires itself.
  useEffect(() => {
    if (lastAnswered !== null) setLastNote(null);
  }, [lastAnswered]);
  const noteShown =
    lastNote !== null &&
    (current === null || stepKeyOf(current) === lastNote.pinnedKey)
      ? lastNote
      : null;
  // The Figma chip stays under the step, where its Connect and Allow live, and
  // on a real question that is below the fold. The receipt above the composer
  // offers the way to it while it is out of view. A callback ref, because the
  // step column is keyed and remounts the anchor on every step.
  //
  // Starts "in view" so an observer's first report decides and the offer never
  // flashes up for a chip that is on screen. With no IntersectionObserver the
  // offer simply stays available.
  const [figmaAnchor, setFigmaAnchor] = useState<HTMLDivElement | null>(null);
  const [figmaInView, setFigmaInView] = useState(true);
  useEffect(() => {
    if (figmaAnchor === null) return;
    if (typeof IntersectionObserver === "undefined") {
      setFigmaInView(false);
      return;
    }
    setFigmaInView(true);
    const observer = new IntersectionObserver(
      (entries) => {
        const last = entries[entries.length - 1];
        if (last !== undefined) {
          setFigmaInView(last.isIntersecting && last.intersectionRatio >= 0.5);
        }
      },
      { threshold: [0, 0.5, 1] },
    );
    observer.observe(figmaAnchor);
    return () => observer.disconnect();
  }, [figmaAnchor]);
  // Only on request: a scroll of its own would take the question off screen.
  // Focus follows, so a keyboard user lands on the chip's actions rather than
  // back in the composer.
  const showFigmaChip = (): void => {
    if (figmaAnchor === null) return;
    const reduced =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (typeof figmaAnchor.scrollIntoView === "function") {
      figmaAnchor.scrollIntoView({ behavior: reduced ? "auto" : "smooth", block: "nearest" });
    }
    figmaAnchor.focus({ preventScroll: true });
  };
  // This flow has already taken work from the user, even though no card exists
  // yet — the typed pre-card ask channel runs entirely in the `!card` branch, so
  // "answered a question and waiting" and "booting" are the same screen there
  // unless something tells them apart. Any user act counts: an answered ask
  // (`handledAskIds`), a flushed batch (`flushedAt`), a held or skipped field,
  // or a turn we are waiting on (`pending`). Latch-shaped by construction —
  // every term is monotonic within a mount (and all but `provided` are cleared
  // by `/reset`, which genuinely is a fresh start).
  const engagedPreCard =
    handledAskIds.size > 0 ||
    flushedAt.size > 0 ||
    provided.size > 0 ||
    declined.size > 0 ||
    answers.size > 0 ||
    pending !== null;
  // An ask target the SERVER has already resolved (superseded, carded, or
  // grounded from elsewhere — see R2-E: a field can green from the model's own
  // read of the seed while the user's reply is still sitting HELD, unflushed)
  // has nothing left to undo BACK TO. `undoLast` re-pins the ask via
  // `setCurrent(step)`, but the reconciliation effect (`ask_resolved`,
  // above) releases any pinned ask that has dropped out of `typedAsks` —
  // unconditionally, the same render pass — so a resolved ask's Undo produced
  // exactly one visible effect: the Undo button itself disappearing. Live:
  // `undo{key:"role"}` → `pin_released{reason:"ask_resolved"}` →
  // `current_set` right back to the question already on screen. A plain
  // "question"/"review" step doesn't share this failure — `undoLast` clears
  // `provided` for the field in the SAME commit, which is what the card-driven
  // release path is gated on, so it can't fire against its own undo.
  //
  // Read against the RAW timeline, not `typedAsks`: `typedAsks` is masked by
  // `handledAskIds`, which the ask being undone is ALWAYS in the instant it's
  // answered or declined (that's the point — it's what keeps it from re-pinning
  // on its own) — checking `typedAsks` here would withhold Undo on every
  // ordinary, still-live ask too, not only a genuinely resolved one.
  const undoTargetStep = lastAnswered?.step ?? null;
  const undoTargetLive =
    undoTargetStep === null ||
    undoTargetStep.kind !== "ask" ||
    ws.timeline.some(
      (e) =>
        e.kind === "agent_ask" &&
        e.issueIid === iid &&
        e.askId === undoTargetStep.askId &&
        !e.resolved &&
        e.question.trim() !== "",
    );
  // The one-shot undo affordance is live only while the last answer is still
  // HELD (not flushed) AND the flow is in its active question/held/thinking body
  // — never on a terminal screen, where pulling an answer back is meaningless.
  const canUndo =
    lastAnswered !== null &&
    undoTargetLive &&
    // A skip's window is the record itself (any later act overwrites it); an
    // answer's is the answer still being HELD, i.e. not yet on the wire; a
    // declined ask's is its id still sitting in `pendingDeclines`.
    (lastAnswered.skipped === true ||
      (lastAnswered.declinedAskId !== undefined &&
        pendingDeclines.has(lastAnswered.declinedAskId)) ||
      answers.has(lastAnswered.key)) &&
    !accepted &&
    !flowTerminal &&
    fatal === null &&
    !budgetDead &&
    !toolsDead &&
    session.error === null;
  // The parked-flow notice. Suppressed while a question is on screen: that
  // screen's whole premise is one thing at a time, and a second issue's number
  // competing with the question in front of the user is the wrong trade. Boot,
  // thinking and card screens carry it — which is where a switch lands anyway.
  //
  // No dismiss control on purpose. It would be mount-local state that a
  // reconnect-driven remount resets, i.e. a promise the surface cannot keep; the
  // bar already self-clears when that flow is finished or applied, which is the
  // honest control.
  const parkedBar =
    parkedFlow === null || current !== null || accepted || flowTerminal ? null : (
      <div className="focus-parked" role="status">
        <span className="focus-parked-text">
          {parkedFlow.resumable
            ? t("focus.refine.parked.note", { iid: parkedFlow.iid })
            : t("focus.refine.parked.noteAsk", { iid: parkedFlow.iid })}
        </span>
        {parkedFlow.resumable && onSwitchIssue !== undefined && (
          <GhostButton
            onClick={() => {
              reportClientEvent("ui.click", {
                control: "back_to_parked",
                issueIid: iid,
                parkedIid: parkedFlow.iid,
              });
              onSwitchIssue(parkedFlow.iid);
            }}
          >
            {t("focus.refine.parked.resume", { iid: parkedFlow.iid })}
          </GhostButton>
        )}
      </div>
    );

  // The Undo names what it takes back, like every other Undo on the answer
  // surfaces: a screen reader otherwise hears "Undo — go back" and nothing
  // about which answer.
  const undoLabel =
    lastAnswered === null
      ? null
      : lastAnswered.step.field !== null
        ? fieldCopy(lastAnswered.step.field).label
        : t("focus.field.fallback.kicker");
  const undoBar = canUndo ? (
    <div className="focus-undo">
      <GhostButton
        onClick={undoLast}
        {...(undoLabel !== null
          ? { ariaLabel: t("focus.refine.undo.aria", { label: undoLabel }) }
          : {})}
      >
        {t("focus.refine.undo.button")}
      </GhostButton>
    </div>
  ) : null;

  /**
   * What the sketch verdict did, said once, under the step that follows it.
   *
   * A "yes" is a one-line acknowledgement and goes the moment the next step
   * pins — it is a thank-you, not an instruction.
   *
   * A "no" is an INSTRUCTION with a price on it, and both halves have to be
   * there: telling someone to paste another link without saying that reading it
   * spends one of roughly twenty Figma reads this account gets a month would be
   * lying about cost. It stays until they do it — a new chip in this flow — so
   * it is never still on screen under the design it was about.
   */
  const previewBar =
    previewEcho === null ||
    accepted ||
    flowTerminal ||
    (previewEcho.verdict === "right" && current !== null) ||
    (previewEcho.verdict === "wrong" && flowFigmaLinks.length > previewEcho.linkCount)
      ? null : (
        <div className="focus-parked fig-prev-echo" role="status">
          <span className="focus-parked-text">
            {t(
              previewEcho.verdict === "right"
                ? "focus.figma.preview.approved"
                : "focus.figma.preview.rejected",
            )}
          </span>
          {previewEcho.verdict === "wrong" && (
            <span className="fig-prev-echo-cost">
              {t("focus.figma.preview.rejectedCost")}
            </span>
          )}
        </div>
      );
  // The typed-ask step's markup, HOISTED out of the `current?.kind === "ask"`
  // branch so a second host can render it. The pin-reconciliation effect bails
  // on `accepted`, so post-accept no ask ever becomes `current` — yet a
  // split-time question raised after the parent was written still has to reach
  // the user. Two copies of this markup would be two controls that must keep
  // sending byte-identical answers forever; one renderer with two callers is the
  // only version that can't drift.
  //
  // Closes over nothing defined below this point. `escape` is the caller's
  // way-out footer: an ask outranks every branch below it in the ladder, so the
  // branch it preempts has to hand its escapes up here or they leave the screen.
  const renderAskStep = (
    ask: Extract<Step, { kind: "ask" }>,
    opts: {
      readonly onAnswer: (text: string, meta?: AnswerMeta) => void;
      readonly onSkip: () => void;
      readonly escape?: ReactNode;
      readonly queueHint?: boolean;
      /** The pinned step's key: this rendering decides whether the composer
       *  folds. Omitted by the post-accept host, which has no composer. */
      readonly foldKey?: string;
    },
  ): ReactNode => {
    const askCopy = ask.field !== null ? fieldCopy(ask.field) : null;
    // PRE-FILL — only the user's OWN still-held answer. A spec-bound ask shares
    // its step identity with a card question for the same field (`stepKeyOf`),
    // so an ask that surfaces for a field the user JUST answered (still held,
    // unflushed — e.g. the card's own typed-ask channel queued this
    // independently of the question the user already closed) must not render
    // blank.
    const askHeldAnswer = answers.get(stepKeyOf(ask));
    const askVerdict =
      ask.field !== null
        ? card?.verdicts.find((x) => x.field === ask.field)
        : undefined;
    const askCardVal = askVerdict?.value;
    const askCardValue =
      typeof askCardVal === "string" && askCardVal.trim() !== "" ? askCardVal : null;
    const askSeed =
      askHeldAnswer !== undefined
        ? seedFrom(ask.field ?? "agent_ask", askHeldAnswer)
        : null;
    const helpNode =
      ask.help !== undefined && ask.help.trim() !== "" ? (
        <Help>{uiProse(ask.help)}</Help>
      ) : askCopy ? (
        <HelpDetail help={askCopy.help} detail={askCopy.detail} />
      ) : null;
    const queueHintNode =
      opts.queueHint === true && queue.length > 0 ? (
        <p className="focus-help focus-queue-hint">
          {t("focus.refine.queueHint", {
            count: queue.length,
            noun: t(
              queue.length === 1
                ? "focus.refine.queueHint.one"
                : "focus.refine.queueHint.other",
            ),
          })}
        </p>
      ) : null;
    // A CONCERN is ABOUT the value as it stands, not a request for one — so the
    // value is never offered back as Loom's answer (it is often the user's own
    // words: s27 offered "passt so" back as "Loom's draft"). It shows WHAT IT
    // WOULD KEEP, then — when the concern carries one — Loom's suggested reply,
    // closed, then Keep / Change of equal weight. "Keep it as it is" is the
    // decline and sends no bytes; "Change it" opens an empty box whose reply
    // goes out as a concern reply, and so does a click on Loom's reply.
    if (ask.concern) {
      if (opts.foldKey !== undefined) askFolded = true;
      // Loom's suggested reply to its own concern: the concern's one-row draft,
      // never the value it questions (`loomAnswerOf`, rule 1). NOT for a
      // milestone: its only answers are a pick from the list ("Change it"
      // opens it) or Keep, and Loom's words are neither — the server would not
      // take them as the milestone anyway.
      const concernReply =
        askHeldAnswer === undefined && ask.field !== "milestone"
          ? loomAnswerOf(
              ask.field ?? "agent_ask",
              {
                concern: true,
                ...(askCardValue !== null ? { value: askCardValue } : {}),
                ...(ask.draft !== undefined ? { draft: ask.draft } : {}),
              },
              { settled: false },
            )
          : null;
      return (
        <>
          <Kicker>{t("focus.refine.concern.kicker")}</Kicker>
          <Headline>{uiProse(ask.question)}</Headline>
          {helpNode}
          <ConcernAnswer
            key={ask.askId}
            field={ask.field}
            currentValue={askCardValue}
            question={ask.question}
            projectId={projectId}
            draftKey={`ask:${ask.askId}`}
            {...(concernReply !== null && concernReply.kind === "text"
              ? { loomReply: concernReply.text }
              : {})}
            {...(askCopy !== null ? { answerLabel: askCopy.label } : {})}
            onKeep={opts.onSkip}
            onReply={(text, meta) => opts.onAnswer(text, meta)}
          />
          {opts.escape}
          {queueHintNode}
        </>
      );
    }
    // LOOM'S ANSWER for a plain ask: its own draft first, then the card's for
    // the same field (a spec-bound ask and a card question for that field are
    // ONE step, so the same question must not show Loom's answer or not
    // depending on which channel raised it), then — once settled — a red value.
    const askAnswer =
      askHeldAnswer === undefined
        ? loomAnswerFor(ask.field ?? "agent_ask", {
            ...(askVerdict?.verdict !== undefined ? { verdict: askVerdict.verdict } : {}),
            ...(askCardValue !== null ? { value: askCardValue } : {}),
            ...((ask.draft ?? askVerdict?.draft) !== undefined
              ? { draft: (ask.draft ?? askVerdict?.draft)! }
              : {}),
          })
        : null;
    // A plain ask bound to a field that is already GREEN: the value is the
    // user's own (or a code green), so it is shown as what the field says now,
    // never offered as Loom's answer — and the box stays open with no bare-yes
    // guard, because "yes, keep it" is a legitimate answer there.
    const askGreen =
      askVerdict?.verdict === "green" &&
      askCardValue !== null &&
      ask.field !== null &&
      !isChoiceField(ask.field);
    // Same marking rule as the card-question branch: a CHOICE control shows no
    // block, so without this an ask bound to milestone/priority renders a list
    // of options with nothing said about the value already in hand.
    const askOwnAnswer = askHeldAnswer ?? undone.get(stepKeyOf(ask));
    const askCurrent: ChoiceCurrent | null =
      askOwnAnswer !== undefined
        ? {
            value: prettyValue(ask.field ?? "agent_ask", askOwnAnswer),
            origin: "you",
          }
        : askCardValue !== null && cardValueStands(askVerdict?.verdict)
          ? { value: askCardValue, origin: "card" }
          : // The issue's own value — see `issueChoiceFor`. The typed-ask
            // channel raises the SAME priority/milestone questions the card
            // does, so leaving it out here would fix the symptom on one channel
            // and leave it live on the other.
            issueChoiceFor(ask.field ?? "", issueCurrent, projectId);
    if (opts.foldKey !== undefined) {
      askFolded = foldsComposer(opts.foldKey, ask.field ?? "agent_ask", askAnswer);
    }
    return (
      <>
        <Kicker>
          {askAnswer !== null && askAnswer.source === "unverified" && askAnswer.kind !== "choice"
            ? t("focus.refine.loomVersion.kicker")
            : (askCopy?.kicker ?? t("focus.field.fallback.kicker"))}
        </Kicker>
        <Headline>{uiProse(ask.question)}</Headline>
        {helpNode}
        {askGreen && ask.field !== null && askCardValue !== null && (
          <div className="focus-concern-current">
            <p className="focus-suggest-label">{t("focus.refine.concern.current")}</p>
            <FieldValue field={ask.field} value={askCardValue} />
          </div>
        )}
        <AnswerWidget
          key={ask.askId}
          // A field-less ask has no purpose-built control — free text is the
          // honest default. A spec-bound one reuses the field's own widget.
          field={ask.field ?? "agent_ask"}
          projectId={projectId}
          ariaLabel={ask.question}
          onAnswer={opts.onAnswer}
          // askId-keyed, NOT field-keyed: two consecutive field-less asks would
          // otherwise share a draft and the second would open pre-filled with
          // the answer to the first.
          draftKey={`ask:${ask.askId}`}
          {...(askSeed ?? {})}
          {...(askAnswer !== null ? { loomAnswer: askAnswer } : {})}
          // The bare-yes guard only on an answer that SETS a value, and never
          // over a green (see `askGreen`). A field-less ask is a yes/no or
          // scoping question: "ja" is its answer.
          {...(ask.field !== null && isValueField(ask.field) && !askGreen
            ? { assentGuard: "refuse" as const }
            : {})}
          {...(askCopy !== null ? { answerLabel: askCopy.label } : {})}
          traceSurface="focus_ask"
          {...(opts.foldKey !== undefined
            ? {
                onClosedChange: (closed: boolean) =>
                  reportClosed(opts.foldKey!, closed),
              }
            : {})}
          {...(askCurrent !== null ? { current: askCurrent } : {})}
        />
        {/* A plain ask is skippable when the model marked it optional, or when
            it is bound to the MILESTONE: its only answers are a pick or a Skip,
            and with an empty or unloadable list Skip is the only way on.
            Deliberately not every optional field — a typed ask about another
            one (story points) keeps the model's own call. (A concern is always
            skippable — its Keep is the decision; see above.) */}
        {(ask.optional || ask.field === "milestone") && (
          <div className="focus-skip-optional">
            <GhostButton onClick={opts.onSkip}>
              {/* Keyed on what skipping DOES, not on which channel asked: the
                  milestone question arrived as a card question in one session
                  and as a typed ask in the next, and offered the user two
                  differently-worded skips for the same outcome. */}
              {t(skipLabelKey({ field: ask.field, concern: ask.concern }))}
            </GhostButton>
          </div>
        )}
        {opts.escape}
        {queueHintNode}
      </>
    );
  };
  const askStep =
    current?.kind === "ask"
      ? renderAskStep(current, {
          onAnswer: (text, meta) => answerAsk(current, text, meta),
          onSkip: () => declineAsk(current),
          queueHint: true,
          foldKey: stepKeyOf(current),
          // The ask branch sits ABOVE breakdown_review in the ladder, so a
          // split-time question replaces that screen outright and takes whatever
          // it was showing off screen with it. What gets handed up here is
          // therefore exactly what the preempted screen would have offered:
          //
          //   proposal LANDED  → "keep as one ticket". A decision about a
          //     partition the user has already read, and the only place left to
          //     take it while the question covers the card.
          //   still DRAFTING   → nothing. There is no partition to keep-as-one
          //     yet, so the button could only mean "stop waiting" — and the
          //     "apply as is" leg beside it is gone for the same reason.
          //
          // The wait cannot become permanent either way: the server parks the
          // breakdown degrade while an ask is open, but the CLIENT's
          // BREAKDOWN_REVIEW_DEADLINE_MS is armed on `tail` alone and falls
          // through to Accept regardless. The ordinary way on is to answer.
          ...(tail === "breakdown_review" &&
          breakdownCard.entry !== null &&
          breakdownCard.entry.noSplit !== true
            ? {
                escape: (
                  <div className="focus-footer">
                    {/* Sends the keep-single turn, it does not merely leave the
                        screen — see `declineSplitFromAsk`. */}
                    <GhostButton onClick={() => declineSplitFromAsk(current)}>
                      {t("breakdown.decline")}
                    </GhostButton>
                  </div>
                ),
              }
            : {}),
        })
      : null;
  /* ── the wait, from server facts (./wait-status) ──────────────────────── */

  // The page's own socket (`offlineSettled` / `unreachableSettled`) is decided
  // above the status pill, which reads it too.
  // Anything that means a turn is still owed, or that the user holds the turn:
  // the "nothing is coming" screens must not fire over any of it. A restart's
  // POST in flight is deliberately NOT here: the screen that offered it stays
  // up reading "Starting…", so a refused POST can say so on the spot. A
  // successful one sets the kick pending, and that clears the screen.
  const waitBlocked =
    card !== null ||
    current !== null ||
    typedAsks.length > 0 ||
    openAsks.length > 0 ||
    answers.size > 0 ||
    pendingDeclines.size > 0 ||
    reKick !== null ||
    accepted ||
    flowTerminal;
  // The grind screen's one fact (`card_stopped`): a card exists, no step is
  // pinned, the workspace is up, the socket open, busy known and IDLE, and
  // nothing of the user's is held — for `IDLE_SETTLE_MS`, the same wait
  // `agent_stopped` takes. It replaced a 10 s no-visible-change timer: that one
  // only named fields waiting on the user, and with none it kept saying "Loom
  // is re-checking the rest against the code" over a turn that had ended —
  // 3 min 24 s on #284 and 3 min on #285 (dev, 2026-09-26), until the user
  // wrote a message.
  const grindIdleFact =
    card !== null &&
    // Only an UNFINISHED card. An applyable one is the tail's (plan, split,
    // ready); an all-green one waiting on its token (a split park re-runs the
    // methodology off-turn, and a deployment with no secret never mints one) is
    // finished, and "ended before the card was finished" would be false there.
    !card.allGreen &&
    current === null &&
    typedAsks.length === 0 &&
    openAsks.length === 0 &&
    ws.status === "open" &&
    ws.busyKnown &&
    !ws.agentBusy &&
    ws.sandbox.phase === "ready" &&
    answers.size === 0 &&
    pendingDeclines.size === 0 &&
    reKick === null &&
    !accepted &&
    !flowTerminal;
  const grindIdleSettled = useHeldFor(grindIdleFact, IDLE_SETTLE_MS);
  /** "Carry on" went out and the server has not said busy yet. */
  const [carryOnAt, setCarryOnAt] = useState<number | null>(null);
  useEffect(() => {
    if (carryOnAt === null) return;
    if (ws.agentBusy) {
      setCarryOnAt(null);
      return;
    }
    // Nothing started: the button comes back rather than spin for good.
    const timer = setTimeout(
      () => setCarryOnAt(null),
      Math.max(0, carryOnAt + IDLE_SETTLE_MS - Date.now()),
    );
    return () => clearTimeout(timer);
  }, [carryOnAt, ws.agentBusy]);
  const carryOn = (): void => {
    if (carryOnAt !== null) return;
    reportClientEvent("ui.click", {
      control: "carry_on",
      surface: "card_stopped",
      issueIid: iid,
    });
    focusDiag("carry_on", { iid });
    traceSeedSend("carry_on");
    // Only ever offered on the server's settled idle, so this cannot land on a
    // running turn. NOT a boundary: the conversation and the card stay.
    ws.send(makeCarryOnNudge(iid), {
      scope: sendScope,
      flow: { issueIid: iid },
      evidenceText: "",
    });
    setCarryOnAt(Date.now());
  };
  const stopCandidate = stopCandidateOf({
    socket: ws.status,
    phase: ws.sandbox.phase,
    busyKnown: ws.busyKnown,
    busy: ws.agentBusy,
    blocked: waitBlocked,
  });
  const stoppedSettled = useHeldFor(stopCandidate === "stopped", IDLE_SETTLE_MS);
  const noWorkspaceSettled = useHeldFor(
    stopCandidate === "noWorkspace",
    NO_WORKSPACE_SETTLE_MS,
  );
  const waitInput: WaitInput = {
    socket: ws.status,
    offlineSettled,
    unreachableSettled,
    phase: ws.sandbox.phase,
    phaseSinceMs: ws.sandbox.sinceMs ?? null,
    busyKnown: ws.busyKnown,
    busy: ws.agentBusy,
    busySinceMs: ws.agentWork?.busySinceMs ?? null,
    tool: ws.agentWork?.activity?.tool ?? null,
    lastStop: ws.agentWork?.lastStop ?? null,
    stoppedSettled,
    noWorkspaceSettled,
  };
  const waitStatus = waitStatusOf(waitInput, Date.now());
  // Re-render at the moment "taking longer" / "hasn't started yet" turns on, so
  // the `focus.wait` trace below records it. The line itself ticks on its own.
  const waitFlipAt = nextWaitFlipMs(waitInput, Date.now());
  const [, setWaitClock] = useState(0);
  useEffect(() => {
    if (waitFlipAt === null) return;
    const timer = setTimeout(
      () => setWaitClock((n) => n + 1),
      Math.max(0, waitFlipAt - Date.now()) + 1,
    );
    return () => clearTimeout(timer);
  }, [waitFlipAt]);
  const waitLine = <WaitLine input={waitInput} />;
  // The page's own socket, said on every screen (see `socketFact`).
  const connectionBar =
    waitStatus.kind === "offline" || waitStatus.kind === "unreachable" ? (
      <ConnectionLine kind={waitStatus.kind} />
    ) : null;
  // While the workspace is reconnecting, or starting with nothing running on
  // it, the PHASE is what the screen heads with. "Checking your answer against
  // the code" over a workspace that is not there claims a check nobody is
  // running: live on #280 it stayed up through two minutes of `degraded` with
  // the server idle at depth 0, and through a restart's boot before any turn
  // existed. The screen's own headline comes back with the workspace.
  const phaseHeads =
    waitStatus.kind === "reconnecting" ||
    (waitStatus.kind === "starting" && waitStatus.working === null);
  const phaseHeadline = t("focus.refine.boot.headline", {
    phase: phaseLabel(ws.sandbox.phase) ?? t("focus.sandbox.gettingSetUp"),
  });

  let body: ReactNode;
  // The branch that rendered, for the `focus.stage` trace. Set once per branch
  // below; read only by that trace.
  let stage: FocusStage = "waiting";
  // Whether the rendered branch offers an Accept / Apply, and "keep the previous
  // value" — the two controls a pending ready-screen edit decides between.
  let acceptVisible = false;
  let cancelVisible = false;
  let withMsgBar = true;
  // A ready-screen edit Loom has not folded into the card yet. The server
  // refuses apply while one is pending (`edit_pending`) whichever screen the
  // click comes from, so the screens that keep Apply reachable when Loom cannot
  // run (a stopped workspace, dead tools, the budget wall) must not offer it
  // then: the refusal lands on `apply_failed`, which has no way to clear the
  // edit. Dev 275ddcf6 #215: the stopped-workspace screen showed Apply with an
  // edit pending (focus.stage 14:18:04, acceptVisible=true pendingEditCount=1)
  // and apply was refused `edit_pending` at 14:20:09; the budget wall did the
  // same three times on #201 and on #184. Those screens now do what the ready
  // screen does: withhold Apply and offer "Keep the previous value".
  const cardApplyable = Boolean(card?.allGreen && card.acceptToken);
  const pendingEditFields = card?.pendingEdits?.map((p) => p.field) ?? [];
  const applyBlockedByEdit = cardApplyable && pendingEditFields.length > 0;
  const pendingEditNote = applyBlockedByEdit ? (
    <div className="focus-note">{t("focus.refine.pendingEdit.blocked")}</div>
  ) : null;
  const pendingEditKeep = applyBlockedByEdit
    ? pendingEditFields.map((field) => (
        <GhostButton
          key={`keep:${field}`}
          onClick={() => ws.sendReadinessFieldEditCancel(iid, field)}
        >
          {t("focus.refine.pendingEdit.keep", { label: fieldCopy(field).label })}
        </GhostButton>
      ))
    : null;
  // Set by the screens whose headline already names the note ("Loom is reading
  // your note"), so the receipt does not say it a second time.
  let headlineNamesNote = false;

  if (fatal) {
    stage = "fatal";
    withMsgBar = false;
    body = (
      <>
        <div className="focus-glyph is-bad">
          <IconArrowRight />
        </div>
        <Headline>{t("focus.refine.fatal.headline")}</Headline>
        <div className="focus-error">{fatal}</div>
        <div className="focus-footer">
          <button type="button" className="focus-btn" onClick={startOver}>
            {t("focus.refine.fatal.startOver")}
          </button>
          <GhostButton onClick={onProjects}>{leaveLabel}</GhostButton>
        </div>
      </>
    );
  } else if (session.error !== null) {
    stage = "session_error";
    // `POST /sessions` never succeeded, so there is no session and no pipeline —
    // `ws` is wired to a null id and parks at "closed", which means neither
    // `fatal` nor any sandbox phase ever fires. Without this branch the flow
    // falls through to the generic `!card` boot body and shows "Loom is waking
    // up…" forever over a boot that was never attempted. Surface the server's
    // reason and let `ensure()` be re-driven — it is idempotent, and the common
    // causes here (503 membership-check, 429, a transient 500) are retryable.
    withMsgBar = false;
    body = (
      <>
        <div className="focus-glyph is-bad">
          <IconArrowRight />
        </div>
        <Kicker>{t("focus.refine.issueKicker", { iid })}</Kicker>
        <Headline>{t("focus.refine.bootFailed.headline")}</Headline>
        <div className="focus-error">{session.error}</div>
        <div className="focus-footer">
          <button
            type="button"
            className="focus-btn"
            onClick={() => session.ensure()}
            disabled={session.starting}
          >
            {session.starting
              ? t("focus.refine.bootFailed.retrying")
              : t("focus.refine.bootFailed.retry")}
          </button>
          <GhostButton onClick={onProjects}>{leaveLabel}</GhostButton>
        </div>
      </>
    );
  } else if (accepted && applyResult && !applyResult.ok) {
    stage = "apply_failed";
    // Deterministic apply reported a failure — say why + offer a way on, so a
    // transient write error isn't a dead end. The why is the catalog line for
    // the result's closed code (`errorCopy.ts`), never its wire `error`: an
    // older server filled that with the apply's exception.
    withMsgBar = false;
    const applyCode = applyErrorCode(applyResult);
    // Retry re-sends the same accept token. Where that token refuses the same
    // way every time — the single-use claim is spent (`card_already_applied`),
    // the token is stale, the checked card is gone — Retry is NOT offered: a
    // button whose only outcome is this same screen is the thing this repair
    // exists to remove. Their copy says to refine again, so that button takes
    // Retry's place: a new session on this same issue (`startFreshHere`).
    const retryHelps = applyRetryHelps(applyCode);
    body = (
      <>
        <div className="focus-glyph is-bad">
          <IconArrowRight />
        </div>
        <Kicker>{t("focus.refine.issueKicker", { iid })}</Kicker>
        <Headline>{t("focus.refine.applyFail.headline")}</Headline>
        <div className="focus-error">{t(applyErrorCopyKey(applyCode))}</div>
        <div className="focus-footer">
          {retryHelps ? (
            <button type="button" className="focus-btn" onClick={retryApply}>
              {t("focus.refine.retryApply")}
            </button>
          ) : (
            <button type="button" className="focus-btn" onClick={startFreshHere}>
              {t("focus.refine.applyFail.refineAgain")}
            </button>
          )}
          <GhostButton onClick={openIssue}>{t("focus.refine.openHashN", { iid })}</GhostButton>
          <GhostButton onClick={() => refineAnother("apply_failed")}>
            {t("focus.refine.refineAnother")}
          </GhostButton>
        </div>
      </>
    );
  } else if (accepted && !applyResult && applyTimedOut) {
    stage = "apply_timeout";
    // No result past the deadline — the accept frame may have been dropped on a
    // socket flap. Let the user re-send instead of hanging on "applying…".
    body = (
      <>
        <div className="focus-glyph is-ok">
          <IconCheck />
        </div>
        <Kicker>{t("focus.refine.issueKicker", { iid })}</Kicker>
        <Headline>{t("focus.refine.applyTimeout.headline")}</Headline>
        <Help>{t("focus.refine.applyTimeout.help")}</Help>
        <div className="focus-footer">
          <button type="button" className="focus-btn" onClick={retryApply}>
            {t("focus.refine.retryApply")}
          </button>
          <GhostButton onClick={openIssue}>{t("focus.refine.openHashN", { iid })}</GhostButton>
        </div>
      </>
    );
  } else if (accepted || flowTerminal) {
    stage = "applied";
    // `flowTerminal` is what makes this branch survive a reload: `accepted` is
    // component state and the replay would otherwise rebuild the pre-apply
    // ladder — plan-validate, then `Accept & apply` armed — over a written issue.
    //
    // `creating` / `failed` / `done` only exist because a `breakdown_accept`
    // already went out, so they are unconditionally this user's split.
    //
    // `review` is the one that needed a guard: it is derived from the latched
    // timeline entry, so it survives a decline and a reload alike. It counts
    // only when THIS mount can vouch for the proposal — the user asked for it
    // from the applied screen or confirmed it (`splitEngaged`), or this mount
    // did the apply and the user never backed out (the F6 accept-race window,
    // where the proposal lands just after the write). After a reload neither
    // holds, and an unconfirmed proposal is left alone rather than re-offered as
    // an irreversible "create N sub-issues" on an issue already written.
    const splitProposalLive = splitEngaged || (accepted && !splitDeclined);
    const splitActive =
      (breakdownCard.phase === "review" && splitProposalLive) ||
      breakdownCard.phase === "creating" ||
      breakdownCard.phase === "failed" ||
      breakdownCard.phase === "done";
    // The headline is about the PARENT write, and `applyResult.ok` is the whole
    // of that question. A visible split surface used to hold it back on
    // "applying…" — narrowed once already, when a declined proposal replayed as
    // `review` did it — but the copy it holds is "Loom is writing the refined
    // story, acceptance criteria, and metadata onto the issue", which is exactly
    // the write that just finished. Live on #1085 that produced three
    // contradictory claims in one view: this heading, a status pill reading
    // `applied`, and the flow's own `agent idle / your turn` — over a card
    // asking for an irreversible "create N sub-issues", with the wrong claim in
    // the largest type on the page.
    //
    // Nothing is lost by letting it read "applied": every split phase already
    // states its own progress inside the card ("Creating the sub-issues… / Sent
    // — Loom is creating them on GitLab now", the failure notice, the review
    // footer), which is where a reader looks for it anyway.
    const done = applyResult?.ok === true;
    // A post-accept split is still possible: the parent applied cleanly, sizing
    // is in and no proposal is live yet, and the user hasn't dismissed the offer.
    const canOfferSplit =
      applyResult?.ok === true &&
      breakdownCard.sizing !== null &&
      breakdownCard.entry === null &&
      breakdownCard.phase === "none" &&
      !breakdownCard.breakdownUnavailable &&
      !postAcceptSplitDismissed;
    // A split-time question raised AFTER the parent was applied. It cannot come
    // through `current` — the pin-reconciliation effect's first line bails on
    // `accepted` / `flowTerminal`, deliberately, so a late verdict can't
    // auto-fire a billed turn — so this host reads `typedAsks` directly.
    // Confined to a live split, which now also means a split the user is
    // actually engaged in: a replayed ask belongs to a flow the user has already
    // left, and its controls send nothing the server is still waiting for.
    //
    // Confined to the REVIEW phase, not merely to `splitActive`. Once the
    // commit is out there is no partition left to ask about: the sub-issues
    // exist, the write cannot be walked back, and the question went on being
    // pinned above them. `handledAskIds` retires it the moment this mount
    // commits; the phase gate is what holds after a reload, when that set is
    // empty again and the server's `question_resolved` may still be in flight.
    const postAcceptAsk =
      splitActive && breakdownCard.phase === "review"
        ? ((typedAsks.find((a) => a.kind === "ask") ?? null) as Extract<
            Step,
            { kind: "ask" }
          > | null)
        : null;
    body = (
      <>
        <div className="focus-glyph is-ok">
          <IconCheck />
        </div>
        <Kicker>{t("focus.refine.issueKicker", { iid })}</Kicker>
        <Headline>
          {done
            ? t("focus.refine.applied.headline")
            : t("focus.refine.applying.headline")}
        </Headline>
        <Help>
          {done
            ? t("focus.refine.applied.help")
            : t("focus.refine.applying.help")}
        </Help>
        {/* The user read a 2–4KB summary and pressed "Yes, that is right", and
            the issue came out without the section. Measured: 4 of 43 applies in
            30 days, and the only witness was a Cloud Logging WARNING.

            Gated on `businessSummary !== null` — this surface having actually
            SHOWN a plan. Most applies write no section because the tail round
            never ran (an IssuePage apply, a flow that stopped early), and
            telling those users something is missing would be a claim about
            work that never existed. `no_plan` is excluded for the same reason:
            the server itself cannot tell "never ran" from "propose_card
            invalidated it", so it is not a reason to state a loss on. */}
        {applyResult?.planSectionWritten === false &&
          businessSummary !== null &&
          applyResult.planDropReason !== "no_plan" && (
            <div className="focus-note">
              {t(
                `focus.refine.applied.planDropped.${
                  applyResult.planDropReason === "not_confirmed" ||
                  applyResult.planDropReason === "criteria_moved" ||
                  applyResult.planDropReason === "empty_after_normalize"
                    ? applyResult.planDropReason
                    : "unknown"
                }`,
              )}
            </div>
          )}
        {/* The split-time question comes FIRST. It used to render under the
            whole card — a full screen the user had to scroll past the rows,
            the parked list and the commit footer to reach, carrying a SECOND
            "Keep as one ticket" next to the card's own. One question, one
            keep-as-one, and both above the thing they are about. */}
        {postAcceptAsk !== null &&
          renderAskStep(postAcceptAsk, {
            onAnswer: (text) => answerAskDirect(postAcceptAsk, text),
            onSkip: () => declineAskDirect(postAcceptAsk),
          })}
        {splitActive && (
          <BreakdownCard
            card={breakdownCard}
            epicTitle={issueTitle}
            issueIid={iid}
            // Truthiness, not `!== undefined`: `onBreakdownConfirm` refuses on a
            // falsy token, and the card treats any DEFINED value as confirmable
            // — so forwarding an empty string enables a button whose handler
            // then refuses. Withholding it instead lands the card on its own
            // "re-checking" wait state, which is what that is.
            {...(card?.acceptToken ? { acceptToken: card.acceptToken } : {})}
            // The SAME fact the spread above reads, stated explicitly: the card
            // otherwise has to re-derive "the token was stripped on purpose"
            // from a token's mere absence, which a proposal that never had one
            // would also read as.
            confirmPending={!card?.acceptToken}
            // Omitted here before, unlike the pre-accept host below — so
            // `agentBusy !== true` was trivially satisfied on the ONE branch
            // that still renders an editable, revisable card: the row menu
            // stayed live mid-turn, the revising chip never showed, and the
            // armed-commit supersede never fired. That is the stale-token shape
            // the other host explicitly guards.
            agentBusy={ws.agentBusy}
            // The parent card is applied on this branch: the issue description
            // is already on GitLab and there is no post-apply must-have write
            // lane, so taking work out of scope is refused. Named on the
            // surface rather than surfacing as a server deny after the click.
            parkDisabled
            sessionClosed={sessionClosed}
            onSend={(text) =>
              ws.send(text, { scope: sendScope, flow: { issueIid: iid } })
            }
            // Committing IS the answer to any question about this split, and it
            // is irreversible — so the question retires here rather than staying
            // pinned over sub-issues that already exist, asking about a decision
            // no answer can reach. Retired CLIENT-side only, on purpose: the
            // server-side decline routes a prompt to the sandbox, i.e. spends a
            // model turn asking the agent to stop caring about a partition the
            // server has already written. The alternative — blocking the commit
            // until the question is answered — makes a model's freely-worded
            // question a gate on a user decision, which is the dead-end shape
            // this whole pass is removing.
            onConfirm={() => {
              if (postAcceptAsk !== null) {
                setHandledAskIds((s) => new Set(s).add(postAcceptAsk.askId));
              }
              onBreakdownConfirm();
            }}
            // The card no longer sends the keep-single sentence itself, because
            // WHICH lane it takes is this host's knowledge: with a split-time
            // ask open it has to ride that ask's `question_answer` frame (a
            // plain send is a second user turn, and the second SIGTERM-kills the
            // turn the first started). That is also why the ask's own escape
            // footer is gone — it was the same decision on a different wire,
            // two buttons deep on one screen.
            onDecline={() => {
              if (postAcceptAsk !== null) {
                declineSplitFromAccepted(postAcceptAsk);
                return;
              }
              setPostAcceptSplitDismissed(true);
              ws.send(buildKeepSingleMessage(iid), {
                scope: sendScope,
                flow: { issueIid: iid },
              });
            }}
            onOpenChild={onOpenIssue}
            stateKey={`${session.sessionId ?? ""}:${iid}:${ws.resetEpoch}`}
          />
        )}
        {canOfferSplit &&
          (postAcceptSplitRequested ? (
            <ThinkingDots label={t("breakdown.review.wait")} />
          ) : (
            // Secondary, corrective framing: the ticket is already applied, so
            // this is an optional "it turned out big" offer, not a primary CTA
            // that undermines the done state.
            <div className="focus-skip-optional">
              {/* The offer has to AGREE with the size verdict in hand. Live, an
                  issue whose ready card had just read "Loom sized this S · ~3
                  files — a split isn't needed" was met one click later with
                  "This turned out bigger than expected", with nothing in
                  between: the offer's copy was hard-coded to the big case while
                  its own button already branched on the verdict. Same three
                  sentences the pre-accept screen uses, so the two screens can't
                  say different things about one verdict. */}
              <div className="focus-note">
                {breakdownCard.sizing === null
                  ? t("focus.refine.postAccept.offer")
                  : breakdownCard.sizing.verdictStale
                    ? t("focus.refine.split.verdictStale")
                    : breakdownCard.sizing.splitRecommended
                      ? t("focus.refine.postAccept.offer")
                      : t("focus.refine.split.sizeNotNeeded", {
                          bucket: breakdownCard.sizing.overall.locBucket,
                          files: String(breakdownCard.sizing.overall.filesTouched),
                        })}
              </div>
              <GhostButton onClick={requestSplitFromAccepted}>
                {breakdownCard.sizing?.splitRecommended === true &&
                breakdownCard.sizing.verdictStale !== true
                  ? t("breakdown.split.cta")
                  : t("breakdown.split.force")}
              </GhostButton>
              <GhostButton onClick={() => setPostAcceptSplitDismissed(true)}>
                {t("focus.refine.postAccept.dismiss")}
              </GhostButton>
            </div>
          ))}
        {/* Outside `canOfferSplit` on purpose: the verdict only exists once
            `entry` is non-null, which is exactly what makes that flag false. */}
        {postAcceptNoSplit && (
          <div className="focus-note">{t("focus.refine.tail.noSplitNote")}</div>
        )}
        {postAcceptDegraded && (
          <div className="focus-note">
            {t("focus.refine.postAccept.degradedNote")}
          </div>
        )}
        {/* Render NO free stream prose — the same leak class the boot (`!card`),
            `thinking` and plan branches already close, extended to the one
            branch that still carried it. Everything the agent narrates from
            here on is post-apply repo work (the sizing / split turn re-reads
            the tree) and it names modules and file paths to a business owner.
            The plan-era partition covers that only when it is ARMED, and it is
            armed only in the mount that itself sent the plan request: a resumed
            flow finds an existing `planResult` and short-circuits the trigger
            before `preplanIdsRef` is captured, leaving every later entry
            unsuppressed. Dropping the box is unconditional, so it holds in both
            cases. The screen keeps its own copy — the applied/applying headline
            + help, the split card, the split offer and the notes above — so
            nothing that carries meaning here depended on the transcript. */}
        <div className="focus-footer">
          <button type="button" className="focus-btn" onClick={openIssue}>
            {t("focus.refine.openHashN", { iid })} <IconArrowRight />
          </button>
          <GhostButton onClick={() => refineAnother("applied")}>
            {t("focus.refine.refineAnother")}
          </GhostButton>
        </div>
      </>
    );
  } else if (sandboxDead && !splitCommitReachable) {
    stage = "sandbox_dead";
    // The sandbox is TERMINATED — a server fact, and one of the three screens
    // that may offer a restart. It is also where a pipeline that died before
    // its bridge connected lands now: the server's pipeline watch turns a
    // failed / canceled pipeline into `terminated` (dev 63198518 sat in
    // `waiting-for-runner` for 23 minutes before it could). The restart here
    // takes `/reconnect`'s rebuild path: a terminated bridge is never reused.
    // Ranked exactly as the pill ranks it: below `accepted` (an issue that was
    // written stays written) and above `toolsDead` (a sandbox that is gone is
    // the stronger fact than the tools it cannot reach).
    //
    // NO `!card` guard, deliberately, mirroring `toolsDead`: an answer typed
    // under a dead sandbox is discarded either way, so an ask branch above this
    // one would render a question that eats the answer.
    //
    // Apply stays reachable for the same reason it does under the budget wall:
    // `readiness_accept` is server-direct, not a sandbox turn, so it banks the
    // refine work instead of forcing a start-over.
    withMsgBar = false;
    const canApply = cardApplyable && !applyBlockedByEdit;
    acceptVisible = canApply;
    cancelVisible = applyBlockedByEdit;
    body = (
      <>
        <div className="focus-glyph is-bad">
          <IconAlert />
        </div>
        <Kicker>{t("focus.refine.issueKicker", { iid })}</Kicker>
        <Headline>{t("focus.refine.sandboxDead.headline")}</Headline>
        <Help>
          {canApply
            ? t("focus.refine.sandboxDead.helpApplyable")
            : t("focus.refine.sandboxDead.help")}
        </Help>
        {pendingEditNote}
        {restartRetryable && (
          <div className="focus-error">
            {t("focus.refine.stalled.reconnectBusy")}
          </div>
        )}
        <div className="focus-footer">
          {canApply && (
            <button type="button" className="focus-btn is-ok" onClick={() => onAccept("sandbox_dead")}>
              {t("focus.refine.budget.applyAnyway")} <IconCheck />
            </button>
          )}
          {/* Non-destructive PRIMARY: same session, same history, same card —
              the server re-triggers the pipeline and resumes the agent's own
              conversation where it can. `startOver` mints a new session and
              loses the conversation, so it is a ghost. */}
          <button
            type="button"
            className={canApply ? "focus-btn" : "focus-btn is-ok"}
            onClick={() => restartSandbox("sandbox_dead")}
          >
            {t("focus.refine.stalled.reconnect")}
          </button>
          {pendingEditKeep}
          <GhostButton onClick={onProjects}>
            {leaveLabel}
          </GhostButton>
          <GhostButton onClick={startOver}>
            {t("focus.refine.stalled.startOverLossy")}
          </GhostButton>
        </div>
      </>
    );
  } else if (toolsDead) {
    stage = "tools";
    // The sandbox can't reach Loom's tools, so `propose_card` can never fire and
    // no card will ever arrive. Before this branch existed that was invisible:
    // the ladder fell through to the `!card` boot screen and span forever
    // (GitLab CI job 5993905). Placed with the other terminal states so no
    // loader branch can win.
    //
    // An already-green card stays applyable for the same reason it does under
    // the budget wall: apply is server-direct (`readiness_accept`), not a
    // sandbox turn, so it banks the work instead of forcing a start-over.
    withMsgBar = false;
    const canApply = cardApplyable && !applyBlockedByEdit;
    acceptVisible = canApply;
    cancelVisible = applyBlockedByEdit;
    body = (
      <>
        <div className="focus-glyph is-bad">
          <IconAlert />
        </div>
        <Kicker>{t("focus.refine.issueKicker", { iid })}</Kicker>
        <Headline>{t("focus.refine.tools.headline")}</Headline>
        <Help>
          {canApply
            ? t("focus.refine.tools.helpApplyable")
            : t("focus.refine.tools.help")}
        </Help>
        {pendingEditNote}
        <div className="focus-footer">
          {/* STAYS ON THIS ISSUE. The tools screen is a property of the
              SESSION — its sandbox lost the MCP bearer — and it says so ("your
              issue is unchanged"), so unwinding to the mode picker throws away
              the one thing the screen just promised was safe. Same defect the
              budget wall had one screen over: the button read "Start a new
              session", and a brand-new issue was abandoned mid-refine with no
              way back except finding it again in the picker.

              NOT the `stalled` / `fatal` screens: their copy
              (`stalled.startOverLossy`, `fatal.startOver`) names the loss out
              loud, so the unwind is the promise they made. */}
          {canApply ? (
            <>
              <button type="button" className="focus-btn is-ok" onClick={() => onAccept("tools")}>
                {t("focus.refine.budget.applyAnyway")} <IconCheck />
              </button>
              <GhostButton onClick={startFreshHere}>
                {t("focus.refine.budget.newSessionHere")}
              </GhostButton>
            </>
          ) : (
            <button
              type="button"
              className="focus-btn is-ok"
              onClick={startFreshHere}
            >
              {t("focus.refine.budget.newSessionHere")}
            </button>
          )}
          {pendingEditKeep}
          <GhostButton onClick={onProjects}>
            {leaveLabel}
          </GhostButton>
        </div>
      </>
    );
  } else if (budgetDead) {
    stage = "budget";
    // F5 — terminal budget state. Above all tail/question/loader branches (no
    // spinner survives) and below the accepted branches (apply-anyway still shows
    // its apply screen). An all-green card can still be applied: the apply is
    // server-direct (readiness_accept, not a sandbox turn) so budget can't block
    // it — banking the refine work instead of forcing a start-over that mints a
    // new session and loses the greens.
    withMsgBar = false;
    const canApply = cardApplyable && !applyBlockedByEdit;
    acceptVisible = canApply;
    cancelVisible = applyBlockedByEdit;
    const spend = ws.budgetExhausted;
    body = (
      <>
        <div className="focus-glyph is-bad">
          <IconAlert />
        </div>
        <Kicker>{t("focus.refine.issueKicker", { iid })}</Kicker>
        <Headline>{t("focus.refine.budget.headline")}</Headline>
        <Help>
          {canApply
            ? t("focus.refine.budget.helpApplyable")
            : t("focus.refine.budget.help")}
        </Help>
        {spend !== null && (
          <div className="focus-note">
            {t("focus.refine.budget.spent", {
              used: spend.used.toFixed(2),
              limit: spend.limit.toFixed(2),
            })}
          </div>
        )}
        <Help>{t("focus.refine.budget.keepGoingHelp")}</Help>
        {pendingEditNote}
        <div className="focus-footer">
          {canApply ? (
            // Applyable → banking the refined issue is the primary action;
            // carrying on is the near-equal alternative, and start-over (which
            // loses the greens) stays demoted.
            <>
              <button type="button" className="focus-btn is-ok" onClick={() => onAccept("budget")}>
                {t("focus.refine.budget.applyAnyway")} <IconCheck />
              </button>
              <button type="button" className="focus-btn" onClick={onKeepGoing}>
                {t("focus.refine.budget.keepGoing")}
              </button>
              {/* Stays on THIS issue — see `startFreshHere`. */}
              <GhostButton onClick={startFreshHere}>
                {t("focus.refine.budget.newSessionHere")}
              </GhostButton>
              <GhostButton onClick={onProjects}>
                {leaveLabel}
              </GhostButton>
            </>
          ) : (
            // Not applyable → carrying on is now the honest primary: a new
            // session would redo the whole grind at the same ceiling, which is
            // what made this screen a dead end. Leaving stays available.
            <>
              <button type="button" className="focus-btn is-ok" onClick={onKeepGoing}>
                {t("focus.refine.budget.keepGoing")}
              </button>
              {pendingEditKeep}
              <GhostButton onClick={onProjects}>
                {leaveLabel}
              </GhostButton>
              <GhostButton onClick={startFreshHere}>
                {t("focus.refine.budget.newSessionHere")}
              </GhostButton>
            </>
          )}
        </div>
      </>
    );
  } else if (current?.kind === "preview") {
    stage = "preview";
    // THE FIGMA SKETCH. Sits with the typed-ask branch below it and above
    // everything else, for the same reason: the agent is waiting on it, and it
    // is upstream of every question the design informs.
    //
    // Renders NO prose block and no message bar of its own — the step body is
    // the drawing and its two buttons, which is what "one question per screen"
    // means for a question whose payload is a picture. The Bigger modal inside
    // the widget REPLACES this screen rather than sitting beside it.
    body = (
      <FigmaPreview
        sessionId={session.sessionId}
        previewId={current.previewId}
        // The picture the READ produced, off this chat's own history, keyed by
        // the ask's own `previewId`. Null falls back to the route, which is
        // what a chat from before the event has.
        //
        // Frozen against TIME, not against a re-read. The server's handle is
        // keyed by `(fileKey, nodeId)` and reused, so reading this same frame
        // again emits a second scene under this same `previewId` and the fold's
        // latest-seq rule swaps the picture under the open question. What that
        // cannot do is show a LATER conversation's bytes, or today's copy of a
        // frame this chat read last week — which is the defect the stored scene
        // exists to close, and the caption's `readAtIso` re-dates itself either
        // way.
        scene={ws.figmaSceneFor(current.previewId)}
        askId={current.askId}
        maxH={PREVIEW_FLOW_MAX_H}
        onVerdict={(verdict) => answerPreview(current, verdict)}
        onSkip={() => skipPreview(current)}
      />
    );
  } else if (current?.kind === "ask") {
    stage = current.concern ? "concern" : "ask";
    // A TYPED ASK. Sits above the `!card` branch — this is the whole point of
    // the channel: before a card exists the agent could only ask in prose, on a
    // screen that renders prose plus an enabled composer, and an answer typed
    // there is unattributable. Above the all-green branches too: an ask the
    // agent is blocked on outranks the ready screen.
    //
    // Renders NO proseBlock, which is what makes "a question and loose prose
    // never coexist" hold in the boot window the same way it already holds for
    // card questions.
    body = askStep;
    composerFolded = askFolded;
  } else if (waitStatus.kind === "stopped" && !card) {
    stage = "agent_stopped";
    // The workspace is up, the SERVER says nothing is running, and nothing is
    // open — no card, no question, no held answer, no restart or seed in flight
    // — and that has held for `IDLE_SETTLE_MS`. The one state in which "nothing
    // more is on its way" is a fact rather than a guess from silence. BELOW the
    // ask branch: anything the flow can still show the user outranks it.
    //
    // The `!card` guard is what confines this to the fall-through it sits in:
    // every branch below reads `card`.
    //
    // The message bar STAYS. A message is the gentler way out: it carries a
    // parked seed and keeps the agent's conversation; "Start again" is a new
    // round (a boundary seed), which the copy says.
    body = (
      <>
        <div className="focus-glyph is-bad">
          <IconAlert />
        </div>
        <Kicker>{t("focus.refine.issueKicker", { iid })}</Kicker>
        <Headline>{t("focus.refine.stopped.headline")}</Headline>
        {waitStatus.reason === "silence" && (
          <Help>{t("focus.refine.stopped.silence")}</Help>
        )}
        <Help>{t("focus.refine.stopped.help")}</Help>
        {restartRetryable && (
          <div className="focus-error">
            {t("focus.refine.stalled.reconnectBusy")}
          </div>
        )}
        <div className="focus-footer">
          <button
            type="button"
            className="focus-btn is-ok"
            disabled={restarting}
            onClick={() => restartSandbox("agent_stopped")}
          >
            {restarting
              ? t("focus.refine.stopped.starting")
              : t("focus.refine.stopped.startAgain")}
          </button>
          <GhostButton onClick={onProjects}>
            {leaveLabel}
          </GhostButton>
          <GhostButton onClick={startOver}>
            {t("focus.refine.stalled.startOverLossy")}
          </GhostButton>
        </div>
        <div className="focus-note">{t("focus.refine.stopped.orMessage")}</div>
      </>
    );
  } else if (waitStatus.kind === "noWorkspace" && !card) {
    stage = "no_workspace";
    // No workspace at all: the phase is `idle` — no supervisor, which is what
    // an instance recycle leaves (supervisors are only opened by `POST
    // /sessions` and `/reconnect`) — and the server says nothing is running,
    // for `NO_WORKSPACE_SETTLE_MS`. A bridge that re-attaches after a recycle
    // makes the census say busy, and that reads "working" instead. Here
    // `/reconnect` REALLY boots one: `bridgeTriggered` is false on such an
    // instance, so the route rebuilds and dispatches.
    withMsgBar = false;
    body = (
      <>
        <div className="focus-glyph is-bad">
          <IconAlert />
        </div>
        <Kicker>{t("focus.refine.issueKicker", { iid })}</Kicker>
        <Headline>{t("focus.refine.noWorkspace.headline")}</Headline>
        <Help>{t("focus.refine.noWorkspace.help")}</Help>
        {restartRetryable && (
          <div className="focus-error">
            {t("focus.refine.stalled.reconnectBusy")}
          </div>
        )}
        <div className="focus-footer">
          <button
            type="button"
            className="focus-btn is-ok"
            disabled={restarting}
            onClick={() => restartSandbox("no_workspace")}
          >
            {restarting
              ? t("focus.refine.stopped.starting")
              : t("focus.refine.noWorkspace.start")}
          </button>
          <GhostButton onClick={onProjects}>
            {leaveLabel}
          </GhostButton>
        </div>
      </>
    );
  } else if (!card) {
    stage = "boot";
    // The boot window renders NO free prose. This is the structural fix for
    // "Loom asked me a question in the output box and I answered it there".
    //
    // The previous `bootProseBlock` tried to solve it by styling the text as
    // quiet status rather than a card. That is a hint, not a guarantee: the
    // text streams token by token for as long as the model writes, and until
    // the ask tool call completes there is no ask card to outrank it — so a
    // bulleted restatement of the questions sat above an enabled composer for
    // seconds, which is exactly what users answered into. Nothing downstream
    // can undo that; the only hard guarantee is not to render it.
    //
    // Deliberately drops the RENDER only. `lastAssistant` still feeds
    // `activitySig` (the loader gate).
    //
    // What the screen says while it waits is the wait line (`waitLine`): the
    // phase and its minutes, whether the server says Loom is working and on
    // what — server facts, never a guess from silence.
    //
    // The user loses nothing they need: the headline names the phase, the help
    // line names the issue, and every real question arrives as its own answer
    // card. The servant prompt is changed in the same commit so it no longer
    // narrates into a surface that does not exist.
    // BOOT copy is a claim about a session that has not started working yet
    // ("Loom is live… Setting up to refine X"). Once the user has answered
    // something it is simply false: the flow is mid-conversation and the model
    // is chewing on the answer just given — live, that boot screen sat there for
    // minutes between two typed asks, before the first card ever existed (the
    // whole pre-card question channel runs in this branch). Which one shows is
    // decided by whether this flow has ever taken an answer, not by the sandbox
    // phase, so it can't flip back to "setting up" mid-flight.
    //
    // A NOTE is neither. With the sandbox live the screen says Loom is reading
    // it, then that Loom has it; the note itself sits above the composer as
    // sent. Before the sandbox is live the boot copy stays true (Loom is still
    // setting up) and the note shows the same way.
    headlineNamesNote = noteShown !== null && live;
    body = noteShown !== null && live ? (
      <>
        {showLoader && <ThinkingDots label={t("focus.refine.thinking.working")} />}
        <Kicker>{t("focus.refine.issueKicker", { iid })}</Kicker>
        <Headline>
          {thinking
            ? t("focus.refine.note.reading")
            : t("focus.refine.note.received")}
        </Headline>
        {!thinking && <Help>{t("focus.refine.note.receivedHelp")}</Help>}
        {waitLine}
      </>
    ) : engagedPreCard && noteShown === null ? (
      <>
        {showLoader &&
          (phaseHeads ? (
            <div className="focus-spinner" />
          ) : (
            <ThinkingDots label={t("focus.refine.thinking.working")} />
          ))}
        <Kicker>{t("focus.refine.issueKicker", { iid })}</Kicker>
        <Headline>{phaseHeads ? phaseHeadline : t("focus.refine.thinking.headline")}</Headline>
        {waitLine}
      </>
    ) : (
      <>
        {showLoader && <div className="focus-spinner" />}
        <Kicker>{t("focus.refine.issueKicker", { iid })}</Kicker>
        <Headline>{phaseHeadline}</Headline>
        <Help>{t("focus.refine.boot.help", { title: issueTitle })}</Help>
        {waitLine}
      </>
    );
  } else if (current?.kind === "question") {
    stage = "question";
    const copy = fieldCopy(current.field);
    // PRE-FILL, and it is the ONLY seed: the user's own still-held answer for
    // this field (the undo + re-ask path). Raw send-text, so `seedFrom`
    // splits/strips it into the right shape.
    const heldAnswer = answers.get(current.field);
    const verdict = card?.verdicts.find((x) => x.field === current.field);
    const cardVal = verdict?.value;
    const seed = heldAnswer !== undefined ? seedFrom(current.field, heldAnswer) : null;
    // LOOM'S ANSWER, shown CLOSED (`loomAnswerOf`): the typed draft rows, or —
    // once settled — the red value, which is Loom's own wording. Never a seed:
    // a box pre-filled with Loom's words is indistinguishable from the user's
    // own draft, so a stray ⌘Enter would spend them with no decision behind it.
    // A click on "Use this text" IS that decision — the user's own act, which
    // the gate grounds like typing.
    const loomAnswer =
      heldAnswer === undefined
        ? loomAnswerFor(current.field, {
            ...(verdict?.verdict !== undefined ? { verdict: verdict.verdict } : {}),
            ...(typeof cardVal === "string" ? { value: cardVal } : {}),
            ...(verdict?.draft !== undefined ? { draft: verdict.draft } : {}),
          })
        : null;
    // Loom's WORDING — a red text or list offered as the answer. A choice keeps
    // its own question: its options are the answer, and Loom's pick is only a
    // mark on one of them.
    const loomWording =
      loomAnswer !== null && loomAnswer.source === "unverified" && loomAnswer.kind !== "choice";
    // A CHOICE control shows every option at once, so Loom's answer marks its
    // option instead — and the value we DO hold gets marked too, most-recent
    // first: the user's own still-held answer, then what an undo just pulled
    // back off this step (`answers` no longer holds it — that is what undoing
    // is), then the card's value if it stands (`cardValueStands`: a refused one
    // is Loom's suggestion, above, and never hides the issue's value below).
    const undoneAnswer = undone.get(current.field);
    const ownAnswer = heldAnswer ?? undoneAnswer;
    const choiceCurrent: ChoiceCurrent | null =
      ownAnswer !== undefined
        ? { value: prettyValue(current.field, ownAnswer), origin: "you" }
        : typeof cardVal === "string" &&
            cardVal.trim() !== "" &&
            cardValueStands(verdict?.verdict)
          ? { value: cardVal, origin: "card" }
          : // …and, when neither exists, THE ISSUE'S OWN VALUE. This is the
            // ordinary case for an open question, not an edge one: the agent's
            // ask is a `needs_input` slot, which carries a question and no
            // value, so the two branches above are both empty on the very step
            // whose question says "keep that, or change it?".
            issueChoiceFor(current.field, issueCurrent, projectId);
    composerFolded = foldsComposer(stepKeyOf(current), current.field, loomAnswer);
    body = (
      <>
        <Kicker>
          {/* A red on a field the user ANSWERED is not the agent asking for
              something missing — it is the gate saying it could not match its
              own version to their words. The kicker has to say which of the two
              this is, or the user reads it as being asked the same thing twice.
              Once Loom's version is ON SCREEN as its answer, the kicker names
              it for what it is: Loom's wording. */}
          {loomWording
            ? t("focus.refine.loomVersion.kicker")
            : current.unverified === true
              ? t("focus.refine.unverified.kicker")
              : copy.kicker}
        </Kicker>
        <Headline>
          {loomWording
            ? t("focus.refine.loomVersion.question", { label: copy.label })
            : uiProse(current.question)}
        </Headline>
        <HelpDetail help={copy.help} detail={copy.detail} />
        <AnswerWidget
          key={current.field}
          field={current.field}
          projectId={projectId}
          ariaLabel={current.question}
          onAnswer={(text, meta) => answerCurrent(current.field, text, meta)}
          draftKey={`q:${current.field}`}
          {...(seed ?? {})}
          {...(loomAnswer !== null ? { loomAnswer } : {})}
          // A bare yes names nothing, and this answer SETS the field's value.
          {...(isValueField(current.field) ? { assentGuard: "refuse" as const } : {})}
          answerLabel={copy.label}
          traceSurface="focus_question"
          onClosedChange={(closed) => reportClosed(stepKeyOf(current), closed)}
          {...(choiceCurrent !== null ? { current: choiceCurrent } : {})}
        />
        {/* Skip is a SIBLING of the widget, outside its own loading gate, so
            while a picker is still fetching its options Skip is the only live
            control on the screen — and a skip durably declines the field. The
            answer to that is the LATENCY, not hiding the escape hatch: the
            milestone picker no longer enumerates every issue in the project
            (`useMilestones({counts:false})`), so the wait it was measured
            against is gone. Holding Skip behind the fetch was tried and
            rejected: gating on `loading` leaves an escape-less screen whenever
            the fetch hangs, which is exactly when the user needs one. */}
        {isOptionalField(current.field) && (
          <div className="focus-skip-optional">
            <GhostButton onClick={() => skipCurrent(current.field)}>
              {t("focus.refine.skipOptional")}
            </GhostButton>
          </div>
        )}
        {queue.length > 0 && (
          <p className="focus-help focus-queue-hint">
            {t("focus.refine.queueHint", {
              count: queue.length,
              noun: t(
                queue.length === 1
                  ? "focus.refine.queueHint.one"
                  : "focus.refine.queueHint.other",
              ),
            })}
          </p>
        )}
      </>
    );
  } else if (current?.kind === "review") {
    stage = "review";
    const copy = fieldCopy(current.field);
    const review = current;
    // The bar under a milestone review only sends a note (`sendFree`), so it
    // folds to the note link, as on every other milestone step.
    composerFolded = review.field === "milestone";
    body = editingReview ? (
      <>
        <Kicker>{copy.label}</Kicker>
        <Headline>{t("focus.refine.review.setHeadline", { label: copy.label.toLowerCase() })}</Headline>
        <HelpDetail help={copy.help} detail={copy.detail} />
        <AnswerWidget
          key={review.field}
          field={review.field}
          projectId={projectId}
          ariaLabel={t("focus.refine.review.setAria", { label: copy.label })}
          onAnswer={(text) => answerCurrent(review.field, text)}
          draftKey={`review:${review.field}`}
          // Open the editor pre-filled with the value being reviewed — and, for
          // a choice control that has no box to pre-fill, with that value
          // marked on its option.
          {...seedFrom(review.field, review.value)}
          current={{ value: review.value, origin: "card" }}
          // The same exits the ready editor has: an unchanged OK is not a
          // change, and a bare yes over a value that stands is a keep.
          refuseUnchanged
          {...(isValueField(review.field)
            ? {
                assentGuard: "keep" as const,
                onKeep: () => keepReview(review.field, review.value),
              }
            : {})}
        />
        <div className="focus-footer">
          <GhostButton onClick={() => setEditingReview(false)}>{t("common.cancel")}</GhostButton>
        </div>
      </>
    ) : (
      <>
        <Kicker>
          {copy.label} · {sourceLabel(review.source)}
        </Kicker>
        <Headline>{t("focus.refine.review.keepHeadline")}</Headline>
        <HelpDetail help={copy.help} detail={copy.detail} />
        {/* Shown in the POSITION it fills in the story sentence when the field
            has one. On #78 the `ends` value was a true sentence about the
            user's problem, and it was rendered bare — "the dashboard is hard to
            scan when a project has a lot of issues" — with the `so that` that
            inverts its meaning nowhere on screen. Both claims above it
            ("Outcome · from what you told Loom", "Loom set this for you") were
            true, and the user kept it.

            This is FRAMING for a value already on screen, not the composed
            artifact: the sentence the issue is written from is composed
            server-side at apply time from the monotonic store, at the CARD's
            resolved frame locale (the project locale when set, else the
            language of the story clauses themselves), and it does not cross
            the wire yet. So the connective here comes from the UI catalog at
            the UI locale and is not claimed to be byte-identical to the
            write — on a German card read through an English UI it will not be.
            `review.value` — the bytes Keep sends and the editor seeds from —
            is untouched. */}
        {/* A LIST field has no story position (no narrative clause), and its
            value is rows — render them as rows here too, for the same reason
            the ready card does: the string on the wire is a serialization, and
            showing the serialization is showing plumbing. */}
        {isListField(review.field) ? (
          <FieldValue field={review.field} value={review.value} />
        ) : (
        <div className="focus-prose">
          {(() => {
            const clause = storyClauseRoleOf(review.field);
            if (clause === null) return shownValue(review.field, review.value, t);
            const shown = prettyValue(review.field, review.value);
            const splice = storyClauseValue(clause, shown, uiLocale);
            // A value carrying its own connective of a class "so that" cannot
            // precede takes the frame that stays silent, rather than stacking.
            const key =
              clause === "ends" && !splice.connective
                ? "focus.refine.review.inStory.endsOwnConnective"
                : IN_STORY_KEY[clause];
            return t(key, { value: splice.text });
          })()}
        </div>
        )}
        <div className="focus-footer">
          <button
            type="button"
            className="focus-btn is-ok"
            onClick={() => keepReview(review.field, review.value)}
          >
            {t("focus.refine.review.keep")} <IconCheck />
          </button>
          <GhostButton onClick={() => setEditingReview(true)}>{t("focus.refine.review.change")}</GhostButton>
        </div>
      </>
    );
  } else if (heldCount > 0 && openAsks.length === 0 && !thinking) {
    stage = "held";
    // Drained the queue, but the agent is still finishing its current pass —
    // the answers are staged and flush together the instant it goes idle, so it
    // resumes with the full set instead of being restarted halfway through.
    body = (
      <>
        {showLoader && <ThinkingDots label={t("focus.refine.held.wrappingUp")} />}
        <Headline>{t("focus.refine.held.headline")}</Headline>
        <Help>
          {(heldCount === 1
            ? t("focus.refine.held.helpOne")
            : t("focus.refine.held.helpMany", { count: heldCount })) +
            t("focus.refine.held.helpTail")}
        </Help>
        {/* NO free prose. The argument the `thinking` branch below makes about
            staleness holds identically for Loom's own MECHANISM: the grind
            screen showed users "Let me ask via the proper question tool" and
            "Now let me resubmit with a valid non-empty quote for the
            `dod_criteria` citation". Prose is outranked, never suppressible
            sentence-by-sentence — there is no reliable way to tell which lines
            are about the product and which are about Loom — so the headline plus
            the held count carry the screen, exactly as they do there. */}
      </>
    );
  } else if (thinking) {
    stage = "thinking";
    headlineNamesNote = noteShown !== null && !phaseHeads;
    // Render NO free prose — the same leak class the boot (`!card`) branch
    // already closes, extended to the mid-grind case. The agent's live narration
    // lags the server-owned CardState, so a streamed sentence can claim a field
    // is "still waiting on your answer" for turns AFTER it greened server-side
    // (live-reproduced for priority). Prose is outranked, never suppressible
    // sentence-by-sentence — there's no reliable way to tell which lines are
    // stale — so the static headline + dots carry the screen instead.
    body = (
      <>
        {showLoader &&
          (phaseHeads ? (
            <div className="focus-spinner" />
          ) : (
            <ThinkingDots label={t("focus.refine.thinking.working")} />
          ))}
        <Headline>
          {phaseHeads
            ? phaseHeadline
            : noteShown !== null
              ? t("focus.refine.note.reading")
              : t("focus.refine.thinking.headline")}
        </Headline>
        {waitLine}
      </>
    );
  } else if (
    card.allGreen &&
    card.acceptToken &&
    (tail === "idle" || tail === "plan_loading")
  ) {
    stage = "plan_simulation";
    // Implementation-simulation step: the repo-aware sandbox agent is producing
    // a plan. The user never sees file paths — and we render NO stream prose
    // here: the plan turn's narration (and its synthetic prompt) name repo paths
    // and are suppressed as plan-era (see `preplanIdsRef`); showing `proseBlock`
    // would re-surface them. The loader copy carries the whole screen.
    withMsgBar = false;
    body = (
      <>
        {/* Dots unconditional (no prose streams here, so the stall-gated loader
            would leave it static); label-less since the headline already says
            what's happening. */}
        <ThinkingDots />
        <Kicker>{t("focus.refine.issueKicker", { iid })}</Kicker>
        {/* A correction re-run is the SAME screen as the first pass, so without
            its own copy the user's "not quite" looks like it went nowhere — and
            it is the one wait where the previous summary is still the last thing
            they read. Say the re-run is theirs. */}
        <Headline>
          {planCorrection !== null
            ? t("focus.refine.plan.reworkHeadline")
            : t("focus.refine.plan.headline")}
        </Headline>
        <Help>
          {planCorrection !== null
            ? t("focus.refine.plan.reworkHelp")
            : t("focus.refine.plan.help")}
        </Help>
        {tailElapsedSec >= 3 && (
          <div className="focus-note">
            {t("focus.refine.plan.elapsed", { sec: String(tailElapsedSec) })}
          </div>
        )}
        {/* NO escape. A card that is applyable is not a card Loom is finished
            with: the sizing turn it is running decides whether this ships as one
            ticket or several, and an apply that pre-empts it writes an issue
            whose size nobody checked. The wait is bounded without the user —
            the server degrades at TAIL_TURN_WALL_CLOCK_MAX_MS and the client
            backstop above it falls through to Accept — so the honest screen
            here is the wait itself. */}
      </>
    );
  } else if (
    card.allGreen &&
    card.acceptToken &&
    tail === "validate" &&
    businessSummary !== null
  ) {
    stage = "plan_confirm";
    // Business-impact validation: the plain-language summary + a yes/not-quite.
    withMsgBar = false;
    body = (
      <>
        <Kicker>{t("focus.refine.issueKicker", { iid })}</Kicker>
        <Headline>{t("focus.refine.validate.headline")}</Headline>
        <Help>{t("focus.refine.validate.help")}</Help>
        <MarkdownView source={uiProse(businessSummary)} className="focus-prose" />
        <div className="focus-footer">
          <button
            type="button"
            className="focus-btn is-ok"
            onClick={() => {
              confirmPlan();
              proceedFromValidate();
            }}
          >
            {t("focus.refine.validate.yes")} <IconCheck />
          </button>
          <GhostButton onClick={() => setTail("correct")}>{t("focus.refine.validate.notQuite")}</GhostButton>
        </div>
      </>
    );
  } else if (card.allGreen && card.acceptToken && tail === "sizing_wait") {
    stage = "sizing_wait";
    // The plan is confirmed but the size verdict hasn't landed yet — hold
    // visibly (same silent-tail treatment as plan_loading: no stream prose,
    // dots + elapsed carry the screen) instead of silently dropping the split
    // path. No escape, for the same reason plan_loading has none: the verdict
    // being waited on is the one that decides whether this is one ticket or
    // several. SIZING_WAIT_DEADLINE_MS ends the wait on its own.
    withMsgBar = false;
    body = (
      <>
        <ThinkingDots />
        <Kicker>{t("focus.refine.issueKicker", { iid })}</Kicker>
        <Headline>{t("focus.refine.sizewait.headline")}</Headline>
        <Help>{t("focus.refine.sizewait.help")}</Help>
        {tailElapsedSec >= 3 && (
          <div className="focus-note">
            {t("focus.refine.plan.elapsed", { sec: String(tailElapsedSec) })}
          </div>
        )}
      </>
    );
    // NOT gated on `card.acceptToken`. Parking a must-have strips that token as
    // its first act — deliberately, so Confirm disarms until the methodology
    // re-run — so gating the screen on it made the one surface offering "Gar
    // nicht bauen" vanish the instant it was used. BreakdownCard already renders
    // the disarmed state correctly: `confirmable` requires the token.
  } else if (card.allGreen && tail === "breakdown_review") {
    stage = "breakdown";
    // Epic/children review: the connected-cards flow. While the proposal turn
    // is still running, a loader carries the screen (stream prose stays
    // suppressed — the breakdown turn is tail-era); the degrade effect above
    // falls through to Accept if nothing lands.
    withMsgBar = false;
    body = (
      <>
        <Kicker>{t("focus.refine.issueKicker", { iid })}</Kicker>
        {breakdownCard.entry === null || breakdownCard.entry.noSplit ? (
          <>
            <ThinkingDots label={t("breakdown.review.wait")} />
            <Headline>
              {breakdownCard.sizing?.splitRecommended === false
                ? t("breakdown.split.forced")
                : t("breakdown.split.recommended")}
            </Headline>
            {tailElapsedSec >= 3 && (
              <div className="focus-note">
                {t("focus.refine.plan.elapsed", { sec: String(tailElapsedSec) })}
              </div>
            )}
            {/* NO way out while the proposal is being drafted. "Keep as one
                ticket" is a real decision, but only against a split the user
                can actually see — offered here it is a decision about nothing,
                taken to escape a spinner. It returns the moment the proposal
                lands (BreakdownCard's own Decline), and
                BREAKDOWN_REVIEW_DEADLINE_MS falls through to Accept if it
                never does. */}
          </>
        ) : (
          <>
            <Headline>{t("breakdown.review.title")}</Headline>
            {/* rationale now renders inside BreakdownCard so both hosts show it */}
            <BreakdownCard
              card={breakdownCard}
              epicTitle={issueTitle}
              issueIid={iid}
              // This host is the one the screen gate was REMOVED from (see
              // `onBreakdownConfirm`), so it is the one that can render a
              // confirm over a token the handler would refuse. Same truthiness
              // rule as the applied host above, for the same reason.
              {...(card.acceptToken ? { acceptToken: card.acceptToken } : {})}
              confirmPending={!card.acceptToken}
              agentBusy={ws.agentBusy}
              sessionClosed={sessionClosed}
              // This screen's own `<Headline>` is "Proposed split" — the card's
              // kicker verbatim, one line lower.
              headingInHost
              onSend={(text) =>
                ws.send(text, { scope: sendScope, flow: { issueIid: iid } })
              }
              onConfirm={onBreakdownConfirm}
              // No ask is pinned on this branch (an ask outranks
              // `breakdown_review` in the ladder), so the plain self-labelled
              // turn is the right lane — the card used to send exactly this.
              onDecline={() => {
                ws.send(buildKeepSingleMessage(iid), {
                  scope: sendScope,
                  flow: { issueIid: iid },
                });
                declineSplit();
              }}
              onOpenChild={onOpenIssue}
              stateKey={`${session.sessionId ?? ""}:${iid}:${ws.resetEpoch}`}
            />
          </>
        )}
      </>
    );
  } else if (card.allGreen && card.acceptToken && tail === "correct") {
    stage = "correct";
    // Mismatch: one freeform box describing the intended outcome → re-run.
    withMsgBar = false;
    body = (
      <>
        <Kicker>{t("focus.refine.issueKicker", { iid })}</Kicker>
        <Headline>{t("focus.refine.correct.headline")}</Headline>
        <Help>{t("focus.refine.correct.help")}</Help>
        <AnswerWidget
          key="business-correction"
          field="business_correction"
          projectId={projectId}
          ariaLabel={t("focus.refine.correct.headline")}
          onAnswer={submitCorrection}
          draftKey="correct:business_correction"
        />
        <div className="focus-footer">
          <GhostButton onClick={() => setTail("validate")}>{t("common.back")}</GhostButton>
        </div>
      </>
    );
  } else if (card.allGreen && card.acceptToken) {
    stage = "ready";
    // The refined issue, field by field, so the user sees what Loom will write
    // before they apply it — and can edit any field in place. `story_points`
    // and other "skip" fields stay hidden; only fields with a grounded value
    // surface.
    const greenFields = FIELD_ORDER.filter((field) => {
      if (policyFor(field) === "skip") return false;
      const v = card.verdicts.find((x) => x.field === field);
      return (
        !!v &&
        v.verdict === "green" &&
        typeof v.value === "string" &&
        v.value.trim() !== ""
      );
    });
    // Mirrors the render below: a row offers "keep the previous value" while
    // its field is pending; Accept shows only with no editor open and no edit
    // pending.
    cancelVisible = greenFields.some(
      (f) => card.pendingEdits?.some((p) => p.field === f) ?? false,
    );
    acceptVisible =
      editField === null &&
      !(card.pendingEdits !== undefined && card.pendingEdits.length > 0);
    body = (
      <>
        <div className="focus-glyph is-ok">
          <IconCheck />
        </div>
        <Kicker>{t("focus.refine.issueKicker", { iid })}</Kicker>
        {/* The title is the most-read field of the work item and the ONE thing the
            user never sees before Accept: the Focus wizard submits title:"" and
            the server LLM-derives it (dashboard/apply.ts) before this flow starts.
            It is NOT a card field — apply deliberately never writes it
            (apply-update-issue.ts) — so this row is read-only on purpose. An Edit
            control here would be a lie about what Accept does.

            Rendered OUTSIDE `.focus-issue`, above the headline that introduces it:
            that block is "the fields Loom verified and will write", and an
            unverified, unwritable value inside it with near-identical markup would
            blur the exact boundary this screen exists to draw. */}
        {issueTitle.trim() !== "" && (
          <div className="focus-issue-title">
            <span className="focus-issue-label">
              {t("focus.refine.ready.titleLabel")}
            </span>
            <div className="focus-prose">{issueTitle}</div>
            <p className="focus-help">{t("focus.refine.ready.titleNote")}</p>
          </div>
        )}
        <Headline>{t("focus.refine.ready.headline")}</Headline>
        <Help>{t("focus.refine.ready.help")}</Help>
        {/* The sizing degrade used to be terminal copy with nothing to press:
            "Loom couldn't finish sizing this" on a screen the user reached
            precisely BECAUSE a multi-minute turn timed out or lost its frame.
            The note stays put while the retry runs (a note that vanished on
            click reads as "done" and gets clicked again) and the control is
            disabled in flight — `readiness_plan_request` is the frame behind the
            166-frames → 260-respawns → OOM incident, and a re-clickable button
            with no in-flight state is the client half of that defect. */}
        {/* Two different degrades reach this screen. When the SIZING never
            landed there is a round to re-run, so the note carries the retry.
            When the sizing is fine and only the SPLIT degraded, re-running the
            sizing is not the repair — but saying nothing is how the F0 live
            run ended: Loom offered a split, withdrew it silently, and the user
            was left to guess. Explain either way; offer the retry only where
            it is the right verb. */}
        {/* The plan-confirm refusal, said in its own words. It rides beside
            the sizing degrade rather than through it: the two are independent
            (the size verdict is in hand on every path that sets this) and the
            sizing copy would claim a failure that did not happen. The apply-time
            `planDropReason` note on the applied screen is the backstop for the
            case where the user never passes through here. */}
        {planConfirmLost !== null && (
          <div className="focus-note">
            {t(
              planConfirmLost === "superseded"
                ? "focus.refine.tail.planConfirmSuperseded"
                : "focus.refine.tail.planConfirmUnavailable",
            )}
          </div>
        )}
        {tailDegraded && breakdownCard.sizing !== null && (
          <div className="focus-note">{t(degradedNoteKey)}</div>
        )}
        {tailDegraded && breakdownCard.sizing === null && (
          <div className="focus-skip-optional">
            {/* Told apart by the server's REASON where there is one. The
                generic line stays the fallback for a client-deadline degrade
                (no frame, so no reason) and for a reason nobody has written
                copy for yet — "couldn't finish" reads as data loss, and each
                specific line says the issue is untouched. */}
            <div className="focus-note">{t(degradedNoteKey)}</div>
            <button
              type="button"
              className="focus-btn is-ghost"
              onClick={retrySizing}
              disabled={planRetryInFlight}
            >
              {planRetryInFlight
                ? t("focus.refine.tail.retrySizingPending")
                : t("focus.refine.tail.retrySizing")}
            </button>
          </div>
        )}
        {noSplitAffirmed && (
          <div className="focus-note">{t("focus.refine.tail.noSplitNote")}</div>
        )}
        <div className="focus-issue">
          {greenFields.map((field) => {
            const v = card.verdicts.find((x) => x.field === field)!;
            const copy = fieldCopy(field);
            const editing = editField === field;
            // Server-set: the agent hasn't folded this edit back into the card
            // yet. Keep showing the OLD value (what apply would write if forced);
            // the amber badge signals the new value is being woven in.
            const rowPending =
              card.pendingEdits?.some((p) => p.field === field) ?? false;
            return (
              <div
                key={field}
                className={
                  rowPending
                    ? "focus-issue-field is-pending"
                    : "focus-issue-field"
                }
              >
                <div className="focus-issue-head">
                  <span className="focus-issue-label">{copy.label}</span>
                  {rowPending ? (
                    <span className="focus-issue-pending">
                      <span className="focus-issue-pending-dot" />
                      {t("focus.refine.ready.pendingBadge")}
                    </span>
                  ) : (
                    !editing && (
                      <button
                        type="button"
                        className="focus-issue-edit"
                        // The six rows each ship an "Edit" button, so the label
                        // has to name the field: unlabelled, a screen reader
                        // read "Edit, button" six times over. Same string the
                        // widget it opens already uses.
                        aria-label={t("focus.refine.ready.editAria", {
                          label: copy.label,
                        })}
                        onClick={() => {
                          setKeptField(null);
                          setEditField(field);
                        }}
                      >
                        {t("common.edit")}
                      </button>
                    )
                  )}
                </div>
                {editing ? (
                  <>
                    <AnswerWidget
                      key={field}
                      field={field}
                      projectId={projectId}
                      ariaLabel={t("focus.refine.ready.editAria", { label: copy.label })}
                      onAnswer={(text) => editGreen(field, text)}
                      draftKey={`editGreen:${field}`}
                      // Open the editor pre-filled with the value about to be written.
                      {...seedFrom(field, v.value!)}
                      // …which a CHOICE control cannot pre-fill (it has no box),
                      // so it marks the option instead — otherwise editing
                      // priority from here opens three unmarked buttons.
                      current={{ value: v.value!, origin: "card" }}
                      // OK is dead until the text differs: an editor opened "to
                      // check" is not an edit. And a bare yes typed here KEEPS
                      // the value — no frame, no turn. Live #239 "yes" in the
                      // Outcome editor became the value and was written to
                      // GitLab as "…so that yes."
                      refuseUnchanged
                      {...(isValueField(field)
                        ? {
                            assentGuard: "keep" as const,
                            onKeep: () => keepGreen(field),
                          }
                        : {})}
                    />
                    <div className="focus-footer">
                      {/* "Keep as is", not "Cancel": leaving the editor is
                          keeping the value, and Accept already writes it. */}
                      <GhostButton onClick={() => setEditField(null)}>
                        {t("focus.refine.ready.keepAsIs")}
                      </GhostButton>
                    </div>
                  </>
                ) : field === "milestone" ? (
                  <ReadyMilestoneValue projectId={projectId} value={v.value!} />
                ) : (
                  <FieldValue field={field} value={v.value!} />
                )}
                {keptField === field && !editing && (
                  <p className="focus-help" role="status">
                    {t("focus.refine.ready.keptNote")}
                  </p>
                )}
                {/* The DoD is the one row the AGENT authored — the directive has
                    it set the quality bar rather than put a testing question in
                    front of a business owner — and it lands in the description
                    verbatim. So it has to say whose words they are AND that
                    Accept writes them. This screen is no longer the ONLY place
                    it is shown — `ReadinessCard.tsx` renders green values on the
                    issue hub too, which is what makes keeping `dod_criteria`
                    code-sourceable defensible; do not remove either surface
                    without re-opening that decision. Deliberately the mirror of the title row above: that one
                    says Accept does NOT write it, this one says it DOES. */}
                {field === "dod_criteria" && !editing && (
                  <p className="focus-help">{t("focus.refine.ready.dodNote")}</p>
                )}
                {/* `revised`, stamped by merge rule 2 for TWO populations, and
                    the note has to be true of both (see `revisedNote`):
                    the model re-quoting a NARROWER span of the same turn to get
                    past a shape bound ("…, die nach wiederverwendbaren
                    Bauteilen sucht" persisted as "Oeffentliche Shop-Kundschaft"),
                    and a value lifted out of the user's reply to a concern Loom
                    raised about this field — where nothing was shortened but the
                    reply may not have been a value at all. Until now nothing
                    anywhere said so, and the value went to GitLab under "from
                    what you told Loom".

                    It is a NOTE on the row, not a step in the queue, and that is
                    load-bearing: a step would have to be dismissible, the only
                    dismissal that clears server state is one that sends bytes,
                    and "keep it" must not cost a model turn. A note is
                    idempotent — it can be rendered on every card, every remount
                    and every reconnect re-emit without ever blocking Accept —
                    and it sits beside the value and its Edit control, on the one
                    screen every applied value provably passes through. */}
                {v.revised === true && !editing && (
                  <p className="focus-help">{t("focus.refine.ready.revisedNote")}</p>
                )}
                {/* An answer from the meeting page or its link (refine light):
                    it counts as given, so this note is the one place the user
                    sees where it came from. Edit changes it like any value. */}
                {v.meetingAnswer !== undefined && v.resolvedSource === "meeting" && !editing && (
                  <p className="focus-help">
                    {t(
                      v.meetingAnswer.via === "link"
                        ? "focus.refine.ready.meetingAnswerLink"
                        : "focus.refine.ready.meetingAnswerOwner",
                    )}
                  </p>
                )}
                {rowPending && (
                  <GhostButton
                    onClick={() => ws.sendReadinessFieldEditCancel(iid, field)}
                  >
                    {t("focus.refine.ready.pendingCancel")}
                  </GhostButton>
                )}
              </div>
            );
          })}
        </div>
        {editError !== null && (
          <div className="focus-note">
            {editError.code === "edit_agent_unavailable"
              ? t("focus.refine.ready.pendingNoSandbox")
              : t(errorCopyKey(editError.code))}
          </div>
        )}
        {/* THE COMPREHENSION OBJECTION.
            `propose_card` already runs a methodology pass over the composed
            narrative on every store-path card, ships the verdicts to the
            browser, and — until this — nothing in Focus read them. On issue #78
            the objection naming the exact defective span was in the browser's
            own state at the moment the user clicked Keep, and rendered only in
            `chat/render/ReadinessCard.tsx`, a surface this flow never shows.

            Between the field list and Accept on purpose: it is about the values
            above, and it has to be readable before the button. ONE criterion
            (see `comprehensionObjection`) — the rest of the family warns on
            most well-formed stories and would turn this screen into a nag box.
            The judge's own sentence, verbatim; the frame around it never says
            what the defect is. ADVISORY: it is rendered outside the
            Accept/pending branch below and changes nothing about it. */}
        {(() => {
          const objection = comprehensionObjection(card);
          return objection === null ? null : (
            <div
              className="focus-comprehension"
              data-testid="comprehension-objection"
            >
              <p className="focus-help">
                {t("focus.refine.ready.comprehension.headline")}
              </p>
              {/* The judge's sentence in a quote block — visually Loom quoting a
                  reader, not Loom asserting a fact about the user's words. */}
              <div className="focus-prose">{objection.text}</div>
              <div className="focus-note">
                {t("focus.refine.ready.comprehension.help")}
              </div>
            </div>
          );
        })()}
        {editField === null &&
          (card.pendingEdits && card.pendingEdits.length > 0 ? (
            // An edit is being folded in — Accept would write the OLD value (and
            // the server's edit_pending block would refuse it anyway). Disarm it
            // until the re-carded value clears the pending state. While the
            // sandbox-away note is up, nothing is being folded in yet — don't
            // stack a second, contradicting note.
            editError?.code === "edit_agent_unavailable" ? null : (
              <div className="focus-note">{t("focus.refine.ready.pendingNote")}</div>
            )
          ) : (
            <NextButton onClick={() => onAccept("ready")} label={t("focus.refine.ready.accept")} tone="ok" />
          ))}
        {/* Split affordance on the Accept preview. Covers BOTH the forced
            split (policy said no) AND the late-sizing case: the user clicked
            "yes, matches" before the sizing landed, so proceedFromValidate saw
            sizing === null and skipped the offer — when splitRecommended
            arrives afterwards this is the remaining affordance. */}
        {editField === null &&
          breakdownCard.sizing !== null &&
          // An EXISTING proposal used to hide every split control here, while
          // being invisible itself (BreakdownCard only renders under the
          // `breakdown_review` tail) — so the attach re-emit of a live proposal
          // both blocked the escape hatch and showed nothing. Backwards: a live
          // proposal is the strongest reason to offer a way BACK to it, and it
          // costs no turn because the rows are already in hand.
          (breakdownCard.entry === null || splitProposalLive) &&
          !breakdownCard.breakdownUnavailable &&
          // Big but single-criterion: it can't be partitioned by acceptance
          // criterion yet. Don't ask for "more criteria" (wrong lens) — ask for
          // the PIECES it ships as; the agent turns those into criteria, then
          // it splits normally.
          (splitProposalLive ? (
            <div className="focus-skip-optional">
              <div className="focus-note">
                {t("focus.refine.split.proposalWaiting")}
              </div>
              <GhostButton onClick={() => setTail("breakdown_review")}>
                {t("breakdown.split.backToProposal")}
              </GhostButton>
            </div>
          ) : breakdownCard.sizing.oversizedSingleCriterion ? (
            <div className="focus-skip-optional">
              <div className="focus-note">
                {t("focus.refine.split.oversizedSingle", {
                  bucket: breakdownCard.sizing.overall.locBucket,
                  files: String(breakdownCard.sizing.overall.filesTouched),
                })}
              </div>
              <GhostButton onClick={requestPieceElicitation}>
                {t("focus.refine.split.breakIntoPieces")}
              </GhostButton>
            </div>
          ) : (
            <div className="focus-skip-optional">
              {/* Show the size verdict so "Split anyway" has an antecedent — the
                  focus flow otherwise never tells the user what Loom sized. */}
              {/* A verdict the server marked stale describes must-haves the
                  user has since changed. Say that, rather than reporting a
                  recommendation about a list that no longer exists. */}
              <div className="focus-note">
                {breakdownCard.sizing.verdictStale
                  ? t("focus.refine.split.verdictStale")
                  : breakdownCard.sizing.splitRecommended
                    ? t("focus.refine.split.sizeRecommended", {
                        bucket: breakdownCard.sizing.overall.locBucket,
                        files: String(breakdownCard.sizing.overall.filesTouched),
                      })
                    : t("focus.refine.split.sizeNotNeeded", {
                        bucket: breakdownCard.sizing.overall.locBucket,
                        files: String(breakdownCard.sizing.overall.filesTouched),
                      })}
              </div>
              <GhostButton
                onClick={
                  !breakdownCard.sizing.verdictStale &&
                  breakdownCard.sizing.splitRecommended
                    ? proceedFromValidate
                    : forceSplit
                }
              >
                {!breakdownCard.sizing.verdictStale &&
                breakdownCard.sizing.splitRecommended
                  ? t("breakdown.split.cta")
                  : t("breakdown.split.force")}
              </GhostButton>
            </div>
          ))}
      </>
    );
  } else {
    // Card present, nothing for the user yet — the chat is grinding red slots
    // (the Stop-hook loop) toward all-green.
    const p = cardProgress(card);
    // …unless nothing is grinding. With the server idle and nothing held for
    // `IDLE_SETTLE_MS` (`grindIdleSettled`), the fields still open are the
    // user's to answer, and this screen said the opposite: dev #174 sat on "Die
    // letzten Punkte werden geschärft — 6 von 7 belegt. Loom prüft den Rest
    // nochmals gegen den Code." for over ten minutes with no turn running, and
    // the one note the user sent got an answer this screen does not show. Name
    // what is open and where to write it. A field the user SKIPPED is not
    // waiting on them.
    const waitingOn = fieldsWaitingOnUser(card, {
      idle: grindIdleSettled,
    }).map((f) => fieldCopy(f).label);
    if (grindIdleSettled && waitingOn.length === 0) {
      stage = "card_stopped";
      // …and with NOTHING open for the user either, nothing more is on its
      // way: the turn ended before the card was finished (the Stop hook lets a
      // turn end once no PROPOSED field is open — a first card holding only the
      // priority the boot-window agent asked about is enough, #284 and #285).
      // Say so, and offer the gentle way on: one same-flow turn that keeps the
      // conversation and the card. The message bar stays, as on
      // `agent_stopped`. No restart — the workspace is fine.
      body = (
        <>
          <div className="focus-glyph is-bad">
            <IconAlert />
          </div>
          <Kicker>{t("focus.refine.issueKicker", { iid })}</Kicker>
          <Headline>{t("focus.refine.stopped.headline")}</Headline>
          {ws.agentWork?.lastStop === "silence" && (
            <Help>{t("focus.refine.stopped.silence")}</Help>
          )}
          <Help>{t("focus.refine.cardStopped.help")}</Help>
          <div className="focus-footer">
            <button
              type="button"
              className="focus-btn is-ok"
              disabled={carryOnAt !== null}
              onClick={carryOn}
            >
              {carryOnAt !== null
                ? t("focus.refine.stopped.starting")
                : t("focus.refine.cardStopped.carryOn")}
            </button>
            <GhostButton onClick={onProjects}>
              {leaveLabel}
            </GhostButton>
          </div>
          <div className="focus-note">{t("focus.refine.stopped.orMessage")}</div>
        </>
      );
    } else {
      stage = "waiting";
      body = (
        <>
          {showLoader && waitingOn.length === 0 && (
            <ThinkingDots label={t("focus.refine.grind.refining")} />
          )}
          <Headline>
            {waitingOn.length > 0
              ? t("focus.refine.grind.waitingHeadline")
              : t("focus.refine.grind.headline")}
          </Headline>
          <Help>
            {waitingOn.length > 0
              ? t("focus.refine.grind.waiting", { fields: waitingOn.join(", ") })
              : ws.agentBusy
                ? t("focus.refine.grind.help", { grounded: p.grounded, total: p.total })
                : // "Loom is re-checking" is a claim only the server's busy flag
                  // may make.
                  t("focus.refine.grind.count", { grounded: p.grounded, total: p.total })}
          </Help>
          {/* Minutes, the tool, "taking longer": a 5-minute grind turn (#284,
              302 s) showed none of it — this screen drew no wait line. */}
          {waitingOn.length === 0 && waitLine}
          {/* The last branch that still rendered the transcript, and the one the
              leak was observed on: this screen showed the user "Let me ask via the
              proper question tool" and a sentence about resubmitting a
              `dod_criteria` citation. The user reads about their product, never
              about Loom's mechanism — and the same headline + `{grounded}/{total}`
              already carry the screen for the `thinking` branch, which renders no
              prose at all. */}
        </>
      );
    }
  }

  // `focus.stage` — which branch of the ladder above rendered, and which of the
  // two edit controls it offered. Reported on CHANGE only (the deps are the
  // reported values), so a re-render of the same screen costs nothing. The
  // session id is a dep too: the reporter drops events until `useWsSession`
  // points it at a session, so the screen showing at that moment is re-sent.
  const pendingEditCount = card?.pendingEdits?.length ?? 0;
  const traceSessionId = session.sessionId;
  useEffect(() => {
    if (traceSessionId === null) return;
    reportClientEvent("focus.stage", {
      stage,
      issueIid: iid,
      pendingEditCount,
      cancelVisible,
      acceptVisible,
    });
  }, [traceSessionId, stage, iid, pendingEditCount, cancelVisible, acceptVisible]);

  // `focus.wait` — what the page told the user while they waited, and the
  // server facts it read. One content-free line each time that CHANGES: the
  // kind of wait on screen (`none` wherever no wait line or stop screen is
  // drawn — a card screen says nothing about waiting), the phase while
  // starting, and whether "taking longer" / "hasn't started yet" is showing
  // (`nextWaitFlipMs` re-renders the page the moment it turns on). The line
  // that ENDS a wait carries `endedKind` and `endedElapsedS`: how long that
  // stretch lasted, which the stretch's own server `since` no longer says once
  // it is over. Replaces the old `boot_timeout` stage as the way to see a wait
  // on dev.
  const waitDrawn = WAIT_DRAWN_STAGES.has(stage);
  const waitKindShown =
    waitStatus.kind === "offline" || waitStatus.kind === "unreachable"
      ? waitStatus.kind
      : stage === "card_stopped"
        ? "stopped"
        : waitDrawn
          ? waitStatus.kind
          : "none";
  const waitStopReason =
    stage === "card_stopped"
      ? (ws.agentWork?.lastStop ?? null)
      : waitStatus.kind === "stopped"
        ? waitStatus.reason
        : null;
  const waitWork =
    waitStatus.kind === "working"
      ? waitStatus
      : waitStatus.kind === "starting"
        ? waitStatus.working
        : null;
  const waitLongShown =
    waitDrawn &&
    ((waitWork !== null && waitWork.long) ||
      (waitStatus.kind === "starting" && waitStatus.runnerLong));
  const waitPhaseShown = waitKindShown === "starting" ? ws.sandbox.phase : null;
  // Where the stretch ON SCREEN began: the phase's `since` while starting, the
  // busy stretch's `since` while working — and nothing for a kind with no
  // clock, including a wait that is not drawn (live, a `none` under a card
  // screen carried the hidden busy stretch's age as `endedElapsedS`).
  const waitStartMs =
    waitKindShown === "starting"
      ? waitInput.phaseSinceMs
      : waitKindShown === "working"
        ? waitInput.busySinceMs
        : null;
  const waitTraceRef = useRef({
    input: waitInput,
    reason: waitStopReason,
    startMs: waitStartMs,
    stage,
  });
  const waitPrevRef = useRef<{ kind: string; startMs: number | null } | null>(null);
  waitTraceRef.current = { input: waitInput, reason: waitStopReason, startMs: waitStartMs, stage };
  useEffect(() => {
    if (traceSessionId === null) return;
    const { input, reason, startMs, stage: stageNow } = waitTraceRef.current;
    const now = Date.now();
    const prev = waitPrevRef.current;
    waitPrevRef.current = { kind: waitKindShown, startMs };
    const activity = input.tool !== null ? activityOf(input.tool) : null;
    reportClientEvent("focus.wait", {
      waitKind: waitKindShown,
      stage: stageNow,
      sandboxPhase: input.phase,
      busy: input.busy,
      ...(startMs !== null ? { elapsedS: Math.max(0, Math.round((now - startMs) / 1000)) } : {}),
      longShown: waitLongShown,
      ...(activity !== null ? { activity } : {}),
      ...(reason !== null ? { reason } : {}),
      ...(prev !== null && prev.kind !== waitKindShown
        ? {
            endedKind: prev.kind,
            ...(prev.startMs !== null
              ? { endedElapsedS: Math.max(0, Math.round((now - prev.startMs) / 1000)) }
              : {}),
          }
        : {}),
      issueIid: iid,
    });
    // `stage` is read, not a trigger: it only decides `waitKindShown`.
  }, [traceSessionId, waitKindShown, waitPhaseShown, waitLongShown, iid]);

  // `refine.parked_notice` — the first render of the parked-issue bar for a
  // given parked issue in this mount, with the apply-result slot it was decided
  // on (a single latest slot: an apply of THIS issue hides an earlier apply of
  // the parked one).
  const parkedNoticeIid = parkedBar !== null && parkedFlow !== null ? parkedFlow.iid : null;
  const parkedTracedRef = useRef<number | null>(null);
  const parkedTimelineLen = ws.timeline.length;
  useEffect(() => {
    if (parkedNoticeIid === null || parkedTracedRef.current === parkedNoticeIid) {
      return;
    }
    parkedTracedRef.current = parkedNoticeIid;
    const fields: ClientTraceFields = {
      issueIid: iid,
      parkedIid: parkedNoticeIid,
      applyResultIssueIid: ws.applyResult?.issueIid ?? null,
      applyResultOk: ws.applyResult?.ok ?? null,
      timelineLen: parkedTimelineLen,
    };
    reportClientEvent("refine.parked_notice", fields);
  }, [parkedNoticeIid, iid, ws.applyResult, parkedTimelineLen]);

  // `refine.parked_notice_shown` — the bar is STILL on screen
  // `PARKED_NOTICE_HELD_MS` after it first rendered. `refine.parked_notice`
  // fires on the first render, and the attach replay renders the bar for an
  // instant before it folds the apply result that hides it (dev 9e92e2f2,
  // 09:16:59.978: `parkedIid` 205, already applied, while no snapshot from
  // 09:17:04 on showed a bar) — so that line cannot tell a flash from a notice
  // the user reads. This one fires only if the SAME parked issue stayed named
  // without a break: any change or unmount clears the timer. The fields are
  // read when it fires, not when the bar appeared.
  const parkedHeldRef = useRef<number | null>(null);
  const parkedTraceNowRef = useRef({
    applyResult: ws.applyResult,
    timelineLen: ws.timeline.length,
  });
  useEffect(() => {
    parkedTraceNowRef.current = {
      applyResult: ws.applyResult,
      timelineLen: ws.timeline.length,
    };
  });
  useEffect(() => {
    if (parkedNoticeIid === null || parkedHeldRef.current === parkedNoticeIid) {
      return;
    }
    const heldIid = parkedNoticeIid;
    const timer = setTimeout(() => {
      parkedHeldRef.current = heldIid;
      const now = parkedTraceNowRef.current;
      reportClientEvent("refine.parked_notice_shown", {
        issueIid: iid,
        parkedIid: heldIid,
        applyResultIssueIid: now.applyResult?.issueIid ?? null,
        applyResultOk: now.applyResult?.ok ?? null,
        timelineLen: now.timelineLen,
        heldMs: PARKED_NOTICE_HELD_MS,
      });
    }, PARKED_NOTICE_HELD_MS);
    return () => clearTimeout(timer);
  }, [parkedNoticeIid, iid]);

  // What the user just sent sits ABOVE the composer they sent it from, not
  // under the step. Under the step it was below the fold on any real question:
  // live at 1280×720 the note landed at 878px and its Figma chip at 993px, in a
  // stage 568px tall, and nothing brought either into view. `.loom-focus` is a
  // fixed column and the message-bar row sits outside `.focus-stage`, the only
  // element that scrolls, so the receipt is on screen at every height and the
  // question keeps the stage it had.
  //
  // Only on the conversational screens — the ones that keep the composer, where
  // the user can have just sent something. The decision and terminal screens
  // carry no message bar and get no receipt: it would compete with the one
  // choice that screen asks for.
  //
  // The label says what Loom is doing with the note. With a question pinned the
  // question is the headline, so without it nothing but the header pill said
  // Loom was working on the note at all.
  const noteLabel =
    noteShown === null
      ? null
      : headlineNamesNote
        ? t("focus.refine.note.sentLabel")
        : thinking || ws.agentBusy
          ? t("focus.refine.note.reading")
          : t("focus.refine.note.received");
  const offerFigmaChip = flowFigmaLinks.length > 0 && !figmaInView;
  const receipt =
    noteShown !== null || offerFigmaChip ? (
      <div className="focus-msgbar-note is-receipt">
        {noteShown !== null && (
          <div className="focus-sent-note">
            {/* The live region wraps the LABEL only: a change of state is
                announced, the user's own sentence is never read back. */}
            <span className="focus-sent-note-label" role="status" aria-live="polite">
              {noteLabel}
            </span>
            <p className="focus-sent-note-text">{noteShown.text}</p>
          </div>
        )}
        {offerFigmaChip && (
          <button
            type="button"
            className="focus-btn is-ghost focus-sent-note-show"
            onClick={showFigmaChip}
          >
            {flowFigmaLinks.length === 1
              ? t("focus.refine.figma.showOne")
              : t("focus.refine.figma.showMany")}
          </button>
        )}
      </div>
    ) : null;

  const debugOn = isFocusDebugEnabled();
  const debugSnap: FocusDebugSnapshot | null = debugOn
    ? {
        sessionId: session.sessionId,
        wsStatus: ws.status,
        sandboxPhase: ws.sandbox.phase,
        agentBusy: ws.agentBusy,
        thinking,
        heldCount,
        epoch,
        cardId,
        derivedCardId: derivedCard?.id ?? null,
        cardRevision: cardRev,
        allGreen: card?.allGreen ?? false,
        verdicts: card
          ? card.verdicts.map((v) => ({
              field: v.field,
              verdict: v.verdict,
              hasValue: typeof v.value === "string" && v.value.trim() !== "",
              ...(v.verdict !== "green" && v.reason
                ? { reason: v.reason.slice(0, 70) }
                : {}),
            }))
          : [],
        // Step KEY, not raw field: a field-less ask has none, and the key is
        // what every collision-sensitive site (queue, batch, pin) actually uses.
        current: current ? { field: stepKeyOf(current), kind: current.kind } : null,
        derivedFirst: openAsks[0]
          ? { field: stepKeyOf(openAsks[0]), kind: openAsks[0].kind }
          : null,
        queue: queue.map(stepKeyOf),
        provided: [...provided],
        pendingAnswers: [...answers.entries()].map(([field, text]) => ({
          field,
          text,
        })),
      }
    : null;

  return (
    <RemountSafeStoreProvider store={widgetDraftsRef.current}>
      {(ws.idleCountdownEndsAt !== null || ws.status === "idle_killed") && (
        <IdleReconnectDialog
          endsAt={ws.idleCountdownEndsAt}
          killed={ws.status === "idle_killed"}
          onStay={ws.dismissIdle}
          onRestart={() => {
            // The idle kill only EVICTS the sandbox — the DELETE(?reason=idle)
            // does NOT stamp `endedAt`, so the session is genuinely resumable.
            // Re-trigger a fresh pipeline via /reconnect: the server forwards
            // `resume: hasCodingState`, so a session that ever reached a live
            // bridge (coding state was persisted) gets `claude --resume` and the
            // agent recovers its full conversation — the in-progress grounding,
            // held answers and card state this surface cares about most — rather
            // than discarding it and re-asking everything from scratch. Then
            // clear the client-side `idleKilled` latch so the WS re-attaches to
            // the SAME session. Only a genuine failure (409 session_revoked, or a
            // network error) falls back to dropping the binding + cold-booting.
            void (async () => {
              const { ok } = await reconnectSandbox();
              if (ok) {
                ws.resumeFromIdleKill();
                return;
              }
              clearPersistedSession(projectId, "issue", iid);
              reloadApp();
            })();
          }}
        />
      )}
      <FocusFrame
        // `stepSig` carries the step KEY (so two consecutive field-less asks are
        // distinct keys and the widget subtree is rebuilt between them — reusing
        // it would open the second ask pre-filled with the first one's draft).
        stepKey={`refine:${stepSig}:${accepted || flowTerminal ? "done" : current?.kind === "ask" ? "ask" : thinking ? "think" : card ? "card" : "boot"}:${tail}:${editingReview ? "edit" : ""}`}
        progress={progress}
        status={status}
        {...(withMsgBar
          ? {
              messageBar: (
                <div className="focus-msgbar-wrap">
                  {receipt}
                  {renderMessageBar(composerFolded)}
                </div>
              ),
            }
          : {})}
      >
        {connectionBar}
        {parkedBar}
        {previewBar}
        {undoBar}
        {body}
        {/* The Figma receipt for a link this flow carried: a chip per link,
            and the connect card when Figma is not connected — refine is where a
            customer pastes a design first. Folded from replayed
            `figma_link_state` rows, so it survives a reload like the chat's.
            Under the step's own content and only on the conversational screens,
            like the note's receipt above the composer (see `receipt`). The
            wrapper is what that receipt scrolls to and focuses: `tabIndex={-1}`
            takes focus from script and is never a Tab stop. */}
        {withMsgBar && flowFigmaLinks.length > 0 && (
          <div ref={setFigmaAnchor} tabIndex={-1} className="focus-figma-anchor">
            <FigmaLinkPanel
              links={flowFigmaLinks}
              // A "don't ask again" given at an earlier step still counts here.
              allLinks={flowFigmaConnectBasis}
              onDecline={ws.sendFigmaConnectDeclined}
              onAllowFile={ws.sendFigmaAllowFile}
              // EXPLICITLY off, though `false` is also the default: this panel
              // is NOT a chat surface. It renders under the step body on every
              // conversational screen, so "Show the sketch" here would open a
              // 400-520px drawing directly beneath the question the user is
              // answering — and on the sketch question itself the same drawing
              // would be reachable twice on one screen. One question per screen
              // is the rule; the sketch's own chat mode must not be the thing
              // that breaks it.
              allowPreview={false}
            />
          </div>
        )}
      </FocusFrame>
      {debugSnap !== null && <FocusDebugPanel snap={debugSnap} />}
    </RemountSafeStoreProvider>
  );
}
